// src/api-client.ts
import jwtDecode from "jwt-decode";
import EventEmitter from "events";
var ApiClient = class _ApiClient {
  config;
  schemaValidators;
  emitter;
  /* Shared token state */
  static tokenLock;
  static tokenUnlock;
  static tokenTimeOut;
  static tokenUseBuffer;
  static callId;
  static debug;
  static apiRouteConfig;
  /**
   * Maps a pair of accountId and refreshToken to a boolean indicating whether the token is valid.
   *
   * The key is formatted as: "<accountId>:<refreshToken>"
   */
  static isRefreshPayloadValidMap = /* @__PURE__ */ new Map();
  constructor(config, schemaValidators) {
    this.config = config;
    this.schemaValidators = schemaValidators;
    this.emitter = new EventEmitter();
    _ApiClient.tokenLock = null;
    _ApiClient.tokenUnlock = () => {
    };
    _ApiClient.tokenTimeOut = null;
    _ApiClient.tokenUseBuffer = null;
    _ApiClient.callId = 0;
    _ApiClient.debug = false;
    _ApiClient.apiRouteConfig = null;
  }
  /* ----------------------------------------------
  	Debug Helper Functions
  ----------------------------------------------*/
  log(msg, ...args) {
    if (_ApiClient.debug) {
      console.error(`[API-CLIENT] ${msg}`, ...args);
    }
  }
  /* ----------------------------------------------
  	Wrappers for config getters and setters
   ----------------------------------------------*/
  async setAccessToken(accessToken2) {
    this.log(`Setting access token to ${accessToken2}`);
    await this.config.setAccessToken(accessToken2);
    this.emitter.emit("access-token-updated", accessToken2 ? jwtDecode(accessToken2) : void 0);
    void this.updateApiRouteConfig();
    this.log(`Set access token to ${accessToken2}`);
    return;
  }
  async getAccessToken() {
    this.log(`Getting access token`);
    const accessToken2 = await this.config.getAccessToken();
    this.log(`Got access token ${accessToken2}`);
    return accessToken2;
  }
  async setRefreshToken(refreshToken) {
    this.log(`Setting refresh token to ${refreshToken}`);
    await this.config.setRefreshToken(refreshToken);
    this.log(`Set refresh token to ${refreshToken}`);
    return;
  }
  async getRefreshToken() {
    this.log(`Getting refresh token`);
    const refreshToken = await this.config.getRefreshToken();
    this.log(`Got refresh token ${refreshToken}`);
    return refreshToken;
  }
  async setAccountId(accountId) {
    this.log(`Setting account id to ${accountId}`);
    await this.config.setAccountId(accountId);
    this.log(`Set account id to ${accountId}`);
    return;
  }
  async getAccountId() {
    this.log(`Getting account id`);
    const accountId = await this.config.getAccountId();
    this.log(`Got account id ${accountId}`);
    return accountId;
  }
  async getLocalRefreshedTime() {
    this.log(`Getting local refreshed time`);
    const timestamp = await this.config.getLocalRefreshedTime();
    this.log(`Got local refreshed time ${timestamp}`);
    return timestamp;
  }
  async setLocalRefreshedTime(timestamp) {
    this.log(`Setting local refreshed time to ${timestamp}`);
    await this.config.setLocalRefreshedTime(timestamp);
    this.log(`Set local refreshed time to ${timestamp}`);
    return;
  }
  /**
   * Use the refresh token to update the access and refresh tokens.
   *
   * If another updateTokens is in progress, a new call will wait for the original to finish.
   */
  async updateTokens() {
    const myId = _ApiClient.callId++;
    this.log(`[Call ${myId}] Updating tokens`);
    if (_ApiClient.tokenLock) {
      this.log(`[Call ${myId}] Waiting for another updateTokens to finish`);
      await _ApiClient.tokenLock;
      this.log(`[Call ${myId}] Another updateTokens finished`);
      return;
    } else {
      this.log(`[Call ${myId}] Setting new token lock`);
      _ApiClient.tokenLock = new Promise((resolve) => {
        _ApiClient.tokenUnlock = () => {
          resolve(true);
          _ApiClient.tokenLock = null;
        };
        _ApiClient.tokenTimeOut = setTimeout(() => {
          resolve(false);
          _ApiClient.tokenLock = null;
        }, 1e4);
      });
    }
    try {
      if (_ApiClient.tokenUseBuffer) {
        this.log(`[Call ${myId}] Waiting for token buffer to finish`);
        await _ApiClient.tokenUseBuffer;
        this.log(`[Call ${myId}] Token buffer finished`);
        return;
      }
      const currentAccountId = await this.getAccountId();
      const currentRefreshToken = await this.getRefreshToken();
      if (!currentAccountId || !currentRefreshToken) {
        this.log(`[Call ${myId}] No accountId or refreshToken, not updating tokens`);
        return;
      }
      const isRefreshPayloadValidKey = `${currentAccountId}:${currentRefreshToken}`;
      const isRefreshPayloadValid = _ApiClient.isRefreshPayloadValidMap.get(isRefreshPayloadValidKey);
      if (isRefreshPayloadValid !== void 0 && !isRefreshPayloadValid) {
        this.log(`[Call ${myId}] Refresh payload is invalid, not updating tokens`);
        return;
      }
      const response = await fetch(`${this.config.verityOrigin}/api/access-control/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountId: currentAccountId,
          refreshToken: currentRefreshToken
        })
      });
      this.emitter.emit("refresh", response);
      this.log(`[Call ${myId}] Response received from refresh`, response);
      if (response.ok) {
        const { accessToken: accessToken2, refreshToken } = await response.json();
        if (typeof accessToken2 === "string") {
          await this.setAccessToken(accessToken2);
        }
        if (typeof refreshToken === "string") {
          await this.setRefreshToken(refreshToken);
        }
        await this.setLocalRefreshedTime(Date.now());
      } else {
        this.log(`[Call ${myId}] Response not ok from refresh`, response);
        _ApiClient.isRefreshPayloadValidMap.set(isRefreshPayloadValidKey, false);
      }
    } catch (e) {
      this.log(`[Call ${myId}] Error updating tokens`, e);
    } finally {
      this.log(`[Call ${myId}] Clearing token timeout`);
      if (_ApiClient.tokenTimeOut) {
        clearTimeout(_ApiClient.tokenTimeOut);
      }
      this.log(`[Call ${myId}] Freeing token lock`);
      _ApiClient.tokenUnlock?.(false);
      this.log(`[Call ${myId}] Creating token buffer`);
      _ApiClient.tokenUseBuffer = new Promise((resolve) => {
        setTimeout(() => {
          resolve(true);
          _ApiClient.tokenUseBuffer = null;
        }, 100);
      });
      this.log(`[Call ${myId}] Finished token update`);
    }
  }
  /**
   * Returns the JWT payload of the current access token, if available.
   * @returns {Jwt | undefined} The JWT payload of the current access token, or undefined if not available.
   */
  async getJwt() {
    this.log(`Getting JWT`);
    const currentAccessToken = await this.getAccessToken();
    if (!currentAccessToken) {
      return void 0;
    }
    const jwt = jwtDecode(currentAccessToken);
    this.log(`Got JWT`, jwt);
    return jwt;
  }
  /**
   * Sets the access token to null and ends the current session.
   */
  async resetAccessToken() {
    this.log(`Resetting access token`);
    await this.setAccessToken(void 0);
    await fetch(`${this.config.verityOrigin}/api/verity/session/end`, { credentials: "include" });
    this.emitter.emit("access-token-updated", void 0);
    this.log(`Reset access token`);
  }
  /**
   * Get the authorization header using the current access token
   */
  async generateAuthorizationHeader() {
    this.log(`Generating authorization header`);
    const accessToken2 = await this.getAccessToken();
    this.log(`Got authorization header`, { Authorization: `Bearer ${accessToken2}` });
    return { Authorization: `Bearer ${accessToken2}` };
  }
  /**
   * Mutate the fetch parameters to include the authorization header
   */
  async applyAuthenticatedFetchParams(...params) {
    this.log(`Generating authenticated fetch parameters`);
    const [originalInput, originalInit] = params;
    if (!await this.getAccessToken()) {
      return [originalInput, originalInit];
    }
    try {
      const authHeader = await this.generateAuthorizationHeader();
      if (originalInput instanceof Request && !originalInit?.headers) {
        Object.entries(authHeader).forEach(([key, value]) => originalInput.headers.set(key, value));
        this.log(`Generated authenticated fetch parameters`, { originalInput, originalInit, authHeader });
        return [originalInput, originalInit];
      } else {
        const modifiedInit = {
          ...originalInit,
          headers: {
            ...originalInit?.headers,
            ...authHeader
          }
        };
        this.log(`Generated authenticated fetch parameters`, { originalInput, originalInit, authHeader, modifiedInit });
        return [originalInput, modifiedInit];
      }
    } catch (error) {
      this.log(`Failed to generate authenticated fetch parameters`, error);
      return [originalInput, originalInit];
    }
  }
  /**
   * Update the API route config for this instance
   */
  async updateApiRouteConfig() {
    try {
      this.log(`Updating API route config`);
      const response = await fetch(`${this.config.verityOrigin}/api/verity/version/config`, {
        headers: {
          ...await this.generateAuthorizationHeader()
        }
      });
      if (!response.ok) {
        throw new Error("Failed to update API route config");
      }
      const data = await response.json();
      if (typeof data !== "object" || !data) {
        throw new Error("Invalid API route config");
      }
      const routeConfig = data;
      _ApiClient.apiRouteConfig = routeConfig;
      this.log(`Updated API route config`, routeConfig);
    } catch (error) {
      _ApiClient.apiRouteConfig = null;
      this.log(`Failed to update API route config`, error);
    }
  }
  /**
   * Use the API route config for this instance. Mutates the fetch parameters to include the API version header
   */
  applyApiRouteConfig(...params) {
    const [originalInput, originalInit] = params;
    try {
      this.log(`Using API route config`);
      if (!_ApiClient.apiRouteConfig) {
        return [originalInput, originalInit];
      }
      const targetUrl = originalInput instanceof Request ? originalInput.url : originalInput instanceof URL ? originalInput.toString() : originalInput;
      for (const { route, version } of _ApiClient.apiRouteConfig) {
        if (targetUrl.includes(route)) {
          this.log(`Using API route config for route ${route}`);
          if (originalInput instanceof Request && !originalInit?.headers) {
            Object.entries({ "X-Api-Version": version }).forEach(
              ([key, value]) => originalInput.headers.set(key, value)
            );
            this.log(`Generated API route config`, {
              originalInput,
              originalInit,
              version,
              headers: originalInput.headers
            });
            return [originalInput, originalInit];
          } else {
            const modifiedInit = {
              ...originalInit,
              headers: {
                ...originalInit?.headers,
                "X-Api-Version": version
              }
            };
            this.log(`Generated API route config`, {
              originalInput,
              originalInit,
              version,
              headers: modifiedInit.headers
            });
            return [originalInput, modifiedInit];
          }
        }
      }
      this.log(`No API route config found for route ${targetUrl}`);
      return [originalInput, originalInit];
    } catch (error) {
      this.log(`Failed to use API route config`, error);
      return [originalInput, originalInit];
    }
  }
  /**
   * Get the current route version for a specific route
   */
  getRouteVersion(route) {
    if (!_ApiClient.apiRouteConfig) {
      return "v1";
    }
    return _ApiClient.apiRouteConfig.find((config) => route.includes(config.route))?.version ?? "v1";
  }
  /**
   * Apply fetch overrides for this instance. Mutates the fetch parameters to include the authorization header and API version header
   */
  async applyFetchOverrides(...params) {
    const [originalInput, originalInit] = params;
    try {
      const [authInput, authInit] = await this.applyAuthenticatedFetchParams(originalInput, originalInit);
      const [versionedInput, versionedInit] = this.applyApiRouteConfig(authInput, authInit);
      this.log(`Applying fetch overrides`, { input: versionedInput, init: versionedInit });
      return [versionedInput, versionedInit];
    } catch (error) {
      this.log(`Failed to apply fetch overrides`, error);
      return [originalInput, originalInit];
    }
  }
  fetchPromise;
  /**
   * Clones the fetch parameters to prevent Request objects from being reused
   */
  cloneFetchParams(...params) {
    const [input, init] = params;
    if (input instanceof Request) {
      return [new Request(input), init ? { ...init } : void 0];
    }
    return [input, init ? { ...init } : void 0];
  }
  /**
   * Wrapper around fetch that applies overrides
   */
  async fetch(...params) {
    this.log(`Fetching`, params);
    if (_ApiClient.apiRouteConfig === null) {
      await this.updateApiRouteConfig();
    }
    if (this.fetchPromise) {
      await this.fetchPromise;
    }
    this.fetchPromise = (async () => {
      try {
        const response = await fetch(...await this.applyFetchOverrides(...this.cloneFetchParams(...params)));
        this.log(`Fetch response`, response);
        if (response.status === 401 || response.status === 403) {
          this.log(`Token expired, updating tokens`);
          await this.updateTokens();
          this.log(`Token updated, retrying fetch`);
          const [retryInput, retryInit] = await this.applyFetchOverrides(...this.cloneFetchParams(...params));
          return await fetch(retryInput, retryInit);
        }
        return response;
      } finally {
        this.fetchPromise = void 0;
      }
    })();
    return await this.fetchPromise;
  }
  /**
   * Clear all cookies in the current document
   */
  clearCookies() {
    if (document && document.cookie) {
      const cookies = document.cookie.split(";");
      cookies.forEach((cookie) => {
        const [name] = cookie.split("=");
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
      });
    }
  }
};

// src/errors.ts
import { Value } from "@sinclair/typebox/value";
var BaseApiClientError = class extends Error {
};
var ApiClientError = class extends BaseApiClientError {
  cause;
  constructor(cause) {
    super(`API Client Error: ${cause?.message ?? "unknown"}`);
    this.name = `ApiClientError: ${cause?.name ?? "unknown"}`;
    this.cause = cause;
  }
};
var ApiClientResponseError = class extends BaseApiClientError {
  status;
  originalMessage;
  code;
  details;
  constructor(status, { code, message, details } = {}) {
    super(`API Client Error: ${code} (${status}): ${message}`);
    this.name = `ApiClientError: ${code} (${status}): ${message}`;
    this.status = status;
    this.originalMessage = message;
    this.code = code;
    this.details = details;
  }
};
var ApiClientSchemaError = class extends BaseApiClientError {
  schema;
  value;
  constructor(schema, value) {
    super(`API Client Error: ${JSON.stringify([...Value.Errors(schema.schema, value)], void 0, 4)}`);
    this.name = "ApiClientError: schema";
    this.schema = schema;
    this.value = value;
  }
};
var UnauthenticatedError = class _UnauthenticatedError extends BaseApiClientError {
  static NAME = "UnauthenticatedError";
  constructor() {
    super(_UnauthenticatedError.NAME);
    this.name = _UnauthenticatedError.NAME;
  }
};
async function handleApiError(promise) {
  try {
    const response = await promise;
    if (response.ok) {
      return response;
    } else if (response.status === 401 || response.status === 403) {
      throw new UnauthenticatedError();
    } else {
      const errorMessage = await response.text();
      throw new ApiClientResponseError(
        response.status,
        errorMessage.includes("<!DOCTYPE html>") ? void 0 : JSON.parse(errorMessage)
      );
    }
  } catch (e) {
    if (e instanceof Error) {
      if (e instanceof BaseApiClientError) {
        throw e;
      }
      throw new ApiClientError(e);
    }
  }
  throw new ApiClientError();
}
async function handleApiErrorWithSchema(promise, schemaValidator) {
  const response = await handleApiError(promise);
  const data = await response.json();
  if (schemaValidator.validate(data)) {
    return data;
  }
  throw new ApiClientSchemaError(schemaValidator, data);
}
function withDefaultErrorHandler(instance) {
  return new Proxy(instance, {
    get(target, prop, receiver) {
      const orig = Reflect.get(target, prop, receiver);
      if (typeof orig === "function") {
        return async (...args) => {
          try {
            return await orig.apply(this, args);
          } catch (err) {
            if (err instanceof Error) throw new ApiClientError(err);
            throw new ApiClientError(new Error(`Unknown error: ${JSON.stringify(err)}`));
          }
        };
      }
      return orig;
    }
  });
}

// src/authentication/schema-validators.ts
import { Type as Type2 } from "@sinclair/typebox";

// src/schema-validator.ts
import { Value as Value2 } from "@sinclair/typebox/value";
var SchemaValidator = class {
  schema;
  // readonly #validator;
  constructor(schema) {
    this.schema = schema;
  }
  validate = (obj) => {
    return Value2.Check(this.schema, obj);
  };
};

// src/shared-schemas.ts
import { Type } from "@sinclair/typebox";
function OptionalOrNull(type) {
  return Type.Optional(Type.Union([Type.Null(), type]));
}
function PaginatedResults(type, keyName = "results") {
  const properties = {
    currentPage: Type.Number(),
    totalElements: Type.Number(),
    totalPages: Type.Number(),
    [keyName]: Type.Array(type)
  };
  return Type.Object(properties);
}

// src/authentication/schema-validators.ts
var authenticated = new SchemaValidator(
  Type2.Object({
    authenticated: Type2.Boolean()
  })
);
var accessCode = new SchemaValidator(
  Type2.Object({
    code: Type2.String()
  })
);
var accessControlAuthCode = new SchemaValidator(
  Type2.Object({
    authCode: Type2.String()
  })
);
var refreshTokenInfo = new SchemaValidator(
  Type2.Object({
    refreshToken: Type2.String(),
    accountId: Type2.String(),
    currentUserId: Type2.String()
  })
);
var accountType = Type2.Object({
  id: Type2.String(),
  email: OptionalOrNull(Type2.String()),
  currentUserId: OptionalOrNull(Type2.String()),
  userIds: Type2.Array(Type2.String())
});
var account = new SchemaValidator(accountType);
var accounts = new SchemaValidator(PaginatedResults(accountType, "accounts"));
var getAccountPortals = new SchemaValidator(
  PaginatedResults(
    Type2.Object({
      accountId: Type2.String(),
      userId: Type2.String(),
      role: Type2.String(),
      instituteId: Type2.String(),
      instituteName: OptionalOrNull(Type2.String()),
      institutePlatform: OptionalOrNull(Type2.String()),
      instituteOrigin: OptionalOrNull(Type2.String()),
      courseId: Type2.String(),
      lmsCourseId: OptionalOrNull(Type2.String()),
      courseCode: OptionalOrNull(Type2.String()),
      courseName: OptionalOrNull(Type2.String())
    }),
    "entries"
  )
);
var getAccountInstitutes = new SchemaValidator(
  PaginatedResults(
    Type2.Object({
      hostname: OptionalOrNull(Type2.String()),
      id: Type2.String(),
      name: OptionalOrNull(Type2.String()),
      origin: OptionalOrNull(Type2.String()),
      platform: OptionalOrNull(Type2.String())
    }),
    "institutes"
  )
);
var schema_validators_default = {
  authenticated,
  accessCode,
  accessControlAuthCode,
  refreshTokenInfo,
  account,
  accounts,
  getAccountPortals,
  getAccountInstitutes
};

// src/authentication/index.ts
var AuthenticationClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default);
  }
  isAuthenticated = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/version/config`, {
        credentials: "include"
      });
      if (response.ok) {
        return true;
      }
      return false;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessCode = async (body) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/session/access-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body)
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessCode.validate(obj)) {
          return obj.code;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessControlAuthCode = async () => {
    try {
      const accountId = await this.getAccountId();
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/account/${accountId}/auth-code`,
        {
          method: "POST"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessControlAuthCode.validate(obj)) {
          return { accountId, authCode: obj.authCode };
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getRefreshTokenFromAuthCode = async (accountId, authCode) => {
    try {
      const response = await fetch(
        `${this.config.verityOrigin}/api/access-control/account/${accountId}/refresh-token`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            authCode
          })
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.refreshTokenInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  validateUserFromServer = async (suffix) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/session/validate/user?${suffix}`, {
        credentials: "include"
      });
      if (response.ok && response.status === 200) {
        return true;
      }
      return false;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
  };
  getAccount = async (accountId) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/account/${accountId}`);
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.account.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccounts = ({ email }) => {
    const searchParams = new URLSearchParams();
    if (email) {
      searchParams.set("email", email);
    }
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/access-control/account?${searchParams.toString()}`),
      this.schemaValidators.accounts
    );
  };
  logout = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/session/end`, {
        credentials: "include"
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  logFailedAuth = async (message) => {
    try {
      await super.fetch(`${this.config.verityOrigin}/api/verity/debug/auth`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message
        })
      });
      return;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
  };
  getAccountPortals = async ({
    accountId,
    searchQuery,
    page,
    size
  }) => {
    try {
      const queryParams = new URLSearchParams();
      if (searchQuery) queryParams.set("searchQuery", searchQuery);
      queryParams.set("page", page.toString());
      queryParams.set("size", size.toString());
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/account/${accountId}/portals?${queryParams.toString()}`,
        {
          method: "GET",
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getAccountPortals.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccountInstitutes = async ({ page = 0, size = 1e3 }) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set("page", page.toString());
      queryParams.set("size", size.toString());
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/institute?${queryParams.toString()}`,
        {
          method: "GET",
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getAccountInstitutes.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  setCurrentuserId = async ({ accountId, userId }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/account/${accountId}/current-user/${userId}`,
        {
          method: "PUT",
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  mapNonLmsUser = async ({
    userUuid,
    courseUuid,
    instituteUuid
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/portal/map`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userUuid,
          courseUuid,
          instituteUuid
        })
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/logger/access-code/schema-validators.ts
import { Type as Type3 } from "@sinclair/typebox";
var accessCodeDisableEvent = new SchemaValidator(
  Type3.Object({
    type: Type3.Literal("access code"),
    name: Type3.Literal("save password disabled"),
    time: Type3.Date(),
    details: Type3.Object({
      success: Type3.Boolean()
    })
  })
);
var accessCodeResetEvent = new SchemaValidator(
  Type3.Object({
    type: Type3.Literal("access code"),
    name: Type3.Literal("save password reset"),
    time: Type3.Date(),
    details: Type3.Object({
      success: Type3.Boolean()
    })
  })
);
var accessCodeAutofillEvent = new SchemaValidator(
  Type3.Object({
    type: Type3.Literal("access code"),
    name: Type3.Literal("autofill"),
    time: Type3.Date(),
    details: Type3.Object({
      success: Type3.Boolean(),
      password: Type3.Optional(Type3.String())
    })
  })
);
var schema_validators_default2 = {
  accessCodeDisableEvent,
  accessCodeResetEvent,
  accessCodeAutofillEvent
};

// src/proctoring-session/logger/access-code/index.ts
var AccessCodeLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default2);
    this.emitter = emitter2;
  }
  async #logAccessCodeEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  disable = async (proctoringSessionId, details) => {
    await this.#logAccessCodeEvent(proctoringSessionId, {
      type: "access code",
      name: "save password disabled",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  reset = async (proctoringSessionId, details) => {
    await this.#logAccessCodeEvent(proctoringSessionId, {
      type: "access code",
      name: "save password reset",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  autofill = async (proctoringSessionId, details) => {
    await this.#logAccessCodeEvent(proctoringSessionId, {
      type: "access code",
      name: "autofill",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/clipboard/schema-validators.ts
import { Type as Type4 } from "@sinclair/typebox";
var clipboardDisableEvent = new SchemaValidator(
  Type4.Object({
    type: Type4.Literal("clipboard"),
    name: Type4.Literal("disable"),
    time: Type4.Date(),
    details: Type4.Object({
      success: Type4.Boolean()
    })
  })
);
var clipboardKeypressEvent = new SchemaValidator(
  Type4.Object({
    type: Type4.Literal("clipboard"),
    name: Type4.Literal("keypress"),
    time: Type4.Date(),
    details: Type4.Object({
      keypress: Type4.String(),
      value: Type4.Optional(Type4.String())
    })
  })
);
var schema_validators_default3 = {
  clipboardDisableEvent,
  clipboardKeypressEvent
};

// src/proctoring-session/logger/clipboard/index.ts
var ClipboardLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default3);
    this.emitter = emitter2;
  }
  async #logClipboardEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  disable = async (proctoringSessionId, details) => {
    await this.#logClipboardEvent(proctoringSessionId, {
      type: "clipboard",
      name: "disable",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  keypress = async (proctoringSessionId, details) => {
    await this.#logClipboardEvent(proctoringSessionId, {
      type: "clipboard",
      name: "keypress",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/http/schema-validators.ts
import { Type as Type5 } from "@sinclair/typebox";
var httpWebNavigationEvent = new SchemaValidator(
  Type5.Object({
    type: Type5.Literal("web navigation"),
    name: Type5.Union([Type5.Literal("webRequest"), Type5.Literal("webNavigation")]),
    time: Type5.Date(),
    details: Type5.Object({
      url: Type5.String(),
      method: Type5.Optional(Type5.String()),
      frame: Type5.Optional(Type5.String())
    })
  })
);
var schema_validators_default4 = {
  httpWebNavigationEvent
};

// src/proctoring-session/logger/http/index.ts
var HttpLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default4);
    this.emitter = emitter2;
  }
  webNavigation = async (proctoringSessionId, name, details) => {
    try {
      const event = {
        type: "web navigation",
        name,
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schema_validators_default4.httpWebNavigationEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/logger/screenshot/schema-validators.ts
import { Type as Type6 } from "@sinclair/typebox";
var screenshotDisableEvent = new SchemaValidator(
  Type6.Object({
    type: Type6.Literal("screenshot"),
    name: Type6.Literal("disable"),
    time: Type6.Date(),
    details: Type6.Object({
      success: Type6.Boolean()
    })
  })
);
var screenshotKeypressEvent = new SchemaValidator(
  Type6.Object({
    type: Type6.Literal("screenshot"),
    name: Type6.Literal("keypress"),
    time: Type6.Date(),
    details: Type6.Object({
      keypress: Type6.String()
    })
  })
);
var schema_validators_default5 = {
  screenshotDisableEvent,
  screenshotKeypressEvent
};

// src/proctoring-session/logger/screenshot/index.ts
var ScreenshotLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default5);
    this.emitter = emitter2;
  }
  async #logScreenshotEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  disable = async (proctoringSessionId, details) => {
    await this.#logScreenshotEvent(proctoringSessionId, {
      type: "screenshot",
      name: "disable",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  keypress = async (proctoringSessionId, details) => {
    await this.#logScreenshotEvent(proctoringSessionId, {
      type: "screenshot",
      name: "keypress",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/session/schema-validators.ts
import { Type as Type7 } from "@sinclair/typebox";
var startAssessmentEvent = new SchemaValidator(
  Type7.Object({
    type: Type7.Literal("session"),
    name: Type7.Literal("start quiz"),
    time: Type7.Date(),
    details: Type7.Object({
      success: Type7.Boolean()
    })
  })
);
var endAssessmentEvent = new SchemaValidator(
  Type7.Object({
    type: Type7.Literal("session"),
    name: Type7.Literal("end quiz"),
    time: Type7.Date(),
    details: Type7.Object({
      success: Type7.Boolean()
    })
  })
);
var endProctoringEvent = new SchemaValidator(
  Type7.Object({
    type: Type7.Literal("session"),
    name: Type7.Literal("end proctoring"),
    time: Type7.Date(),
    details: Type7.Object({
      success: Type7.Boolean()
    })
  })
);
var schema_validators_default6 = {
  startAssessmentEvent,
  endAssessmentEvent,
  endProctoringEvent
};

// src/proctoring-session/logger/session/index.ts
var SessionLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default6);
    this.emitter = emitter2;
  }
  async #proctoringEvent({
    proctoringSessionId,
    event
  }) {
    try {
      this.emitter.emit("log", event);
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
        credentials: "include"
      });
      if (!response.ok) {
        throw new ApiClientError(new Error(`Invalid Response: ${response.status} - ${response.statusText}`));
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
  }
  startAssessment = async ({
    proctoringSessionId,
    event
  }) => {
    return await this.#proctoringEvent({
      proctoringSessionId,
      event: {
        type: "session",
        name: "start quiz",
        ...event
      }
    });
  };
  endAssessment = async ({
    proctoringSessionId,
    event
  }) => {
    return await this.#proctoringEvent({
      proctoringSessionId,
      event: {
        type: "session",
        name: "end quiz",
        ...event
      }
    });
  };
  endProctoring = async ({
    proctoringSessionId,
    event
  }) => {
    return await this.#proctoringEvent({
      proctoringSessionId,
      event: {
        type: "session",
        name: "end proctoring",
        ...event
      }
    });
  };
};

// src/proctoring-session/logger/tab/schema-validators.ts
import { Type as Type8 } from "@sinclair/typebox";
var tabCloseOneEvent = new SchemaValidator(
  Type8.Object({
    type: Type8.Literal("tab"),
    name: Type8.Literal("close one"),
    time: Type8.Date(),
    details: Type8.Object({
      url: Type8.String(),
      title: Type8.String(),
      autoClose: Type8.Boolean()
    })
  })
);
var tabCreateEvent = new SchemaValidator(
  Type8.Object({
    type: Type8.Literal("tab"),
    name: Type8.Literal("create"),
    time: Type8.Date(),
    details: Type8.Object({
      url: Type8.String(),
      title: Type8.String()
    })
  })
);
var tabWhitelistEvent = new SchemaValidator(
  Type8.Object({
    type: Type8.Literal("tab"),
    name: Type8.Literal("whitelist"),
    time: Type8.Date(),
    details: Type8.Object({
      success: Type8.Boolean(),
      whiteList: Type8.Array(Type8.String())
    })
  })
);
var tabDisableEvent = new SchemaValidator(
  Type8.Object({
    type: Type8.Literal("tab"),
    name: Type8.Literal("disable"),
    time: Type8.Date(),
    details: Type8.Object({
      success: Type8.Boolean()
    })
  })
);
var tabFocusEvent = new SchemaValidator(
  Type8.Object({
    type: Type8.Literal("tab"),
    name: Type8.Literal("focus"),
    time: Type8.Date(),
    details: Type8.Object({
      focus: Type8.Boolean()
    })
  })
);
var schema_validators_default7 = {
  tabCloseOneEvent,
  tabCreateEvent,
  tabWhitelistEvent,
  tabDisableEvent,
  tabFocusEvent
};

// src/proctoring-session/logger/tab/index.ts
var TabLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default7);
    this.emitter = emitter2;
  }
  async #logTabEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  closeOne = async (proctoringSessionId, details) => {
    await this.#logTabEvent(proctoringSessionId, {
      type: "tab",
      name: "close one",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  create = async (proctoringSessionId, details) => {
    await this.#logTabEvent(proctoringSessionId, {
      type: "tab",
      name: "create",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  whitelist = async (proctoringSessionId, details) => {
    await this.#logTabEvent(proctoringSessionId, {
      type: "tab",
      name: "whitelist",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  disable = async (proctoringSessionId, details) => {
    await this.#logTabEvent(proctoringSessionId, {
      type: "tab",
      name: "disable",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  focus = async (proctoringSessionId, details) => {
    await this.#logTabEvent(proctoringSessionId, {
      type: "tab",
      name: "focus",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/keyboard/schema-validators.ts
import { Type as Type9 } from "@sinclair/typebox";
var keyboardKeypressEvent = new SchemaValidator(
  Type9.Object({
    type: Type9.Literal("keyboard"),
    name: Type9.Literal("keypress"),
    time: Type9.Date(),
    details: Type9.Object({
      value: Type9.String()
    })
  })
);
var schema_validators_default8 = {
  keyboardKeypressEvent
};

// src/proctoring-session/logger/keyboard/index.ts
var KeyboardLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default8);
    this.emitter = emitter2;
  }
  async #logKeyboardEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  keypress = async (proctoringSessionId, details) => {
    await this.#logKeyboardEvent(proctoringSessionId, {
      type: "keyboard",
      name: "keypress",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/index.ts
import EventEmitter2 from "events";

// src/proctoring-session/logger/live/schema-validators.ts
import { Type as Type10 } from "@sinclair/typebox";
var liveBuildAudioBridgeEvent = new SchemaValidator(
  Type10.Object({
    type: Type10.Literal("live"),
    name: Type10.Literal("audio bridge"),
    time: Type10.Date(),
    details: Type10.Object({
      success: Type10.Boolean()
    })
  })
);
var liveProctorJoinedEvent = new SchemaValidator(
  Type10.Object({
    type: Type10.Literal("live"),
    name: Type10.Literal("proctor joined"),
    time: Type10.Date(),
    details: Type10.Object({
      proctorId: Type10.String(),
      count: Type10.Optional(Type10.Number())
    })
  })
);
var liveProctorLeftEvent = new SchemaValidator(
  Type10.Object({
    type: Type10.Literal("live"),
    name: Type10.Literal("proctor left"),
    time: Type10.Date(),
    details: Type10.Object({
      proctorId: Type10.String(),
      count: Type10.Optional(Type10.Number())
    })
  })
);
var liveJoinSessionEvent = new SchemaValidator(
  Type10.Object({
    type: Type10.Literal("live"),
    name: Type10.Literal("join session"),
    time: Type10.Date(),
    details: Type10.Object({
      liveSessionId: Type10.String(),
      liveUserSessionId: Type10.String(),
      proctorIds: Type10.Optional(Type10.Array(Type10.String())),
      proctorGroupIds: Type10.Optional(Type10.Array(Type10.Number()))
    })
  })
);
var noProctorsEvent = new SchemaValidator(
  Type10.Object({
    type: Type10.Literal("live"),
    name: Type10.Literal("no proctors"),
    time: Type10.Date(),
    details: Type10.Object({
      isOnJoin: Type10.Boolean()
    })
  })
);
var schema_validators_default9 = {
  liveBuildAudioBridgeEvent,
  liveProctorJoinedEvent,
  liveProctorLeftEvent,
  liveJoinSessionEvent,
  noProctorsEvent
};

// src/proctoring-session/logger/live/index.ts
var LiveLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default9);
    this.emitter = emitter2;
  }
  createAudioBridge = async (proctoringSessionId, details) => {
    try {
      const event = {
        type: "live",
        name: "audio bridge",
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schema_validators_default9.liveBuildAudioBridgeEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  proctorJoined = async (proctoringSessionId, details) => {
    try {
      const event = {
        type: "live",
        name: "proctor joined",
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schema_validators_default9.liveProctorJoinedEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  proctorLeft = async (proctoringSessionId, details) => {
    try {
      const event = {
        type: "live",
        name: "proctor left",
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schema_validators_default9.liveProctorLeftEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  joinedSession = async (proctoringSessionId, details) => {
    try {
      const event = {
        type: "live",
        name: "join session",
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schema_validators_default9.liveJoinSessionEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  noProctors = async (proctoringSessionId, details) => {
    try {
      const event = {
        type: "live",
        name: "no proctors",
        time: /* @__PURE__ */ new Date(),
        details
      };
      this.emitter.emit("log", event);
      if (schema_validators_default9.noProctorsEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/logger/system/schema-validators.ts
import { Type as Type11 } from "@sinclair/typebox";
var connectionCheckEvent = new SchemaValidator(
  Type11.Object({
    type: Type11.Literal("system"),
    name: Type11.Literal("connection check"),
    time: Type11.Date(),
    details: Type11.Object({
      metrics: Type11.Any(),
      uploadSpeed: Type11.Number()
    })
  })
);
var systemResourceEvent = new SchemaValidator(
  Type11.Object({
    type: Type11.Literal("system"),
    name: Type11.Literal("system resources"),
    time: Type11.Date(),
    details: Type11.Object({
      cpuUsage: Type11.Number(),
      memoryUsage: Type11.Number()
    })
  })
);
var notInGeofenceEvent = new SchemaValidator(
  Type11.Object({
    type: Type11.Literal("system"),
    name: Type11.Literal("not in geofence"),
    time: Type11.Date(),
    details: Type11.Object({})
  })
);
var locationUnknownEvent = new SchemaValidator(
  Type11.Object({
    type: Type11.Literal("system"),
    name: Type11.Literal("location unknown"),
    time: Type11.Date(),
    details: Type11.Object({})
  })
);
var schema_validators_default10 = {
  connectionCheckEvent,
  systemResourceEvent,
  notInGeofenceEvent,
  locationUnknownEvent
};

// src/proctoring-session/logger/system/index.ts
var SystemLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default10);
    this.emitter = emitter2;
  }
  connectionCheck = async ({
    proctoringSessionId,
    details
  }) => {
    const event = {
      type: "system",
      name: "connection check",
      time: /* @__PURE__ */ new Date(),
      details
    };
    this.emitter.emit("log", event);
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
        credentials: "include"
      })
    );
  };
  systemResources = async ({
    proctoringSessionId,
    details
  }) => {
    const event = {
      type: "system",
      name: "system resources",
      time: /* @__PURE__ */ new Date(),
      details
    };
    this.emitter.emit("log", event);
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
        credentials: "include"
      })
    );
  };
  notInGeofence = async ({
    proctoringSessionId,
    details
  }) => {
    const event = {
      type: "system",
      name: "not in geofence",
      time: /* @__PURE__ */ new Date(),
      details
    };
    this.emitter.emit("log", event);
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
        credentials: "include"
      })
    );
  };
  locationUnknown = async ({
    proctoringSessionId,
    details
  }) => {
    const event = {
      type: "system",
      name: "location unknown",
      time: /* @__PURE__ */ new Date(),
      details
    };
    this.emitter.emit("log", event);
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
        credentials: "include"
      })
    );
  };
};

// src/proctoring-session/logger/face-detection/schema-validators.ts
import { Type as Type12 } from "@sinclair/typebox";
var otherFaceDetectedEvent = new SchemaValidator(
  Type12.Object({
    type: Type12.Literal("face detection"),
    name: Type12.Literal("other"),
    time: Type12.Date(),
    details: Type12.Object({
      count: Type12.Number()
    })
  })
);
var targetFaceDetectedEvent = new SchemaValidator(
  Type12.Object({
    type: Type12.Literal("face detection"),
    name: Type12.Literal("target"),
    time: Type12.Date(),
    details: Type12.Object({
      count: Type12.Number()
    })
  })
);
var schema_validators_default11 = {
  otherFaceDetectedEvent,
  targetFaceDetectedEvent
};

// src/proctoring-session/logger/face-detection/index.ts
var FaceDetectionLoggerClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default11);
  }
  async facesDetected(proctoringSessionId, { who, count: count2 }) {
    try {
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "face detection",
          name: who,
          time: /* @__PURE__ */ new Date(),
          details: {
            count: count2
          }
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
};

// src/proctoring-session/logger/interrupt/schema-validators.ts
import { Type as Type13 } from "@sinclair/typebox";
var interruptTriggerEvent = new SchemaValidator(
  Type13.Object({
    type: Type13.Literal("interrupt"),
    name: Type13.Literal("trigger"),
    time: Type13.Date(),
    details: Type13.Object({
      success: Type13.Boolean(),
      type: Type13.Union([
        Type13.Literal("webcam"),
        Type13.Literal("display"),
        Type13.Literal("monitor count"),
        Type13.Literal("face recorder"),
        Type13.Literal("speaking"),
        Type13.Literal("enables_blocked_extension")
      ]),
      extNames: Type13.Optional(Type13.Array(Type13.String()))
    })
  })
);
var interruptResumeEvent = new SchemaValidator(
  Type13.Object({
    type: Type13.Literal("interrupt"),
    name: Type13.Literal("resume"),
    time: Type13.Date(),
    details: Type13.Object({
      success: Type13.Boolean(),
      type: Type13.Union([
        Type13.Literal("webcam"),
        Type13.Literal("display"),
        Type13.Literal("monitor count"),
        Type13.Literal("face recorder"),
        Type13.Literal("enables_blocked_extension")
      ])
    })
  })
);
var schema_validators_default12 = {
  interruptTriggerEvent,
  interruptResumeEvent
};

// src/proctoring-session/logger/interrupt/index.ts
var InterruptLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default12);
    this.emitter = emitter2;
  }
  async #logInterruptEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  trigger = async (proctoringSessionId, details) => {
    await this.#logInterruptEvent(proctoringSessionId, {
      type: "interrupt",
      name: "trigger",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  resume = async (proctoringSessionId, details) => {
    await this.#logInterruptEvent(proctoringSessionId, {
      type: "interrupt",
      name: "resume",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
};

// src/proctoring-session/logger/user-action/schema-validators.ts
import { Type as Type14 } from "@sinclair/typebox";
var sidePanelDetectedEvent = new SchemaValidator(
  Type14.Object({
    type: Type14.Literal("user action"),
    name: Type14.Literal("side panel detected"),
    time: Type14.Date(),
    details: Type14.Object({})
  })
);
var rightClickBlockedEvent = new SchemaValidator(
  Type14.Object({
    type: Type14.Literal("user action"),
    name: Type14.Literal("right click blocked"),
    time: Type14.Date(),
    details: Type14.Object({})
  })
);
var printingBlockedEvent = new SchemaValidator(
  Type14.Object({
    type: Type14.Literal("user action"),
    name: Type14.Literal("printing blocked"),
    time: Type14.Date(),
    details: Type14.Object({})
  })
);
var typedSignatureEvent = new SchemaValidator(
  Type14.Object({
    type: Type14.Literal("user action"),
    name: Type14.Literal("typed signature"),
    time: Type14.Date(),
    details: Type14.Object({
      signature: Type14.String()
    })
  })
);
var schema_validators_default13 = {
  sidePanelDetectedEvent,
  rightClickBlockedEvent,
  printingBlockedEvent,
  typedSignatureEvent
};

// src/proctoring-session/logger/user-action/index.ts
var UserActionLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default13);
    this.emitter = emitter2;
  }
  async #logUserActionEvent(proctoringSessionId, event) {
    try {
      this.emitter.emit("log", event);
      const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: event.type,
          name: event.name,
          time: event.time,
          details: event.details
        }),
        credentials: "include"
      });
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  sidePanelDetected = async (proctoringSessionId, details) => {
    await this.#logUserActionEvent(proctoringSessionId, {
      type: "user action",
      name: "side panel detected",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  rightClickBlocked = async (proctoringSessionId, details) => {
    await this.#logUserActionEvent(proctoringSessionId, {
      type: "user action",
      name: "right click blocked",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  printingBlocked = async (proctoringSessionId, details) => {
    await this.#logUserActionEvent(proctoringSessionId, {
      type: "user action",
      name: "printing blocked",
      time: /* @__PURE__ */ new Date(),
      details
    });
  };
  logTypedSignatureEvent = async ({
    proctoringSessionId,
    details
  }) => {
    try {
      const event = {
        type: "user action",
        name: "typed signature",
        time: /* @__PURE__ */ new Date(),
        details: { signature: details.signature }
      };
      if (schema_validators_default13.typedSignatureEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/logger/worker/schema-validators.ts
import { Type as Type15 } from "@sinclair/typebox";
var detectedFace = Type15.Object({
  bbox: Type15.Object({
    normalizedX: Type15.Number(),
    normalizedY: Type15.Number(),
    normalizedWidth: Type15.Number(),
    normalizedHeight: Type15.Number()
  }),
  bboxRaw: Type15.Object({
    x: Type15.Number(),
    y: Type15.Number(),
    width: Type15.Number(),
    height: Type15.Number()
  }),
  score: Type15.Number(),
  gestures: Type15.Array(Type15.String()),
  similarity: OptionalOrNull(Type15.Number()),
  embedding: OptionalOrNull(Type15.Array(Type15.Number()))
});
var faceWorkerEvent = new SchemaValidator(
  Type15.Partial(
    Type15.Object({
      type: Type15.String(),
      name: Type15.String(),
      time: Type15.Date(),
      details: Type15.Object({
        topic: Type15.String(),
        metadata: OptionalOrNull(
          Type15.Object({
            duration: OptionalOrNull(Type15.Number())
          })
        ),
        detections: Type15.Array(Type15.Partial(detectedFace))
      })
    })
  )
);
var detectedObject = Type15.Object({
  bbox: Type15.Object({
    normalizedX: Type15.Number(),
    normalizedY: Type15.Number(),
    normalizedWidth: Type15.Number(),
    normalizedHeight: Type15.Number()
  }),
  bboxRaw: Type15.Object({
    x: Type15.Number(),
    y: Type15.Number(),
    width: Type15.Number(),
    height: Type15.Number()
  }),
  score: Type15.Number(),
  name: Type15.String()
});
var objectWorkerEvent = new SchemaValidator(
  Type15.Partial(
    Type15.Object({
      type: Type15.String(),
      name: Type15.String(),
      time: Type15.Date(),
      details: Type15.Object({
        topic: Type15.String(),
        metadata: OptionalOrNull(
          Type15.Object({
            duration: OptionalOrNull(Type15.Number())
          })
        ),
        detections: Type15.Array(Type15.Partial(detectedObject))
      })
    })
  )
);
var schema_validators_default14 = {
  faceWorkerEvent,
  objectWorkerEvent
};

// src/proctoring-session/logger/worker/index.ts
var WorkerLoggerClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default14);
  }
  logFaceWorkerEvent = async ({
    sessionId,
    event
  }) => {
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event)
      })
    );
  };
  logObjectWorkerEvent = async ({
    sessionId,
    event
  }) => {
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event)
      })
    );
  };
};

// src/proctoring-session/logger/media/schema-validators.ts
import { Type as Type16 } from "@sinclair/typebox";
var logStartRecording = new SchemaValidator(Type16.String());
var schema_validators_default15 = {
  logStartRecording
};

// src/proctoring-session/logger/media/index.ts
var MediaLoggerClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default15);
  }
  logStartRecording = async ({
    sessionId,
    recordingDetails
  }) => {
    const response = await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "session",
          name: "start recording",
          time: /* @__PURE__ */ new Date(),
          details: { ...recordingDetails, success: true }
        })
      })
    );
    try {
      const data = await response.json();
      if (!schema_validators_default15.logStartRecording.validate(data)) {
        throw new ApiClientError(new Error(`Invalid log start recording: ${JSON.stringify(data)}`));
      }
      return data;
    } catch (error) {
      return "";
    }
  };
  logEndRecording = async ({
    sessionId,
    recordingDetails
  }) => {
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "session",
          name: "end recording",
          time: /* @__PURE__ */ new Date(),
          details: { ...recordingDetails, success: true }
        })
      })
    );
  };
  logEndUpload = async ({
    sessionId,
    recordingDetails
  }) => {
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "session",
          name: "end upload",
          time: /* @__PURE__ */ new Date(),
          details: recordingDetails
        })
      })
    );
  };
};

// src/proctoring-session/logger/machine-learning/index.ts
import { LogMachineLearningQuizEventContract, coerceToContractAbsoluteBase, withContractBase } from "@yujacorp/verity-api-contract";
var MachineLearningLoggerClient = class extends ApiClient {
  constructor(config) {
    super(config, {});
  }
  logMachineLearningQuizEvent = withDefaultErrorHandler(async (dynamicInput) => {
    const handlers = withContractBase({
      contract: LogMachineLearningQuizEventContract,
      base: coerceToContractAbsoluteBase(`${this.config.verityOrigin}/api/verity/`)
    }).withFetchParams({
      pathParams: dynamicInput.pathParams,
      body: dynamicInput.body
    }).withFetcher(async ({ method, url: url2, body }) => {
      const res = await this.fetch(url2, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : void 0
      });
      return { ok: res.ok };
    });
    return handlers.toRaw().then((value) => value.ok);
  });
};

// src/proctoring-session/logger/id/schema-validators.ts
import { Type as Type17 } from "@sinclair/typebox";
var idEvent = new SchemaValidator(
  Type17.Object({
    type: Type17.Literal("id"),
    name: Type17.Union([Type17.Literal("face id mismatch"), Type17.Literal("bypassed ai detection")]),
    time: Type17.Date(),
    details: Type17.Object({
      similarity: Type17.Optional(Type17.Number({ minimum: 0, maximum: 1 })),
      success: Type17.Optional(Type17.Boolean())
    })
  })
);
var schema_validators_default16 = {
  idEvent
};

// src/proctoring-session/logger/id/index.ts
var IdLoggerClient = class extends ApiClient {
  emitter;
  constructor(config, emitter2) {
    super(config, schema_validators_default16);
    this.emitter = emitter2;
  }
  logIdEvent = async ({
    proctoringSessionId,
    name,
    details
  }) => {
    try {
      const event = {
        type: "id",
        name,
        time: /* @__PURE__ */ new Date(),
        details: name === "face id mismatch" ? details : { success: true }
      };
      this.emitter.emit("log", event);
      if (schema_validators_default16.idEvent.validate(event)) {
        const resp = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(event),
          credentials: "include"
        });
        if (resp.ok) {
          return true;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/logger/index.ts
var emitter = new EventEmitter2();
var LoggerClient = class extends ApiClient {
  TabLoggerClient;
  HttpLoggerClient;
  AccessCodeLoggerClient;
  ClipboardLoggerClient;
  ScreenshotLoggerClient;
  SessionLoggerClient;
  InterruptLoggerClient;
  KeyboardLoggerClient;
  LiveLoggerClient;
  SystemLoggerClient;
  FaceDetectionLoggerClient;
  UserActionLoggerClient;
  WorkerLoggerClient;
  MediaLoggerClient;
  MachineLearningLoggerClient;
  IdLoggerClient;
  on;
  off;
  once;
  // readonly ToolbarLoggerClient: ToolbarLoggerClient;
  constructor(config) {
    super(config, {});
    this.TabLoggerClient = new TabLoggerClient(config, emitter);
    this.HttpLoggerClient = new HttpLoggerClient(config, emitter);
    this.AccessCodeLoggerClient = new AccessCodeLoggerClient(config, emitter);
    this.ClipboardLoggerClient = new ClipboardLoggerClient(config, emitter);
    this.ScreenshotLoggerClient = new ScreenshotLoggerClient(config, emitter);
    this.SessionLoggerClient = new SessionLoggerClient(config, emitter);
    this.InterruptLoggerClient = new InterruptLoggerClient(config, emitter);
    this.KeyboardLoggerClient = new KeyboardLoggerClient(config, emitter);
    this.LiveLoggerClient = new LiveLoggerClient(config, emitter);
    this.SystemLoggerClient = new SystemLoggerClient(config, emitter);
    this.FaceDetectionLoggerClient = new FaceDetectionLoggerClient(config);
    this.UserActionLoggerClient = new UserActionLoggerClient(config, emitter);
    this.WorkerLoggerClient = new WorkerLoggerClient(config);
    this.MediaLoggerClient = new MediaLoggerClient(config);
    this.MachineLearningLoggerClient = new MachineLearningLoggerClient(config);
    this.IdLoggerClient = new IdLoggerClient(config, emitter);
    this.on = emitter.on.bind(emitter);
    this.once = emitter.once.bind(emitter);
    this.off = emitter.off.bind(emitter);
  }
};

// src/proctoring-session/schema-validators.ts
import { Type as Type19 } from "@sinclair/typebox";

// src/verity-options/schema-validators.ts
import { Type as Type18 } from "@sinclair/typebox";
var recordingModeType = Type18.Union([Type18.Literal("off"), Type18.Literal("normal"), Type18.Literal("strict")]);
var proctoringOptionsType = Type18.Object({
  enforceLockdown: Type18.Boolean(),
  enforceMobile: Type18.Boolean(),
  isExtension: Type18.Boolean(),
  isLockdown: Type18.Boolean(),
  isMobile: Type18.Boolean(),
  isChromeOs: Type18.Boolean(),
  videoRecordingMode: recordingModeType,
  audioRecordingMode: recordingModeType,
  recordScreen: Type18.Boolean(),
  recordWebTraffic: Type18.Boolean(),
  detectSidePanel: Type18.Boolean(),
  excludedWebTrafficRecordingUrls: OptionalOrNull(Type18.Array(Type18.String())),
  recordClipboard: Type18.Boolean(),
  recordInput: Type18.Boolean(),
  oneScreenEnforcementMode: recordingModeType,
  disableNewTab: Type18.Boolean(),
  closeOpenTabs: Type18.Boolean(),
  tabWhiteList: OptionalOrNull(Type18.Array(Type18.String())),
  tabBlockList: OptionalOrNull(Type18.Array(Type18.String())),
  keyboardShortcutBlockList: OptionalOrNull(Type18.Array(Type18.String())),
  disableClipboardMode: Type18.Union([Type18.Literal("off"), Type18.Literal("tab"), Type18.Literal("system")]),
  enableGuidedAccessMode: Type18.Boolean(),
  restrictHandoffMode: recordingModeType,
  disableSpellcheck: Type18.Boolean(),
  disablePrinting: Type18.Boolean(),
  disableRightClick: Type18.Boolean(),
  extensionBlockMode: recordingModeType,
  allowedExtensionsList: OptionalOrNull(Type18.Array(Type18.String())),
  disableDownloadFiles: Type18.Boolean(),
  hideOtherWindows: Type18.Boolean(),
  showRecordingPanel: Type18.Boolean(),
  idVerification: Type18.Boolean(),
  idVerificationEnableAi: Type18.Boolean(),
  faceTrackingMode: recordingModeType,
  objectDetectionMode: recordingModeType,
  applicationBlockMode: recordingModeType,
  applicationBlockList: OptionalOrNull(Type18.Array(Type18.String())),
  blockVirtualMachines: Type18.Boolean(),
  recordApplicationsActivity: Type18.Boolean(),
  isLiveProctored: Type18.Boolean(),
  isLivePopIn: Type18.Boolean(),
  enablePostReview: Type18.Boolean(),
  scanRoom: Type18.Boolean(),
  quietEnvironment: Type18.Boolean(),
  enableCalculator: Type18.Union([Type18.Literal("off"), Type18.Literal("scientific"), Type18.Literal("standard")]),
  enableWhiteboard: Type18.Boolean(),
  uploadScratchNotes: Type18.Boolean(),
  idVerificationMode: recordingModeType,
  restrictDevices: Type18.Boolean(),
  audioTranscription: Type18.Boolean(),
  flaggedKeywords: Type18.Union([
    Type18.Null(),
    Type18.Array(
      Type18.Object({
        keyword: Type18.String(),
        level: Type18.Optional(Type18.Number())
      })
    )
  ]),
  uploadSignature: Type18.Boolean(),
  enableGeofence: Type18.Boolean(),
  geofenceLocations: OptionalOrNull(
    Type18.Array(
      Type18.Object({
        longitude: Type18.Number(),
        latitude: Type18.Number(),
        radius: Type18.Number(),
        name: Type18.String(),
        isMetric: Type18.Boolean()
      })
    )
  ),
  disableVirtualCamera: Type18.Boolean()
});
var chromeOsAccessType = OptionalOrNull(
  Type18.Union([Type18.Literal("managed-guest-session"), Type18.Literal("all")])
);
var verityOptions = new SchemaValidator(
  Type18.Object({
    ...proctoringOptionsType.properties,
    enabled: Type18.Boolean(),
    requiresIncognito: Type18.Boolean(),
    chromeOsAccess: chromeOsAccessType,
    logEvents: Type18.Boolean(),
    password: OptionalOrNull(Type18.String()),
    thirdPartyAssessmentOptions: OptionalOrNull(
      Type18.Object({
        thirdPartyUrl: OptionalOrNull(Type18.String()),
        password: OptionalOrNull(Type18.String()),
        thirdPartyPlatformId: OptionalOrNull(Type18.Number())
      })
    )
  })
);
var applicationResponse = new SchemaValidator(
  Type18.Array(
    Type18.Object({
      id: Type18.Number(),
      name: Type18.String(),
      processName: Type18.Array(Type18.String()),
      iconSvg: Type18.String(),
      type: Type18.Union([
        Type18.Literal("Browser"),
        Type18.Literal("Communication"),
        Type18.Literal("Screen Sharing"),
        Type18.Literal("Other")
      ]),
      createdAt: Type18.String(),
      updatedAt: Type18.String()
    })
  )
);
var extensionResponse = new SchemaValidator(
  Type18.Array(
    Type18.Object({
      id: Type18.Number(),
      extensionId: Type18.String(),
      extensionName: Type18.String(),
      iconSvg: Type18.String(),
      type: Type18.Union([Type18.Literal("Writing Tools"), Type18.Literal("Screen Capture Tools"), Type18.Literal("Other")]),
      createdAt: Type18.String(),
      updatedAt: Type18.String()
    })
  )
);
var schema_validators_default17 = {
  verityOptions,
  applicationResponse,
  extensionResponse
};

// src/proctoring-session/schema-validators.ts
function StringLiteralUnion(values) {
  const literals = values.map((value) => Type19.Literal(value));
  return Type19.Union(literals);
}
var startProctoring = new SchemaValidator(
  Type19.Object({
    sessionId: Type19.String(),
    proctoringOptions: schema_validators_default17.verityOptions.schema
  })
);
var recordingBlobInfo = new SchemaValidator(
  Type19.Object({
    proctoringSessionId: Type19.String(),
    recordingType: Type19.Union([Type19.Literal("display"), Type19.Literal("webcam")]),
    recordingNumber: Type19.Number(),
    blobNumber: Type19.Number()
  })
);
var uploadS3Url = new SchemaValidator(
  Type19.Object({
    url: Type19.String()
  })
);
var lmsContext = new SchemaValidator(
  Type19.Object({
    institute: Type19.Object({
      id: Type19.Number(),
      origin: Type19.String(),
      accessControlUuid: Type19.String()
    }),
    course: Type19.Object({
      id: Type19.Number(),
      courseId: Type19.String(),
      accessControlUuid: Type19.String()
    }),
    assessment: Type19.Object({
      id: Type19.Number(),
      quizId: Type19.String()
    })
  })
);
var mediaAvailability = StringLiteralUnion(["unavailable", "processing", "available", "error"]);
var sessionStatus = StringLiteralUnion(["unflagged", "flagged", "incomplete", "pending", "error", "unavailable"]);
var videoAvailabilityList = Type19.Object({
  available: Type19.Array(Type19.Union([Type19.Number(), Type19.String(), Type19.Null()])),
  processing: Type19.Array(Type19.Union([Type19.Number(), Type19.String(), Type19.Null()]))
  // error: Type.Array(Type.Number()),
});
var userInfo = Type19.Object({
  username: OptionalOrNull(Type19.String()),
  full_name: OptionalOrNull(Type19.String()),
  given_name: OptionalOrNull(Type19.String()),
  family_name: OptionalOrNull(Type19.String()),
  email: OptionalOrNull(Type19.String()),
  profile_picture: OptionalOrNull(Type19.String())
});
var signatureType = Type19.Union([Type19.Literal("drawn"), Type19.Literal("photo"), Type19.Null()]);
var testResults = new SchemaValidator(
  Type19.Array(
    Type19.Object({
      id: Type19.Number(),
      studentId: Type19.Number(),
      lmsQuizId: Type19.Number(),
      uuid: Type19.String(),
      screencaptureAvailability: mediaAvailability,
      webcamAvailability: mediaAvailability,
      screencaptureVideos: OptionalOrNull(videoAvailabilityList),
      webcamVideos: OptionalOrNull(videoAvailabilityList),
      idPhotoAvailable: Type19.Boolean(),
      personPhotoAvailable: Type19.Boolean(),
      userInfo: OptionalOrNull(userInfo),
      startTime: OptionalOrNull(Type19.String()),
      endTime: OptionalOrNull(Type19.String()),
      status: OptionalOrNull(sessionStatus),
      lmsQuizName: OptionalOrNull(Type19.String()),
      createdAt: OptionalOrNull(Type19.String()),
      realQuizId: OptionalOrNull(Type19.String()),
      lmsUserId: OptionalOrNull(Type19.String()),
      flagLevel: OptionalOrNull(Type19.Number()),
      hasNotes: Type19.Boolean(),
      hasRoomScanNotes: Type19.Boolean(),
      isRoomScanDeleted: Type19.Boolean(),
      isWebcamAndScreenCaptureDeleted: Type19.Boolean(),
      signatureType
    })
  )
);
var singleEventType = Type19.Object({
  id: Type19.Number(),
  type: Type19.String(),
  name: Type19.String(),
  details: Type19.Record(Type19.String(), Type19.Any()),
  time: Type19.String(),
  proctoringSessionId: Type19.Number(),
  anomalyRef: OptionalOrNull(Type19.Number())
});
var singleEvent = new SchemaValidator(singleEventType);
var proctoringEvent = new SchemaValidator(
  Type19.Object({
    events: Type19.Array(singleEventType),
    anomalies: Type19.Array(
      Type19.Object({
        type: Type19.String(),
        flagLevel: Type19.Number(),
        timeStart: OptionalOrNull(Type19.String()),
        timeEnd: OptionalOrNull(Type19.String()),
        id: Type19.Number(),
        comment: OptionalOrNull(Type19.String()),
        resolved: Type19.Boolean()
      })
    )
  })
);
var sessionNotes = new SchemaValidator(
  Type19.Array(
    Type19.Object({
      id: Type19.Number(),
      content: Type19.String(),
      timestamp: Type19.String()
    })
  )
);
var roomScanNotes = new SchemaValidator(
  Type19.Array(
    Type19.Object({
      id: Type19.Number(),
      content: Type19.String(),
      timestamp: Type19.Number()
    })
  )
);
var count = new SchemaValidator(Type19.Number());
var concatenationRequestId = new SchemaValidator(Type19.Object({ requestId: Type19.String() }));
var concatenationRequest = new SchemaValidator(
  Type19.Object({
    status: Type19.Union([
      Type19.Literal("POSTPROCESS_STARTED"),
      Type19.Literal("POSTPROCESS_COMPLETE"),
      Type19.Literal("POSTPROCESS_FAILED")
    ])
  })
);
var proctoringSession = new SchemaValidator(
  Type19.Object({
    id: Type19.Number(),
    uuid: Type19.String(),
    studentId: Type19.Number(),
    ipAddress: OptionalOrNull(Type19.String())
  })
);
var paginatedEventsWithSessions = new SchemaValidator(
  PaginatedResults(Type19.Intersect([singleEventType, Type19.Object({ proctoringSession: proctoringSession.schema })]))
);
var aiSummary = new SchemaValidator(
  Type19.Object({
    summary: OptionalOrNull(Type19.String()),
    severe: OptionalOrNull(Type19.String()),
    major: OptionalOrNull(Type19.String()),
    minor: OptionalOrNull(Type19.String())
  })
);
var schema_validators_default18 = {
  startProctoring,
  lmsContext,
  testResults,
  proctoringEvent,
  recordingBlobInfo,
  uploadS3Url,
  url: uploadS3Url,
  sessionNotes,
  roomScanNotes,
  count,
  concatenationRequestId,
  concatenationRequest,
  proctoringSession,
  paginatedEventsWithSessions,
  singleEvent,
  aiSummary
};

// src/proctoring-session/state/schema-validators.ts
import { Type as Type20 } from "@sinclair/typebox";
var proctoringSessionState = new SchemaValidator(
  Type20.Object({
    status: Type20.Union([Type20.String(), Type20.Undefined()])
  })
);
var schema_validators_default19 = {
  proctoringSessionState
};

// src/proctoring-session/state/index.ts
var stateClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default19);
  }
  get = async (uuid) => {
    try {
      const response = await super.fetch(new URL(`${this.config.verityOrigin}/api/verity/quiz/state/${uuid}`), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.proctoringSessionState.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  set = async (uuid, status) => {
    try {
      const response = await super.fetch(new URL(`${this.config.verityOrigin}/api/verity/quiz/state/${uuid}`), {
        headers: {
          "Content-Type": "application/json"
        },
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          status
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.proctoringSessionState.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/proctoring-session/index.ts
import {
  withContractBase as withContractBase2,
  coerceToContractAbsoluteBase as coerceToContractAbsoluteBase2,
  GetProctoringSessionFlaggedKeywordsContract
} from "@yujacorp/verity-api-contract";
var ProctoringSessionClient = class extends ApiClient {
  logger;
  stateClient;
  constructor(config) {
    super(config, schema_validators_default18);
    this.logger = new LoggerClient(config);
    this.stateClient = new stateClient(config);
  }
  startProctoring = async (assessmentInfo3, devicesInfo, capabilities) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/quiz/event/${assessmentInfo3.lms ?? "verity"}/${assessmentInfo3.hostname ?? location.hostname}/${assessmentInfo3.courseId ?? ""}/${assessmentInfo3.quizId}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "session",
          name: "start proctoring",
          time: /* @__PURE__ */ new Date(),
          details: { success: true, devicesInfo, capabilities }
        }),
        credentials: "include"
      }
    ),
    this.schemaValidators.startProctoring
  );
  upload = async (recordingBlobInfo2, blob) => {
    const searchParams = new URLSearchParams({
      segment: `${recordingBlobInfo2.blobNumber}`,
      url: "upload"
    });
    const response = await super.fetch(
      `${this.config.verityOrigin}/api/verity/quiz/recording/${recordingBlobInfo2.proctoringSessionId}/${recordingBlobInfo2.recordingType === "display" ? "screencapture" : recordingBlobInfo2.recordingType}/${recordingBlobInfo2.recordingNumber}?${searchParams.toString()}`,
      {
        credentials: "include"
      }
    );
    if (!response.ok) {
      throw new ApiClientError(
        new Error(`Upload to get S3 pre-signed URL: ${response.status} - ${response.statusText}`)
      );
    }
    const uploadUrl = await response.json();
    if (schema_validators_default18.uploadS3Url.validate(uploadUrl)) {
      const s3Response = await super.fetch(uploadUrl.url, {
        method: "PUT",
        body: blob
      });
      if (!s3Response.ok) {
        throw new ApiClientError(new Error(`Upload to S3 failed: ${s3Response.status} - ${s3Response.statusText}`));
      }
    }
  };
  uploadRoomScan = async (proctoringSessionId, blob) => {
    const response = await super.fetch(
      `${this.config.verityOrigin}/api/verity/quiz/recording/${proctoringSessionId}/room_scan.webm`,
      {
        method: "PUT",
        body: blob
      }
    );
    if (!response.ok) {
      throw new ApiClientError(new Error(`Upload to S3 failed: ${response.status} - ${response.statusText}`));
    }
  };
  getRoomScanUrl = async (proctoringSessionId) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/${proctoringSessionId}/room_scan/latest.webm`,
        {
          method: "GET"
        }
      );
      if (!response.ok) {
        throw new ApiClientError(
          new Error(`Failed to retrieve room scan video: ${response.status} - ${response.statusText}`)
        );
      }
      const videoUrl = response.url;
      return videoUrl;
    } catch (error) {
      console.error("Error fetching room scan video: ", error);
      throw error;
    }
  };
  getCachedRoomScanUrl = async (proctoringSessionId) => {
    try {
      const urlResponse = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/${proctoringSessionId}/room_scan/url`
      );
      if (urlResponse.ok) {
        const resObj = await urlResponse.json();
        if (this.schemaValidators.url.validate(resObj)) {
          const videoResponse = await super.fetch(resObj.url, { method: "HEAD" });
          if (videoResponse.ok) {
            return resObj.url;
          }
        }
      }
    } catch (error) {
      console.error("Error fetching room scan video: ", error);
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    return void 0;
  };
  getPhotoUrl = async (proctoringSessionId, type) => {
    const ext = "png";
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/photo/${proctoringSessionId}-${type}.${ext}`
      );
      if (response.ok) {
        const imageBlob = await response.blob();
        return URL.createObjectURL(imageBlob);
      }
    } catch (error) {
      console.error(`Error fetching ${type} photo: `, error);
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    return void 0;
  };
  getPhotoUrlJson = async (proctoringSessionId, type) => {
    const ext = "png";
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/photo/${proctoringSessionId}-${type}.${ext}?format=json`
      );
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      console.error(`Error fetching ${type} photo: `, error);
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    return void 0;
  };
  getLmsContext = async (sessionId) => {
    try {
      const res = await super.fetch(
        `${this.config.verityOrigin}/api/verity/proctoring-session/${sessionId}/lms-context`
      );
      if (res.ok) {
        const lmsContext2 = await res.json();
        if (this.schemaValidators.lmsContext.validate(lmsContext2)) {
          return lmsContext2;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  async isCorrectProctoringSessionId(sessionId, assessmentInfo3) {
    try {
      const lmsContext2 = await this.getLmsContext(sessionId);
      return assessmentInfo3.quizId === lmsContext2.assessment.quizId;
    } catch (err) {
      console.error(err);
    }
    return false;
  }
  async getSessionResultList(lmsQuizId, courseId2) {
    try {
      const res = await super.fetch(
        `${this.config.verityOrigin}/api/verity/test-results/${courseId2}/${lmsQuizId}?session=all`,
        {
          credentials: "include"
        }
      );
      if (res.ok) {
        const events = await res.json();
        if (this.schemaValidators.testResults.validate(events)) {
          return events;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getSessionList(courseId2) {
    try {
      const res = await super.fetch(`${this.config.verityOrigin}/api/verity/test-results/${courseId2}?session=all`, {
        credentials: "include"
      });
      if (res.ok) {
        const events = await res.json();
        if (this.schemaValidators.testResults.validate(events)) {
          return events;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getSessionLoggedEvents({ sessionId, courseId: courseId2 }) {
    try {
      const res = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${courseId2}/${sessionId}`, {
        credentials: "include"
      });
      if (res.ok) {
        const events = await res.json();
        if (this.schemaValidators.proctoringEvent.validate(events)) {
          return events;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getVideoUrl({
    sessionId,
    recordingType,
    courseId: courseId2
  }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/url/${courseId2}/${sessionId}/${recordingType}`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async requestVideoConcatenation(sessionId) {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}/concatenated-recording`, {
        method: "POST",
        credentials: "include"
      }),
      this.schemaValidators.concatenationRequestId
    );
  }
  async getConcatenationRequest(sessionId, requestId) {
    return handleApiErrorWithSchema(
      super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}/concatenation-request/${requestId}`,
        {
          credentials: "include"
        }
      ),
      this.schemaValidators.concatenationRequest
    );
  }
  async deleteRecordingsFromSession(sessionUuid) {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionUuid}`, {
        method: "DELETE"
      });
      if (!response.ok) {
        throw new ApiClientError();
      }
      return true;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async deleteRoomScanFromSession(sessionUuid) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/${sessionUuid}/room_scan.webm`,
        {
          method: "DELETE"
        }
      );
      if (!response.ok) {
        throw new ApiClientError();
      }
      return true;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getSessionNotes({ sessionId, courseId: courseId2 }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/session-notes/${courseId2}/${sessionId}`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.sessionNotes.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async addSessionNote({
    sessionId,
    courseId: courseId2,
    content,
    timestamp
  }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/session-notes/${courseId2}/${sessionId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            content,
            timestamp
          })
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async editSessionNote({
    sessionId,
    courseId: courseId2,
    noteId,
    content
  }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/session-notes/${courseId2}/${sessionId}/${noteId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            content
          })
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async deleteSessionNote({ sessionId, courseId: courseId2, noteId }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/session-notes/${courseId2}/${sessionId}/${noteId}`,
        {
          method: "DELETE",
          credentials: "include"
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getRoomScanNotes({ sessionId, courseId: courseId2 }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId2}/${sessionId}`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.roomScanNotes.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async addRoomScanNote({
    sessionId,
    courseId: courseId2,
    content,
    timestamp
  }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId2}/${sessionId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            content,
            timestamp
          })
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async editRoomScanNote({
    sessionId,
    courseId: courseId2,
    noteId,
    content
  }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId2}/${sessionId}/${noteId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            content
          })
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async deleteRoomScanNote({ sessionId, courseId: courseId2, noteId }) {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/roomscan-notes/${courseId2}/${sessionId}/${noteId}`,
        {
          method: "DELETE",
          credentials: "include"
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async getOwnResultsCount(courseId2, lmsProvidedAssessmentId) {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/test-results/${courseId2}/${lmsProvidedAssessmentId}/count`, {
        credentials: "include"
      }),
      this.schemaValidators.count
    );
  }
  async get(proctoringSessionId) {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/proctoring-session/${proctoringSessionId}`, {
        credentials: "include"
      }),
      this.schemaValidators.proctoringSession
    );
  }
  async createCustomEvent({
    proctoringSessionId,
    message,
    flagLevel,
    time
  }) {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          type: "custom",
          name: "live",
          details: {
            message,
            flagLevel
          },
          time
        })
      }),
      this.schemaValidators.singleEvent
    );
  }
  async updateCustomEvent({
    proctoringSessionId,
    eventId,
    message
  }) {
    return handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}/${eventId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          details: {
            message
          }
        })
      })
    );
  }
  async createCustomEventFlag({
    proctoringSessionId,
    eventId,
    flagLevel
  }) {
    return handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}/${eventId}/anomaly`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ flagLevel })
      })
    );
  }
  async updateEventFlag({
    proctoringSessionId,
    eventId,
    resolved,
    flagLevel,
    comment
  }) {
    return handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event/${proctoringSessionId}/${eventId}/anomaly`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          resolved,
          flagLevel,
          comment
        })
      })
    );
  }
  getFlaggedKeywords = withDefaultErrorHandler(
    async (dynamicInput) => {
      const handlers = withContractBase2({
        contract: GetProctoringSessionFlaggedKeywordsContract,
        base: coerceToContractAbsoluteBase2(`${this.config.verityOrigin}/api/verity/`)
      }).withFetchParams({
        pathParams: dynamicInput.pathParams
      }).withFetcher(async ({ method, url: url2 }) => {
        const res = await this.fetch(url2, {
          method,
          headers: { "Content-Type": "application/json" }
        });
        return res.json();
      });
      return handlers.toParsed();
    }
  );
  getProctoringEvents({
    assessmentIds = [],
    types = []
  }) {
    const searchParams = new URLSearchParams();
    assessmentIds.forEach((id) => searchParams.append("assessmentId", String(id)));
    if (types.length > 0) {
      searchParams.set("type", JSON.stringify(types));
    }
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/event?${searchParams.toString()}`),
      this.schemaValidators.paginatedEventsWithSessions
    );
  }
  getAiSummary = async (proctoringSessionId) => {
    return handleApiErrorWithSchema(
      super.fetch(
        `${this.config.verityOrigin}/api/verity/test-results/proctoring/session/${proctoringSessionId}/summary`,
        {
          method: "GET",
          credentials: "include"
        }
      ),
      this.schemaValidators.aiSummary
    );
  };
  generateAiSummary = async (proctoringSessionId, events) => {
    return handleApiErrorWithSchema(
      super.fetch(
        `${this.config.verityOrigin}/api/verity/test-results/proctoring/session/${proctoringSessionId}/summary`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(events)
        }
      ),
      this.schemaValidators.aiSummary
    );
  };
};

// src/live/schema-validators.ts
import { Type as Type21 } from "@sinclair/typebox";
var liveSession = new SchemaValidator(
  Type21.Object({
    liveSessionId: Type21.String()
  })
);
var proctorGroup = Type21.Object({
  id: Type21.Number(),
  name: Type21.String(),
  ownerInstituteId: Type21.String(),
  createdAt: Type21.String(),
  updatedAt: Type21.String()
});
var liveSessionUser = Type21.Object({
  liveSessionId: Type21.String(),
  userId: Type21.String(),
  role: Type21.Union([Type21.Literal("proctor"), Type21.Literal("student")]),
  createdAt: Type21.String(),
  updatedAt: Type21.String()
});
var liveSessionUserValidator = new SchemaValidator(liveSessionUser);
var liveSessionUserSession = Type21.Object({
  id: Type21.String(),
  userId: Type21.String(),
  liveSessionId: Type21.String(),
  role: Type21.Union([Type21.Literal("proctor"), Type21.Literal("student")]),
  connectionState: Type21.Union([
    Type21.Literal("connecting"),
    Type21.Literal("connected"),
    Type21.Literal("reconnecting"),
    Type21.Literal("failed")
  ])
});
var liveSessionUserSessionValidator = new SchemaValidator(liveSessionUserSession);
var liveSessionInvitation = Type21.Object({
  liveSessionId: Type21.String(),
  proctorGroupId: Type21.Number(),
  state: Type21.Object({
    selectedProctorId: OptionalOrNull(Type21.String()),
    firstJoinedAt: OptionalOrNull(Type21.String()),
    cancelledAt: OptionalOrNull(Type21.String())
  }),
  createdAt: Type21.String(),
  updatedAt: Type21.String()
});
var liveSessionInvitations = new SchemaValidator(Type21.Array(liveSessionInvitation));
var liveSessionNote = Type21.Object({
  id: Type21.String(),
  liveSessionId: Type21.String(),
  student: Type21.String(),
  edits: Type21.Array(
    Type21.Object({
      id: Type21.String(),
      editor: Type21.String(),
      content: Type21.String(),
      timestamp: Type21.Number()
    })
  )
});
var liveSessionNotes = new SchemaValidator(Type21.Array(liveSessionNote));
var assessmentLiveSession = {
  id: Type21.String(),
  instituteId: OptionalOrNull(Type21.String()),
  courseId: OptionalOrNull(Type21.String()),
  assessmentId: OptionalOrNull(Type21.String()),
  name: Type21.String(),
  startTime: Type21.String(),
  proctorGuide: OptionalOrNull(Type21.String()),
  endTime: Type21.String(),
  capacity: Type21.Number(),
  registrationStartTime: OptionalOrNull(Type21.String()),
  registrationEndTime: OptionalOrNull(Type21.String()),
  createdAt: Type21.String(),
  updatedAt: Type21.String()
};
var liveSessionWithUsers = Type21.Object({
  ...assessmentLiveSession,
  users: Type21.Array(liveSessionUser)
});
var baseAssessmentLiveSession = new SchemaValidator(Type21.Object(assessmentLiveSession));
var singleLiveSession = new SchemaValidator(liveSessionWithUsers);
var liveSessionList = new SchemaValidator(PaginatedResults(Type21.Object(assessmentLiveSession)));
var liveSessionListWithUsers = new SchemaValidator(PaginatedResults(liveSessionWithUsers));
var courseLiveProctorInfo = new SchemaValidator(
  Type21.Array(
    Type21.Object({
      id: Type21.Number(),
      uuid: Type21.String(),
      name: Type21.String(),
      email: OptionalOrNull(Type21.String())
    })
  )
);
var instituteProctorGroups = new SchemaValidator(
  PaginatedResults(
    Type21.Intersect([
      proctorGroup,
      Type21.Object({
        allowedInstitutes: Type21.Array(
          Type21.Object({
            instituteId: Type21.String(),
            minHoursNotice: Type21.Number(),
            trackCancellationHoursBefore: Type21.Number(),
            trackCancellationMessage: Type21.String(),
            autoCancelEmptyHoursBefore: OptionalOrNull(Type21.Number())
          })
        )
      })
    ])
  )
);
var registerLiveSessionInstituteUser = new SchemaValidator(liveSessionUser);
var deregisterLiveSessionInstituteUser = new SchemaValidator(liveSessionUser);
var registerLiveSessionProctorGroup = new SchemaValidator(OptionalOrNull(liveSessionInvitation));
var deregisterLiveSessionProctorGroup = new SchemaValidator(liveSessionInvitation);
var liveSessionInvitationValidator = new SchemaValidator(liveSessionInvitation);
var liveSessionLiveState = new SchemaValidator(
  Type21.Object({
    id: Type21.String(),
    userSessionIds: Type21.Array(Type21.String())
  })
);
var liveProctorGroup = new SchemaValidator(proctorGroup);
var liveProctorGroups = new SchemaValidator(Type21.Array(proctorGroup));
var schema_validators_default20 = {
  liveSession,
  singleLiveSession,
  liveSessionUser: liveSessionUserValidator,
  liveSessionUserSession: liveSessionUserSessionValidator,
  liveSessionInvitation: liveSessionInvitationValidator,
  liveSessionList,
  liveSessionListWithUsers,
  liveSessionInvitations,
  courseLiveProctorInfo,
  instituteProctorGroups,
  liveSessionNotes,
  baseAssessmentLiveSession,
  registerLiveSessionInstituteUser,
  deregisterLiveSessionInstituteUser,
  registerLiveSessionProctorGroup,
  deregisterLiveSessionProctorGroup,
  liveSessionLiveState,
  liveProctorGroup,
  liveProctorGroups
};

// src/live/index.ts
var emptyPaginatedResults = {
  currentPage: 0,
  totalElements: 0,
  totalPages: 1,
  results: []
};
var LiveClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default20);
  }
  getLiveSessionsWithUsers = ({
    instituteUuid,
    courseUuid,
    assessmentUuid,
    sortBy,
    order,
    size,
    page = 0
  }) => {
    const searchParams = new URLSearchParams();
    if (instituteUuid) {
      searchParams.set("instituteId", instituteUuid);
    }
    if (courseUuid) {
      searchParams.set("courseId", courseUuid);
    }
    if (assessmentUuid) {
      searchParams.set("assessmentId", assessmentUuid);
    }
    if (sortBy) {
      searchParams.set("sortBy", sortBy);
    }
    if (order) {
      searchParams.set("order", order);
    }
    if (size != null) {
      searchParams.set("size", String(size));
    }
    if (page != null) {
      searchParams.set("page", String(page));
    }
    searchParams.set("includeUsers", "true");
    return handleApiErrorWithSchema(
      super.fetch(new URL(`/api/live/liveSession?${searchParams}`, `${this.config.verityOrigin}`)),
      this.schemaValidators.liveSessionListWithUsers
    );
  };
  getCourseLiveSessionInfo = async (instituteUuid, courseUuid) => {
    try {
      const resp = await super.fetch(
        new URL(
          `/api/live/liveSession?instituteId=${instituteUuid}&courseId=${courseUuid}`,
          `${this.config.verityOrigin}`
        )
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.liveSessionList.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    console.error("Failed to retrieve course live session info");
    return emptyPaginatedResults;
  };
  getLiveSession = async (liveSessionId) => {
    try {
      const resp = await super.fetch(new URL(`/api/live/liveSession/${liveSessionId}`, `${this.config.verityOrigin}`));
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.singleLiveSession.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getLiveSessionLiveState = async (liveSessionId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/liveState`, `${this.config.verityOrigin}`)
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.liveSessionLiveState.validate(data)) {
          return data;
        }
      } else {
        return void 0;
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getLiveSessionUser = async (liveSessionId, userId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/user/${userId}`, `${this.config.verityOrigin}`)
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.liveSessionUser.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getLiveSessionInvitations = async (liveSessionId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/invitation`, `${this.config.verityOrigin}`),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.liveSessionInvitations.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getCourseLiveProctorInfo = async (lmsProvidedCourseId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/user/instructor-roster/${lmsProvidedCourseId}`, `${this.config.verityOrigin}`),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.courseLiveProctorInfo.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    console.error("Failed to retrieve course live proctor info");
    return [];
  };
  getLiveProctorGroup = async (proctorGroupId) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/live/proctorGroup/${proctorGroupId}`, `${this.config.verityOrigin}`), {
      credentials: "include"
    }),
    this.schemaValidators.liveProctorGroup
  );
  getUserProctorGroups = async (userId) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/live/user/${userId}/proctorGroup`, `${this.config.verityOrigin}`), {
      credentials: "include"
    }),
    this.schemaValidators.liveProctorGroups
  );
  getLiveInstituteProctorGroup = async (instituteUuid) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/live/proctorGroup?instituteId=${instituteUuid}`, `${this.config.verityOrigin}`),
        {
          credentials: "include"
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.instituteProctorGroups.validate(data)) {
          return {
            ...data,
            results: data.results.filter(
              ({ allowedInstitutes }) => allowedInstitutes.some(({ instituteId: instituteId2 }) => instituteId2 === instituteUuid)
            ).map((proctorGroup2) => {
              const {
                minHoursNotice,
                trackCancellationHoursBefore,
                trackCancellationMessage,
                autoCancelEmptyHoursBefore
              } = proctorGroup2.allowedInstitutes.find(({ instituteId: instituteId2 }) => instituteId2 === instituteUuid);
              return {
                ...proctorGroup2,
                minHoursNotice,
                trackCancellationHoursBefore,
                trackCancellationMessage,
                autoCancelEmptyHoursBefore
              };
            })
          };
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    console.error("Failed to retrieve institute proctor groups");
    return emptyPaginatedResults;
  };
  getAssessmentLiveSessionInfo = async ({ instituteUuid, courseUuid, assessmentUuid }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/live/liveSession?instituteId=${instituteUuid}&courseId=${courseUuid}&assessmentId=${assessmentUuid}&isPopIn=any`
      );
      const liveSessionInfo = await response.json();
      if (this.schemaValidators.liveSessionList.validate(liveSessionInfo)) {
        return liveSessionInfo;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getLiveSessionNotes = async (liveSessionId) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/live/liveSession/${liveSessionId}/notes`);
      const liveSessionNotes2 = await response.json();
      if (this.schemaValidators.liveSessionNotes.validate(liveSessionNotes2)) {
        return liveSessionNotes2;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getUserCourseLiveSessions = async (userUuid, courseUuid) => {
    try {
      const liveSessions = await (await super.fetch(`${this.config.verityOrigin}/api/live/user/${userUuid}/liveSession?courseId=${courseUuid}`)).json();
      if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
        return liveSessions.results;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getUserAssessmentLiveSessions = async (assessmentUuid) => {
    try {
      const liveSessions = await (await super.fetch(
        `${this.config.verityOrigin}/api/live/user/${(await this.getJwt())?.uid}/liveSession?assessmentId=${assessmentUuid}&sortBy=endTime`
      )).json();
      if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
        return liveSessions.results;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  doesLiveSessionExistForAssessment = async (assessmentUuidInfo) => {
    const result = await this.getAssessmentLiveSessionInfo(assessmentUuidInfo);
    return result.totalElements > 0;
  };
  getInstituteLiveSessions = async (instituteUuid) => {
    try {
      const liveSessions = await (await super.fetch(`${this.config.verityOrigin}/api/live/liveSession?instituteId=${instituteUuid}`)).json();
      if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
        return liveSessions;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseLiveSessions = async (instituteUuid, courseUuid) => {
    try {
      const liveSessions = await (await super.fetch(
        `${this.config.verityOrigin}/api/live/liveSession?instituteId=${instituteUuid}&courseId=${courseUuid}`
      )).json();
      if (this.schemaValidators.liveSessionList.validate(liveSessions)) {
        return liveSessions;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getStudentInstituteId = async () => {
    try {
      const instituteId2 = await (await super.fetch(`${this.config.verityOrigin}/api/verity/user/institute-id`)).json();
      if (typeof instituteId2 === "number") {
        return instituteId2;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  addLiveSession = async ({ instituteUuid, courseUuid, assessmentUuid }, payload) => {
    try {
      const resp = await super.fetch(new URL(`/api/live/liveSession`, `${this.config.verityOrigin}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...payload,
          users: payload.users ?? [],
          instituteId: instituteUuid,
          courseId: courseUuid,
          assessmentId: assessmentUuid
        }),
        credentials: "include"
      });
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.singleLiveSession.validate(data)) {
          return {
            success: resp.ok,
            status: resp.status,
            error: void 0,
            updatedSession: data
          };
        }
      } else {
        const errorMessage = await resp.text();
        return {
          success: resp.ok,
          status: resp.status,
          error: errorMessage.includes("html") ? void 0 : JSON.parse(errorMessage),
          updatedSession: void 0
        };
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      success: false,
      error: void 0,
      updatedSession: void 0
    };
  };
  deleteLiveSession = async ({ liveSessionId }) => {
    try {
      const resp = await super.fetch(new URL(`/api/live/liveSession/${liveSessionId}`, `${this.config.verityOrigin}`), {
        method: "DELETE",
        credentials: "include"
      });
      if (resp.ok) {
        return {
          success: resp.ok,
          status: resp.status,
          error: void 0
        };
      } else {
        const errorMessage = await resp.text();
        return {
          success: resp.ok,
          status: resp.status,
          error: errorMessage.includes("html") ? void 0 : JSON.parse(errorMessage),
          updatedSession: void 0
        };
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      success: false,
      error: void 0
    };
  };
  modifyLiveSession = async ({
    liveSessionId,
    payload
  }) => {
    try {
      const resp = await super.fetch(new URL(`/api/live/liveSession/${liveSessionId}`, `${this.config.verityOrigin}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.singleLiveSession.validate(data)) {
          return {
            success: resp.ok,
            status: resp.status,
            error: void 0,
            updatedSession: data
          };
        }
      } else {
        const errorMessage = await resp.text();
        return {
          success: resp.ok,
          status: resp.status,
          error: errorMessage.includes("<!DOCTYPE html>") ? void 0 : JSON.parse(errorMessage),
          updatedSession: void 0
        };
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      success: false,
      error: void 0,
      updatedSession: void 0
    };
  };
  saveLiveSessionInstituteUser = async ({
    liveSessionId,
    action,
    role,
    userId
  }) => {
    try {
      let resp;
      if (action === "register") {
        resp = await super.fetch(
          new URL(`/api/live/liveSession/${liveSessionId}/user`, `${this.config.verityOrigin}`),
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId, role }),
            credentials: "include"
          }
        );
      } else {
        resp = await super.fetch(
          new URL(`/api/live/liveSession/${liveSessionId}/user/${userId}`, `${this.config.verityOrigin}`),
          {
            method: "DELETE",
            credentials: "include"
          }
        );
      }
      if (resp.ok) {
        if (action === "register") {
          const data = await resp.json();
          if (this.schemaValidators.registerLiveSessionInstituteUser.validate(data)) {
            return {
              success: resp.ok,
              error: void 0,
              updatedSession: data
            };
          }
        } else if (action === "deregister") {
          return {
            success: resp.ok,
            error: void 0,
            updatedSession: void 0
          };
        }
      } else {
        const errorMessage = await resp.text();
        return {
          success: resp.ok,
          error: errorMessage.includes("<!DOCTYPE html>") ? void 0 : JSON.parse(errorMessage),
          updatedSession: void 0
        };
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      success: false,
      error: void 0,
      updatedSession: void 0
    };
  };
  addLiveSessionUser = ({
    liveSessionId,
    role,
    userId
  }) => {
    return handleApiErrorWithSchema(
      super.fetch(new URL(`/api/live/liveSession/${liveSessionId}/user`, `${this.config.verityOrigin}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, role }),
        credentials: "include"
      }),
      this.schemaValidators.registerLiveSessionInstituteUser
    );
  };
  removeLiveSessionUser = async (liveSessionId, userId) => {
    await handleApiError(
      super.fetch(new URL(`/api/live/liveSession/${liveSessionId}/user/${userId}`, `${this.config.verityOrigin}`), {
        method: "DELETE",
        credentials: "include"
      })
    );
  };
  registerSelfAsProctor = async (liveSessionId) => {
    const userId = (await this.getJwt())?.uid;
    if (!userId) {
      throw new UnauthenticatedError();
    }
    try {
      await this.addLiveSessionUser({
        liveSessionId,
        userId,
        role: "proctor"
      });
    } catch (e) {
      if (!(e instanceof ApiClientResponseError) || e.code !== "userExists") {
        throw e;
      }
    }
  };
  saveLiveSessionProctorGroup = async ({
    liveSessionId,
    action,
    proctorGroupId
  }) => {
    try {
      let resp;
      if (action === "registerGroup") {
        resp = await super.fetch(
          new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
          {
            method: "POST",
            credentials: "include"
          }
        );
      } else {
        resp = await super.fetch(
          new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
          {
            method: "DELETE",
            credentials: "include"
          }
        );
      }
      if (resp.ok) {
        if (action === "registerGroup") {
          const data = await resp.json();
          if (this.schemaValidators.registerLiveSessionProctorGroup.validate(data)) {
            return {
              success: resp.ok,
              error: void 0,
              updatedSession: data
            };
          }
        } else if (action === "deregisterGroup") {
          return {
            success: resp.ok,
            error: void 0
          };
        }
      } else {
        const errorMessage = await resp.text();
        return {
          success: resp.ok,
          error: errorMessage.includes("<!DOCTYPE html>") ? void 0 : JSON.parse(errorMessage),
          updatedSession: void 0
        };
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      success: false,
      error: void 0,
      updatedSession: void 0
    };
  };
  createProctorGroupInvitation = (liveSessionId, proctorGroupId) => {
    return handleApiErrorWithSchema(
      super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
        {
          method: "POST",
          credentials: "include"
        }
      ),
      this.schemaValidators.liveSessionInvitation
    );
  };
  deleteProctorGroupInvitation = async (liveSessionId, proctorGroupId) => {
    await handleApiError(
      super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/invitation/${proctorGroupId}`, `${this.config.verityOrigin}`),
        {
          method: "DELETE",
          credentials: "include"
        }
      )
    );
  };
  markLiveSessionViewed = async (liveSessionId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/view`, `${this.config.verityOrigin}`),
        {
          method: "POST",
          credentials: "include"
        }
      );
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  endLiveSession = async (liveSessionId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/live/liveSession/${liveSessionId}/end`, `${this.config.verityOrigin}`),
        {
          method: "POST",
          credentials: "include"
        }
      );
      if (resp.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/platform-info/schema-validators.ts
import { Type as Type22 } from "@sinclair/typebox";
var platformInfo = new SchemaValidator(
  Type22.Object({
    platform: Type22.Union([
      Type22.Literal("none"),
      Type22.Literal("lockdown"),
      Type22.Literal("extension"),
      Type22.Literal("mobile")
    ]),
    version: OptionalOrNull(Type22.String()),
    versionSupported: Type22.Boolean()
  })
);
var versionSupported = new SchemaValidator(
  Type22.Object({
    versionSupported: Type22.Boolean()
  })
);
var schema_validators_default21 = {
  platformInfo,
  versionSupported
};

// src/platform-info/index.ts
var PlatformInfoClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default21);
  }
  current = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/platform-info`);
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.platformInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  isVersionSupported = async (version) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/platform-info/version/${version}`);
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.versionSupported.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/permission/schema-validators.ts
import { Type as Type23 } from "@sinclair/typebox";
var verityResultPermission = new SchemaValidator(
  Type23.Object({
    verified: Type23.Boolean()
  })
);
var portalPermission = new SchemaValidator(
  Type23.Array(
    Type23.Union([
      Type23.Literal("InstituteEdit"),
      Type23.Literal("CourseEdit"),
      Type23.Literal("VeritySettingsEdit"),
      Type23.Literal("CourseView"),
      Type23.Literal("LiveProctoringPrivilege"),
      Type23.Literal("LiveProctoring")
    ])
  )
);
var assessmentPermissions = new SchemaValidator(
  Type23.Object({
    assessment: Type23.Array(Type23.Union([Type23.Literal("create"), Type23.Literal("update"), Type23.Literal("read")]))
  })
);
var scopedPrivileges = new SchemaValidator(
  Type23.Record(
    Type23.String(),
    Type23.Array(
      Type23.Object({
        courseId: OptionalOrNull(Type23.String()),
        instituteId: OptionalOrNull(Type23.String()),
        resourceId: OptionalOrNull(Type23.Unknown())
      })
    )
  )
);
var schema_validators_default22 = {
  verityResultPermission,
  portalPermission,
  assessmentPermissions,
  scopedPrivileges
};

// src/permission/index.ts
var PermissionClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default22);
  }
  doesUserHaveTestResultPermission = async (quizInfo) => {
    try {
      const resp = await super.fetch(
        new URL(
          `/api/verity/test-results/${quizInfo.lms}/${quizInfo.courseId ?? ""}/${quizInfo.quizId}?verify=permissions`,
          `${this.config.verityOrigin}`
        ),
        { credentials: "include" }
      );
      if (resp.status === 403) return false;
      const data = await resp.json();
      if (this.schemaValidators.verityResultPermission.validate(data)) {
        return data.verified;
      }
      return false;
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
      return false;
    }
  };
  // Same as above but only needs lmsCourseId and instituteId
  doesUserHaveInstructorPermission = async (lmsCourseId, instituteId2) => handleApiErrorWithSchema(
    super.fetch(
      new URL(
        `${this.config.verityOrigin}/api/verity/test-results/${lmsCourseId}/instructor?instituteId=${instituteId2}`
      ),
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.verityResultPermission
  );
  portalPermissions = async (lmsCourseId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/portal/permissions?lmsCourseId=${lmsCourseId}`, `${this.config.verityOrigin}`),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.portalPermission.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return [];
  };
  // TODO: get permission from access-control token
  fromHostname = async (info) => {
    try {
      const res = await super.fetch(
        new URL(`/api/verity/permissions/hostname/${info.hostname}`, `${this.config.verityOrigin}`)
      );
      if (res.ok) {
        const data = await res.json();
        if (this.schemaValidators.assessmentPermissions.validate(data)) {
          return data;
        }
      } else if (res.status === 401) {
        throw new UnauthenticatedError();
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return {
      assessment: []
    };
  };
  doesUserHaveInstituteProfilePermissions = async () => {
    try {
      const res = await super.fetch(
        new URL(`/api/verity/permissions/institute-profiles`, `${this.config.verityOrigin}`)
      );
      if (res.ok) {
        return true;
      } else if (res.status === 401) {
        throw new UnauthenticatedError();
      }
      return false;
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    return false;
  };
  getUserPrivileges = () => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/access-control/user/self/privilege`, `${this.config.verityOrigin}`)),
    this.schemaValidators.scopedPrivileges
  );
};

// src/lms-info/schema-validators.ts
import { Type as Type24 } from "@sinclair/typebox";
var lms = new SchemaValidator(
  Type24.Union([
    Type24.Literal("canvas"),
    Type24.Literal("moodle"),
    Type24.Literal("BlackboardLearn"),
    Type24.Literal("desire2learn")
  ])
);
var assessmentInfo = new SchemaValidator(
  Type24.Object({
    lms: Type24.Optional(lms.schema),
    hostname: Type24.Optional(Type24.String()),
    courseId: Type24.Optional(Type24.String()),
    quizId: Type24.String(),
    quizInfo: Type24.Optional(Type24.Record(Type24.String(), Type24.Any())),
    quizOther: Type24.Optional(Type24.Record(Type24.String(), Type24.Any()))
  })
);
var deleteAssessmentInfo = new SchemaValidator(
  Type24.Object({
    id: Type24.Number(),
    courseId: Type24.Number(),
    quizId: Type24.String(),
    quizInfo: Type24.Optional(Type24.Record(Type24.String(), Type24.Any())),
    other: Type24.Optional(Type24.Record(Type24.String(), Type24.Any())),
    isDeleted: Type24.Boolean(),
    createdAt: Type24.String(),
    updatedAt: Type24.String()
  })
);
var assessmentInfoAlternate = new SchemaValidator(
  Type24.Object({
    lms: lms.schema,
    hostname: Type24.String(),
    lmsProvidedCourseId: Type24.Optional(Type24.String()),
    lmsProvidedAssessmentId: Type24.String()
  })
);
var url = new SchemaValidator(Type24.String());
var instituteLmsInfo = new SchemaValidator(
  Type24.Object({
    id: Type24.Number(),
    instituteName: Type24.Union([Type24.Null(), Type24.String()]),
    origin: Type24.Optional(Type24.Union([Type24.Null(), Type24.String()])),
    lmsApiClientId: Type24.Optional(Type24.Union([Type24.Null(), Type24.String()]))
  })
);
var tokenExists = new SchemaValidator(
  Type24.Object({
    exists: Type24.Boolean()
  })
);
var tokenTestResults = new SchemaValidator(
  Type24.Array(
    Type24.Object({
      key: Type24.String(),
      relativePath: Type24.String(),
      requestType: Type24.String(),
      status: Type24.String()
    })
  )
);
var deleteResponse = new SchemaValidator(
  Type24.Object({
    success: Type24.Boolean(),
    lmsResponse: Type24.Optional(Type24.Unknown())
  })
);
var apiIntegrationInfo = new SchemaValidator(
  Type24.Object({
    course: OptionalOrNull(
      Type24.Object({
        edit: Type24.Boolean()
      })
    ),
    institute: OptionalOrNull(
      Type24.Object({
        edit: Type24.Boolean()
      })
    )
  })
);
var schema_validators_default23 = {
  lms,
  assessmentInfo,
  url,
  instituteLmsInfo,
  tokenExists,
  tokenTestResults,
  deleteResponse,
  assessmentInfoAlternate,
  deleteAssessmentInfo,
  apiIntegrationInfo
};

// src/lms-info/index.ts
var LmsInfoClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default23);
  }
  getAssessmentUrl = async (courseId2, quizId, type) => {
    return handleApiErrorWithSchema(
      super.fetch(
        `${this.config.verityOrigin}/api/verity/lms-course/${courseId2}/assessment/${quizId}/link?type=${type}`,
        { credentials: "include" }
      ),
      this.schemaValidators.url
    );
  };
  getCourseUrl = async (courseId2, type) => {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/lms-course/${courseId2}/link?type=${type}`, {
        credentials: "include"
      }),
      this.schemaValidators.url
    );
  };
  getInstituteLmsInfo = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms`, { credentials: "include" });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteLmsInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  createMoodleToken = async (accessToken2) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms/moodle-token`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessToken: accessToken2 })
      });
      if (response.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getTokenExists = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms/token`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.tokenExists.validate(obj)) {
          return obj.exists;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  deleteToken = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/lms/token`, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getExactLmsVersion = async (instituteId2) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms/version?instituteId=${instituteId2}`,
        {
          method: "GET",
          credentials: "include"
        }
      );
      if (response.ok) {
        return await response.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getLTIRef = async (instituteId2) => {
    try {
      const resp = await super.fetch(new URL(`/api/verity/ltiRef/${instituteId2}`, `${this.config.verityOrigin}`), {
        credentials: "include"
      });
      if (resp.ok) {
        const data = await resp.text();
        if (typeof data === "string") {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
  };
  getLTIRefByHostname = async (hostname) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/ltiRef/hostname/${hostname}`, `${this.config.verityOrigin}`),
        {
          credentials: "include"
        }
      );
      if (resp.ok) {
        const data = await resp.text();
        if (typeof data === "string") {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
  };
  getSessionToken = async (lmsCourseId) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms/session-token?lmsCourseId=${lmsCourseId}`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const token = (await response.json()).sessionToken;
        return token;
      }
    } catch (error) {
      console.error(error);
    }
  };
  testToken = async ({
    lms: lms2,
    category,
    lmsProvidedCourseId,
    accessToken: accessToken2
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/scope/${category}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lms: lms2,
          lmsProvidedCourseId,
          accessToken: accessToken2
        }),
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.tokenTestResults.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  createAssessment = async (lms2, lmsCourseId) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms/${lms2}/lms-course/${lmsCourseId}/lms-assessment`,
        {
          method: "POST",
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        return obj;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateAssessment = async (lms2, lmsCourseId, lmsAssessmentId, accessCode2) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms-course/${lmsCourseId}/lms-assessment/${lmsAssessmentId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lms: lms2, accessCode: accessCode2 }),
          credentials: "include"
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  addQuestionToAssessment = async (lms2, lmsCourseId, lmsAssessmentId, origin, question) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms-course/${lmsCourseId}/lms-assessment/${lmsAssessmentId}/question`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lms: lms2, origin, question }),
          credentials: "include"
        }
      );
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  deleteAssessment = async (assessmentId, deleteFromLms) => {
    try {
      const url2 = new URL(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}`);
      if (deleteFromLms) {
        url2.searchParams.set("deleteFromLms", "true");
      }
      const response = await super.fetch(url2, {
        method: "DELETE",
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.deleteResponse.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAllAssessmentInfoById = async (assessmentId) => {
    try {
      const resp = await super.fetch(new URL(`/api/verity/assessment/${assessmentId}`, `${this.config.verityOrigin}`), {
        credentials: "include"
      });
      if (resp.ok) {
        const obj = await resp.json();
        if (this.schemaValidators.deleteAssessmentInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAssessmentInfoById = async (assessmentId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/assessment/${assessmentId}/info`, `${this.config.verityOrigin}`),
        {
          credentials: "include"
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.assessmentInfoAlternate.validate(data)) {
          return {
            ...data,
            courseId: data.lmsProvidedCourseId,
            quizId: data.lmsProvidedAssessmentId
          };
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getApiIntegrationInfo = async ({ courseId: courseId2, instituteId: instituteId2 }) => handleApiErrorWithSchema(
    super.fetch(
      new URL(
        `/api/verity/lms/api-integration?instituteId=${instituteId2}${courseId2 !== void 0 ? `&courseId=${courseId2}` : ""}`,
        `${this.config.verityOrigin}`
      ),
      { credentials: "include" }
    ),
    this.schemaValidators.apiIntegrationInfo
  );
};

// src/download/schema-validators.ts
import { Type as Type25 } from "@sinclair/typebox";
var lockdownDownloadPath = new SchemaValidator(
  Type25.Object({
    path: Type25.String(),
    files: Type25.Array(
      Type25.Object({
        url: Type25.String(),
        size: Type25.Number()
      })
    )
  })
);
var requestProctoringSessionDownloadResponse = new SchemaValidator(
  Type25.Object({
    ready: Type25.Boolean(),
    emailDestination: Type25.String()
  })
);
var schema_validators_default24 = {
  lockdownDownloadPath,
  requestProctoringSessionDownloadResponse
};

// src/download/index.ts
import { parse } from "yaml";
var DownloadClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default24);
  }
  getLockdownDownloadUrl = async (os, type) => {
    try {
      const response = await fetch(
        `${this.config.lockdown.origin}/${this.config.lockdown.channel}${os.startsWith("mac") ? "-mac" : ""}.yml`
      );
      if (response.ok) {
        const text = await response.text();
        if (typeof text === "string") {
          const appMeta = parse(text);
          if (this.schemaValidators.lockdownDownloadPath.validate(appMeta)) {
            let file;
            if (os === "mac-arm") {
              file = appMeta.files.find(({ url: url2 }) => url2.endsWith("arm64.dmg"));
            } else if (os === "mac") {
              file = appMeta.files.find(({ url: url2 }) => !url2.endsWith("arm64.dmg") && url2.endsWith(".dmg"));
            } else {
              file = appMeta.files.find(({ url: url2 }) => url2.endsWith(`.${type ?? "exe"}`));
            }
            return {
              url: `${this.config.lockdown.cdnBaseUrl}/${encodeURIComponent(file?.url ?? appMeta.path)}`,
              size: file?.size
            };
          }
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  requestProctoringSessionRecordingDownload = async (sessionId, userEnteredEmail, userEnteredName) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/download-request/${sessionId}/${userEnteredEmail}/${userEnteredName}`,
        {
          method: "POST",
          credentials: "include"
        }
      );
      if (response.ok) {
        const details = await response.json();
        if (this.schemaValidators.requestProctoringSessionDownloadResponse.validate(details)) {
          return details;
        }
        throw new Error(`Invalid shape of response: ${JSON.stringify(details)}`);
      }
      throw new Error(`Invalid response, ${response.status}`);
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/verity-options/index.ts
var VerityOptionsClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default17);
  }
  get = async ({
    isManagedGuestSession = false,
    ...quizInfo
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/options/${quizInfo?.lms ?? "verity"}/${quizInfo.hostname ?? location.hostname}/${quizInfo.courseId ?? ""}/${quizInfo.quizId}?isManagedGuestSession=${isManagedGuestSession}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.verityOptions.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  save = async ({
    quizInfo,
    courseInfo: courseInfo2,
    options,
    ignore,
    standaloneOptions
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/options/${quizInfo?.lms ?? "verity"}/${quizInfo.hostname ?? location.hostname}/${quizInfo.courseId ?? ""}/${quizInfo.quizId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            quizInfo,
            courseInfo: courseInfo2,
            verityOptions: options,
            ignore,
            standaloneOptions
          }),
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAllApplications = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/applications/all`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.applicationResponse.validate(obj)) {
          const APPLICATION_LIST = obj.map((app) => ({
            id: app.id,
            name: app.name,
            processName: app.processName,
            iconSvg: app.iconSvg,
            type: app.type
          }));
          return APPLICATION_LIST;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAllExtensions = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/extensions/all`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.extensionResponse.validate(obj)) {
          const EXTENSION_LIST = obj.map((ext) => ({
            id: ext.extensionId,
            name: ext.extensionName,
            iconSvg: ext.iconSvg,
            type: ext.type
          }));
          return EXTENSION_LIST;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  fetchChromeExtensionData = async (id) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/extensions/webstore/${id}`, {
        credentials: "include"
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/institute/schema-validators.ts
import { Type as Type27 } from "@sinclair/typebox";

// src/proctoring-profile/schema-validators.ts
import { Type as Type26 } from "@sinclair/typebox";
var recordingModeType2 = Type26.Union([Type26.Literal("off"), Type26.Literal("normal"), Type26.Literal("strict")]);
var proctoringOptionProfileType = Type26.Union([
  Type26.Literal("institute"),
  Type26.Literal("course"),
  Type26.Literal("user"),
  Type26.Literal("assessment")
]);
function OptionalOrNull2(type) {
  return Type26.Optional(Type26.Union([Type26.Null(), type]));
}
var proctoringOptionType = Type26.Object({
  enforceLockdown: Type26.Boolean(),
  enforceMobile: Type26.Boolean(),
  isExtension: Type26.Boolean(),
  isLockdown: Type26.Boolean(),
  isMobile: Type26.Boolean(),
  isChromeOs: Type26.Boolean(),
  videoRecordingMode: recordingModeType2,
  audioRecordingMode: recordingModeType2,
  recordScreen: Type26.Boolean(),
  recordWebTraffic: Type26.Boolean(),
  detectSidePanel: Type26.Boolean(),
  excludedWebTrafficRecordingUrls: OptionalOrNull2(Type26.Array(Type26.String())),
  recordClipboard: Type26.Boolean(),
  recordInput: Type26.Boolean(),
  oneScreenEnforcementMode: recordingModeType2,
  disableNewTab: Type26.Boolean(),
  closeOpenTabs: Type26.Boolean(),
  tabWhiteList: OptionalOrNull2(Type26.Array(Type26.String())),
  tabBlockList: OptionalOrNull2(Type26.Array(Type26.String())),
  keyboardShortcutBlockList: OptionalOrNull2(Type26.Array(Type26.String())),
  disableClipboardMode: Type26.Union([Type26.Literal("off"), Type26.Literal("tab"), Type26.Literal("system")]),
  enableGuidedAccessMode: Type26.Boolean(),
  restrictHandoffMode: recordingModeType2,
  disableSpellcheck: Type26.Boolean(),
  disablePrinting: Type26.Boolean(),
  disableRightClick: Type26.Boolean(),
  extensionBlockMode: recordingModeType2,
  allowedExtensionsList: OptionalOrNull2(Type26.Array(Type26.String())),
  disableDownloadFiles: Type26.Boolean(),
  hideOtherWindows: Type26.Boolean(),
  showRecordingPanel: Type26.Boolean(),
  idVerification: Type26.Boolean(),
  idVerificationEnableAi: Type26.Boolean(),
  faceTrackingMode: recordingModeType2,
  objectDetectionMode: recordingModeType2,
  applicationBlockMode: recordingModeType2,
  blockWindowedApplications: Type26.Boolean(),
  applicationBlockList: OptionalOrNull2(Type26.Array(Type26.String())),
  allowedApplicationsList: OptionalOrNull2(Type26.Array(Type26.String())),
  blockVirtualMachines: Type26.Boolean(),
  recordApplicationsActivity: Type26.Boolean(),
  scanRoom: Type26.Boolean(),
  quietEnvironment: Type26.Boolean(),
  enableCalculator: Type26.Union([Type26.Literal("off"), Type26.Literal("scientific"), Type26.Literal("standard")]),
  enableWhiteboard: Type26.Boolean(),
  uploadScratchNotes: Type26.Boolean(),
  idVerificationMode: recordingModeType2,
  restrictDevices: Type26.Boolean(),
  audioTranscription: Type26.Boolean(),
  flaggedKeywords: Type26.Union([
    Type26.Null(),
    Type26.Array(
      Type26.Object({
        keyword: Type26.String(),
        level: Type26.Optional(Type26.Number())
      })
    )
  ]),
  uploadSignature: Type26.Boolean(),
  enableGeofence: Type26.Boolean(),
  geofenceLocations: OptionalOrNull2(
    Type26.Array(
      Type26.Object({
        longitude: Type26.Number(),
        latitude: Type26.Number(),
        radius: Type26.Number(),
        name: Type26.String(),
        isMetric: Type26.Boolean()
      })
    )
  ),
  disableVirtualCamera: Type26.Boolean()
});
var proctoringOptionProfile = Type26.Object({
  id: Type26.Number(),
  name: Type26.Union([Type26.Null(), Type26.String()]),
  type: proctoringOptionProfileType,
  proctoringOption: Type26.Object({
    ...proctoringOptionType.properties
  }),
  instituteProfile: OptionalOrNull2(
    Type26.Object({
      id: Type26.Number(),
      enforced: Type26.Boolean(),
      instituteId: Type26.Number(),
      proctoringOptionProfileId: Type26.Number()
    })
  ),
  courseProfile: OptionalOrNull2(
    Type26.Object({
      id: Type26.Number(),
      courseId: Type26.Number(),
      proctoringOptionProfileId: Type26.Number()
    })
  ),
  userProfile: OptionalOrNull2(
    Type26.Object({
      id: Type26.Number(),
      userId: Type26.Number(),
      proctoringOptionProfileId: Type26.Number()
    })
  )
});
var profiles = new SchemaValidator(Type26.Array(proctoringOptionProfile));
var enforced = new SchemaValidator(Type26.Object({ enforced: Type26.Boolean() }));
var schema_validators_default25 = {
  profile: new SchemaValidator(proctoringOptionProfile),
  profileOrNull: new SchemaValidator(Type26.Union([Type26.Null(), proctoringOptionProfile])),
  profiles,
  enforced,
  proctoringOption: new SchemaValidator(proctoringOptionType)
};

// src/institute/schema-validators.ts
var accessControlInstituteId = new SchemaValidator(Type27.String());
var instituteInfo = new SchemaValidator(
  Type27.Object({
    id: Type27.Integer(),
    uuid: Type27.String(),
    name: Type27.String(),
    origin: OptionalOrNull(Type27.String()),
    lms: OptionalOrNull(Type27.String())
  })
);
var instituteLanguage = new SchemaValidator(
  Type27.Object({
    language: Type27.String()
  })
);
var verityInstituteSettings = new SchemaValidator(
  Type27.Object({
    allowLiveProctoring: Type27.Boolean(),
    liveProctoringResetableCredits: OptionalOrNull(Type27.Number()),
    liveProctoringCreditsResetDate: OptionalOrNull(Type27.String()),
    liveProctoringOneTimeCredits: Type27.Number(),
    allowLivePopIn: Type27.Boolean(),
    livePopInResetableCredits: OptionalOrNull(Type27.Number()),
    livePopInCreditsResetDate: OptionalOrNull(Type27.String()),
    livePopInOneTimeCredits: Type27.Number(),
    allowLivePopInPostReview: Type27.Boolean(),
    allowStandaloneMode: Type27.Boolean(),
    allowAiStrictMode: Type27.Boolean(),
    allowDemoMode: Type27.Boolean(),
    allowExtensionPlatform: Type27.Boolean(),
    allowLockdownPlatform: Type27.Boolean(),
    allowMobilePlatform: Type27.Boolean(),
    showAllAssessments: Type27.Boolean(),
    liteMode: Type27.Boolean(),
    salesforceId: OptionalOrNull(Type27.String()),
    cleanupEnabled: Type27.Boolean(),
    cutoffDate: OptionalOrNull(Type27.String()),
    cleanupFrequency: OptionalOrNull(Type27.String()),
    lastCleanupAt: OptionalOrNull(Type27.String())
  })
);
var integrated = new SchemaValidator(
  Type27.Object({
    integrated: Type27.Boolean()
  })
);
var CourseSettings = new SchemaValidator(
  Type27.Object({
    "CourseSetting-allowStopRecording": Type27.Boolean(),
    "InstituteSetting-allowStopRecording": Type27.Boolean(),
    "CourseEmailSetting-courseEmailSetting": Type27.Boolean(),
    "InstituteEmailSetting-instituteEmailSetting": Type27.Boolean(),
    "Info-instituteEmailCount": Type27.String()
  })
);
var disabledProctoringOptionType = Type27.Array(Type27.String());
var instituteDisabledProctoringOptions = new SchemaValidator(disabledProctoringOptionType);
var instituteAllowStrictMode = new SchemaValidator(
  Type27.Object({
    allowStrictMode: Type27.Boolean(),
    allowAiStrictMode: Type27.Boolean()
  })
);
var lmsAccounts = new SchemaValidator(
  Type27.Array(
    Type27.Object({
      id: Type27.Integer(),
      accountName: Type27.String(),
      lmsAccountId: Type27.String(),
      parentLmsAccountId: OptionalOrNull(Type27.String()),
      rootLmsAccountId: OptionalOrNull(Type27.String()),
      lmsAccountState: OptionalOrNull(Type27.String()),
      verityEnabled: Type27.Boolean()
    })
  )
);
var instituteScopeMode = new SchemaValidator(
  Type27.Object({
    scopeMode: Type27.Union([
      Type27.Literal("allCourses"),
      Type27.Literal("selectedAccounts"),
      Type27.Literal("selectedCourses")
    ]),
    allAccounts: Type27.Array(
      Type27.Object({
        id: Type27.Integer(),
        accountName: Type27.String(),
        lmsAccountId: Type27.String(),
        parentLmsAccountId: OptionalOrNull(Type27.String()),
        verityEnabled: Type27.Boolean()
      })
    ),
    selectedCourses: Type27.Array(
      Type27.Object({
        id: Type27.Integer(),
        courseId: Type27.String(),
        courseName: Type27.String(),
        courseUrl: Type27.String()
      })
    )
  })
);
var instituteSuffix = new SchemaValidator(
  Type27.Object({
    suffix: Type27.Union([Type27.String(), Type27.Null()])
  })
);
var latestReport = new SchemaValidator(
  Type27.Object(
    {
      month: Type27.Number(),
      year: Type27.Number(),
      title: Type27.String(),
      summary: Type27.String(),
      topMetrics: Type27.Array(
        Type27.Object({
          key: Type27.String(),
          type: Type27.Optional(Type27.String()),
          currentValue: Type27.String(),
          previousValue: Type27.Optional(Type27.String()),
          percentChange: Type27.Optional(Type27.String()),
          imageUrl: Type27.Optional(Type27.String()),
          texts: Type27.Array(Type27.String())
        })
      )
    },
    { additionalProperties: true }
  )
);
var proctoringOptionKey = new SchemaValidator(Type27.KeyOf(proctoringOptionProfile.properties.proctoringOption));
var instituteId = new SchemaValidator(
  Type27.Object({
    id: Type27.Number()
  })
);
var accessControlInstitute = new SchemaValidator(
  Type27.Object({
    id: Type27.String(),
    name: OptionalOrNull(Type27.String()),
    origin: OptionalOrNull(Type27.String()),
    platform: OptionalOrNull(Type27.String()),
    subdomain: OptionalOrNull(Type27.String()),
    hostnames: Type27.Array(Type27.String())
  })
);
var apiKey = new SchemaValidator(
  Type27.Object({
    id: Type27.Number(),
    keyId: OptionalOrNull(Type27.String()),
    platform: OptionalOrNull(Type27.String()),
    origin: OptionalOrNull(Type27.String()),
    apiUrl: OptionalOrNull(Type27.String())
  })
);
var thirdPartyPlatformRoster = new SchemaValidator(
  Type27.Object({
    selectedPlatforms: Type27.Array(
      Type27.Object({
        platformId: Type27.Number(),
        platformName: Type27.String()
      })
    ),
    unselectedPlatforms: Type27.Array(
      Type27.Object({
        platformId: Type27.Number(),
        platformName: Type27.String()
      })
    )
  })
);
var verityEnabled = new SchemaValidator(
  Type27.Object({
    verityEnabled: Type27.Boolean()
  })
);
var apiKeys = new SchemaValidator(Type27.Object({ apiKeys: Type27.Array(apiKey.schema) }));
var hostnames = new SchemaValidator(Type27.Object({ hostnames: Type27.Array(Type27.String()) }));
var thirdPartyIntegrations = new SchemaValidator(thirdPartyPlatformRoster.schema);
var instituteProctoringAccommodation = new SchemaValidator(
  Type27.Array(
    Type27.Object({
      userId: Type27.Integer(),
      accommodationNotes: OptionalOrNull(Type27.String()),
      proctoringOptionProfileId: OptionalOrNull(Type27.Integer())
    })
  )
);
var ruleType = Type27.Intersect([
  Type27.Union([
    Type27.Object({
      scope: Type27.Literal("global")
    }),
    Type27.Object({
      scope: Type27.Literal("lmsProvidedCourseId"),
      scopeValue: Type27.String()
    }),
    Type27.Object({
      scope: Type27.Literal("courseNamePrefix"),
      scopeValue: Type27.String()
    })
  ]),
  Type27.Object({
    type: Type27.Literal("editConfiguration"),
    details: OptionalOrNull(Type27.Unknown()),
    enabled: Type27.Boolean()
  })
]);
var rulesResponse = new SchemaValidator(Type27.Array(ruleType));
var usageHours = new SchemaValidator(Type27.Object({ hours: Type27.Number() }));
var instituteSettings = new SchemaValidator(
  Type27.Object({
    suffix: OptionalOrNull(Type27.String()),
    allowStrictMode: Type27.Boolean(),
    lockdownRequireManagedGuestSession: Type27.Boolean(),
    managedGuestSessionRequireIncognito: Type27.Boolean(),
    flaggedKeywords: Type27.Union([
      Type27.Null(),
      Type27.Array(
        Type27.Object({
          keyword: Type27.String(),
          level: Type27.Optional(Type27.Number())
        })
      )
    ]),
    allowQuizAttemptOnChrome: Type27.Boolean(),
    allowQuizAttemptOnFirefox: Type27.Boolean(),
    allowQuizAttemptOnSafari: Type27.Boolean(),
    disabledProctoringOptions: Type27.Union([Type27.Null(), disabledProctoringOptionType])
  })
);
var visualizerStatus = new SchemaValidator(
  Type27.Object({
    active: Type27.Boolean()
  })
);
var schema_validators_default26 = {
  accessControlInstituteId,
  instituteInfo,
  verityInstituteSettings,
  integrated,
  CourseSettings,
  instituteLanguage,
  instituteDisabledProctoringOptions,
  instituteAllowStrictMode,
  instituteScopeMode,
  lmsAccounts,
  instituteSuffix,
  latestReport,
  proctoringOptionKey,
  instituteId,
  accessControlInstitute,
  apiKey,
  apiKeys,
  verityEnabled,
  hostnames,
  thirdPartyIntegrations,
  instituteProctoringAccommodation,
  rulesResponse,
  usageHours,
  instituteSettings,
  visualizerStatus
};

// src/institute/index.ts
var InstituteClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default26);
  }
  get = async (instituteId2) => {
    try {
      if (!Number.isInteger(instituteId2)) {
        throw new Error(`Invalid instituteId: ${instituteId2}`);
      }
      const response = await super.fetch(new URL(`/api/verity/institute/${instituteId2}`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessControlId = async ({ subdomain }) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/access-control/institute/id?subdomain=${subdomain}`, this.config.verityOrigin), {
      credentials: "include"
    }),
    this.schemaValidators.accessControlInstituteId
  );
  isIntegrated = async (hostname) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute/hostname/${hostname}`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.integrated.validate(obj)) {
          return obj.integrated;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getLanguage = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/institute/language`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteLanguage.validate(obj)) {
          return obj.language;
        }
      } else if (response.status === 404) {
        return Promise.resolve("en");
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  isLiveEnabled = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v1/verity/settings`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.verityInstituteSettings.validate(obj)) {
          return !obj.liteMode && obj.allowLiveProctoring;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getVeritySettings = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v1/verity/settings`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.verityInstituteSettings.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseSetting = async (lmsCourseId) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/portal/settings?lmsCourseId=${lmsCourseId}`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        obj["CourseSetting-allowStopRecording"] = obj?.["CourseSetting-allowStopRecording"] === "true";
        obj["InstituteSetting-allowStopRecording"] = obj?.["InstituteSetting-allowStopRecording"] === "true";
        obj["CourseEmailSetting-courseEmailSetting"] = obj?.["CourseEmailSetting-courseEmailSetting"] === "true";
        obj["InstituteEmailSetting-instituteEmailSetting"] = obj?.["InstituteEmailSetting-instituteEmailSetting"] === "true";
        if (this.schemaValidators.CourseSettings.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  uploadInstituteAgreement = async ({
    instituteId: instituteId2,
    fileName,
    extension,
    file,
    deleteFile
  }) => {
    try {
      const response = await super.fetch(
        `/api/verity/agreement/${instituteId2}/${fileName}.${extension}${deleteFile ? "?delete=true" : ""}`,
        {
          method: "PUT",
          credentials: "include",
          body: file
        }
      );
      return response;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  deleteInstituteAgreement = async ({ instituteId: instituteId2 }) => {
    try {
      const response = await super.fetch(`/api/verity/agreement/${instituteId2}/delete`, {
        method: "PUT",
        credentials: "include"
      });
      return response;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  #convertDisabledProctoringOptions = (disabledProctoringOptionResponse) => {
    const validOptions = [];
    disabledProctoringOptionResponse?.forEach(
      (x) => this.schemaValidators.proctoringOptionKey.validate(x) && validOptions.push(x)
    );
    return validOptions;
  };
  getInstituteDisabledProctoringOptions = async () => this.#convertDisabledProctoringOptions(
    await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/institute-settings/disabled-options`, this.config.verityOrigin), {
        credentials: "include"
      }),
      this.schemaValidators.instituteDisabledProctoringOptions
    )
  );
  saveInstituteDisabledProctoringOptions = async (disabledProctoringOptions) => this.#convertDisabledProctoringOptions(
    await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/institute-settings/disabled-options`, this.config.verityOrigin), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ disabledProctoringOptions }),
        credentials: "include"
      }),
      this.schemaValidators.instituteDisabledProctoringOptions
    )
  );
  getInstituteThirdPartyPlatformsWithInstituteId = async (instituteId2) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/${instituteId2}/third-party-integrations`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        const selectedPlatformRoster = /* @__PURE__ */ new Map();
        const unselectedPlatformRoster = /* @__PURE__ */ new Map();
        if (this.schemaValidators.thirdPartyIntegrations.validate(obj)) {
          obj.selectedPlatforms.forEach((platform) => {
            selectedPlatformRoster.set(platform.platformId, platform);
          });
          obj.unselectedPlatforms.forEach((platform) => {
            unselectedPlatformRoster.set(platform.platformId, platform);
          });
        }
        return {
          selectedPlatformRoster: Object.fromEntries(selectedPlatformRoster),
          unselectedPlatformRoster: Object.fromEntries(unselectedPlatformRoster)
        };
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteThirdPartyPlatforms = async () => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/third-party-integrations`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        const selectedPlatformRoster = /* @__PURE__ */ new Map();
        const unselectedPlatformRoster = /* @__PURE__ */ new Map();
        if (this.schemaValidators.thirdPartyIntegrations.validate(obj)) {
          obj.selectedPlatforms.forEach((platform) => {
            selectedPlatformRoster.set(platform.platformId, platform);
          });
          obj.unselectedPlatforms.forEach((platform) => {
            unselectedPlatformRoster.set(platform.platformId, platform);
          });
        }
        return {
          selectedPlatformRoster: Object.fromEntries(selectedPlatformRoster),
          unselectedPlatformRoster: Object.fromEntries(unselectedPlatformRoster)
        };
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  saveInstituteThirdPartyPlatforms = async (thirdPartyPlatformRosters, rawNewlyEnabledThirdPartyPlatforms) => {
    try {
      if (!thirdPartyPlatformRosters) {
        return { ok: false, reason: "Failed to save third party integrations." };
      }
      const selectedPlatforms = Object.values(thirdPartyPlatformRosters.selectedPlatformRoster).map((platform) => ({
        platformId: platform.platformId,
        platformName: platform.platformName
      }));
      const unselectedPlatforms = Object.values(thirdPartyPlatformRosters.unselectedPlatformRoster).map((platform) => ({
        platformId: platform.platformId,
        platformName: platform.platformName
      }));
      const serializableRosters = {
        unselectedPlatforms,
        selectedPlatforms
      };
      let newlyEnabledThirdPartyPlatforms = void 0;
      if (rawNewlyEnabledThirdPartyPlatforms && rawNewlyEnabledThirdPartyPlatforms.length > 0) {
        const mapToBackendPlatform = (p) => ({
          name: p.platformName,
          id: p.platformId
        });
        newlyEnabledThirdPartyPlatforms = rawNewlyEnabledThirdPartyPlatforms.map(mapToBackendPlatform);
      }
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/third-party-integrations`, this.config.verityOrigin),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ thirdPartyPlatformRosters: serializableRosters, newlyEnabledThirdPartyPlatforms }),
          credentials: "include"
        }
      );
      if (response.ok) {
        return { ok: true };
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteAllowStrictMode = async () => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/allow-strict-mode`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteAllowStrictMode.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  saveInstituteAllowStrictMode = async (allowStrictMode) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/allow-strict-mode`, this.config.verityOrigin),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ allowStrictMode }),
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteAllowStrictMode.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteScopeSettings = async () => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/scope-mode`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteScopeMode.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  saveInstituteScopeSettings = async (scopeSettingsData) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/scope-mode`, this.config.verityOrigin),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ scopeSettingsData }),
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteRules = async (instituteUuid) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/verity/rule/${instituteUuid}`, this.config.verityOrigin), {
      credentials: "include"
    }),
    this.schemaValidators.rulesResponse
  );
  updateInstituteRules = async ({
    instituteUuid,
    ...rules
  }) => handleApiError(
    super.fetch(new URL(`/api/verity/rule/${instituteUuid}`, this.config.verityOrigin), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(rules)
    })
  );
  syncLmsAccounts = async () => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/sync-accounts`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteIdFromUuid = async (uuid) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/institute/uuid:${uuid}`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteId.validate(obj)) {
          return obj.id;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessControlInfo = async (uuid) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/institute/${uuid}`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessControlInstitute.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateAccessControlInfo = async ({
    instituteUuid,
    origin
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // name,
          origin
          // platform,
        }),
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessControlInstitute.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getApiKeys = async (uuid) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/institute/${uuid}/api-key`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.apiKeys.validate(obj)) {
          return obj.apiKeys;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  createApiKey = async ({
    instituteUuid,
    keyId,
    keyValue,
    platform,
    origin,
    apiUrl
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}/api-key`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            keyId,
            keyValue,
            platform,
            origin,
            apiUrl
          }),
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      } else {
        throw new Error(`${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateApiKey = async ({
    instituteUuid,
    id,
    platform,
    origin,
    apiUrl
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}/api-key/${id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            platform,
            origin,
            apiUrl
          }),
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.apiKey.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  deleteApiKey = async ({ instituteUuid, id }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}/api-key/${id}`,
        {
          method: "DELETE",
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      } else {
        throw new Error(`${response.status} - ${response.statusText}`);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  logInstituteUpdate = async ({
    type,
    payload,
    details,
    timestamp
  }) => {
    try {
      await super.fetch(`${this.config.verityOrigin}/api/verity/debug/institute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          details: {
            type,
            payload,
            ...details
          },
          timestamp
        })
      });
      return;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getSuffix = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/institute-settings/suffix`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteSuffix.validate(obj)) {
          return obj.suffix;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  saveSuffix = async (suffix) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/institute-settings/suffix`, this.config.verityOrigin), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ suffix }),
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteSuffix.validate(obj)) {
          return obj.suffix;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  isVerityEnabled = async (courseId2) => {
    return (await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/institute-settings/is-verity-enabled/${courseId2}`, this.config.verityOrigin), {
        credentials: "include"
      }),
      this.schemaValidators.verityEnabled
    )).verityEnabled;
  };
  #convertInstituteSettings = (instituteSettingsResponse) => {
    return {
      ...instituteSettingsResponse,
      disabledProctoringOptions: this.#convertDisabledProctoringOptions(
        instituteSettingsResponse.disabledProctoringOptions
      )
    };
  };
  getSettings = async (instituteUuid) => this.#convertInstituteSettings(
    await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/institute/${instituteUuid}/settings`, this.config.verityOrigin), {
        credentials: "include"
      }),
      this.schemaValidators.instituteSettings
    )
  );
  updateSettings = async (instituteUuid, settings) => this.#convertInstituteSettings(
    await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/institute/${instituteUuid}/settings`, this.config.verityOrigin), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings)
      }),
      this.schemaValidators.instituteSettings
    )
  );
  getLiveProctoringUsage = async (instituteUuid) => {
    return (await handleApiErrorWithSchema(
      super.fetch(
        new URL(`/api/verity/usage/live-proctoring?instituteId=${instituteUuid}`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      ),
      this.schemaValidators.usageHours
    )).hours;
  };
  getLivePopInUsage = async (instituteUuid) => {
    return (await handleApiErrorWithSchema(
      super.fetch(new URL(`/api/verity/usage/live-pop-in?instituteId=${instituteUuid}`, this.config.verityOrigin), {
        credentials: "include"
      }),
      this.schemaValidators.usageHours
    )).hours;
  };
  //#region Hostname
  /**
   * Retrieves the list of hostnames associated with the given institute.
   *
   * @param {string} instituteUuid - The Database ID of the institute.
   *
   * @returns {Promise<string[]>} - A promise that resolves with an array of hostnames.
   */
  getHostnames = async ({ instituteUuid }) => {
    try {
      const response = await super.fetch(
        new URL(`/api/access-control/institute/${instituteUuid}/hostname`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (!response.ok) {
        throw new ApiClientError(Error(`Invalid response: ${response.statusText}`));
      }
      const obj = await response.json();
      const isValidObject = this.schemaValidators.hostnames.validate(obj);
      if (!isValidObject) {
        throw new ApiClientError(Error(`Invalid object: ${JSON.stringify(obj)}`));
      }
      return obj;
    } catch (error) {
      throw new ApiClientError(Error(`Caught Error: ${String(error)}`));
    }
  };
  /**
   * Adds a new hostname to the given institute.
   *
   * @param {string} instituteUuid - The Database ID of the institute.
   * @param {string} hostname - The hostname to be added.
   *
   * @returns {Promise<string>} - A promise that resolves when the hostname is successfully added.
   */
  addHostname = async ({ instituteUuid, hostname }) => {
    try {
      const response = await super.fetch(
        new URL(`/api/access-control/institute/${instituteUuid}/hostname/${hostname}`, this.config.verityOrigin),
        {
          method: "POST",
          credentials: "include"
        }
      );
      if (!response.ok) {
        throw new ApiClientError(Error(`Invalid response: ${response.statusText}`));
      }
      return hostname;
    } catch (error) {
      throw new ApiClientError(Error(`Caught Error: ${String(error)}`));
    }
  };
  /**
   * Removes a hostname from the given institute.
   *
   * @param {number} instituteUuid - The Database ID of the institute.
   * @param {string} hostname - The hostname to be removed.
   *
   * @returns {Promise<string>} - A promise that resolves when the hostname is successfully removed.
   */
  removeHostname = async ({ instituteUuid, hostname }) => {
    try {
      const response = await super.fetch(
        new URL(`/api/access-control/institute/${instituteUuid}/hostname/${hostname}`, this.config.verityOrigin),
        {
          method: "DELETE",
          credentials: "include"
        }
      );
      if (!response.ok) {
        throw new ApiClientError(Error(`Invalid response: ${response.statusText}`));
      }
      return hostname;
    } catch (error) {
      throw new ApiClientError(Error(`Caught Error: ${String(error)}`));
    }
  };
  getInstitutionProctoringAccommodation = async () => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/proctoring-accommodations`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.instituteProctoringAccommodation.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateInstitutionProctoringAccommodation = async (proctoringAccommodations) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-settings/proctoring-accommodations`, this.config.verityOrigin),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ proctoringAccommodations }),
          credentials: "include"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getVisualizerStatus = async (instituteId2) => {
    try {
      const url2 = new URL(`/api/verity/theme/${instituteId2}/status`, this.config.verityOrigin);
      const response = await super.fetch(url2, { credentials: "include" });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.visualizerStatus.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  //#endregion
};

// src/user/schema-validators.ts
import { Type as Type28 } from "@sinclair/typebox";
var studentType = Type28.Object({
  id: Type28.Number(),
  uuid: Type28.String(),
  name: Type28.String()
});
var students = new SchemaValidator(Type28.Array(studentType));
var studentId = new SchemaValidator(Type28.Object({ id: Type28.Number() }));
var exemptions = new SchemaValidator(
  Type28.Union([
    Type28.Null(),
    Type28.Array(
      Type28.Object({
        userId: Type28.Integer()
      })
    )
  ])
);
var accessControlUserInfo = new SchemaValidator(
  Type28.Object({
    id: Type28.String(),
    name: Type28.String(),
    platform: Type28.String(),
    instituteId: Type28.String(),
    timezone: Type28.String(),
    privilegeGroupIds: Type28.Array(Type28.Number())
  })
);
var proctoringExemptions = new SchemaValidator(
  Type28.Array(
    Type28.Object({
      type: Type28.String(),
      userId: Type28.Integer(),
      accommodationNotes: Type28.Optional(Type28.String()),
      proctoringOptionProfileId: Type28.Optional(Type28.Integer())
    })
  )
);
var exemptionStatus = new SchemaValidator(
  Type28.Object({
    exempt: Type28.Boolean(),
    proctoringOption: Type28.Optional(proctoringOptionType)
  })
);
var language = new SchemaValidator(Type28.Union([Type28.Literal("en"), Type28.Literal("fr"), Type28.Null()]));
var standaloneAccount = new SchemaValidator(
  Type28.Object({
    id: Type28.String(),
    userIds: Type28.Array(Type28.String(), { minItems: 1 })
  })
);
var notifications = new SchemaValidator(
  Type28.Array(
    Type28.Object({
      type: Type28.String(),
      details: OptionalOrNull(Type28.Unknown())
    })
  )
);
var loginCount = new SchemaValidator(
  Type28.Object({
    loginCount: Type28.Number()
  })
);
var schema_validators_default27 = {
  students,
  studentId,
  exemptions,
  proctoringExemptions,
  exemptionStatus,
  accessControlUserInfo,
  language,
  standaloneAccount,
  notifications,
  loginCount
};

// src/user/index.ts
var UserClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default27);
  }
  getInstituteStudents = async () => {
    try {
      const response = await super.fetch(new URL("/api/verity/user/student-roster", this.config.verityOrigin), {
        credentials: "include"
      });
      const obj = await response.json();
      if (this.schemaValidators.students.validate(obj)) {
        return obj;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseStudents = async (assessmentInfo3) => {
    if (assessmentInfo3.courseId) {
      try {
        const response = await super.fetch(
          new URL(`/api/verity/user/student-roster/${assessmentInfo3.courseId}`, this.config.verityOrigin),
          {
            credentials: "include"
          }
        );
        if (response.ok) {
          const obj = await response.json();
          if (this.schemaValidators.students.validate(obj)) {
            return obj;
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          throw new ApiClientError(error);
        }
      }
    } else {
      console.error("No courseId provided");
    }
    throw new ApiClientError();
  };
  getProctoringExemptionStatus = async (assessmentInfo3) => {
    try {
      const response = await super.fetch(
        new URL(
          `/api/verity/proctoring-exemption/lms-course/${assessmentInfo3.courseId}/lms-assessment/${assessmentInfo3.quizId}/user`,
          this.config.verityOrigin
        ),
        { credentials: "include" }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.exemptionStatus.validate(obj)) {
          return obj;
        }
      }
      throw new Error("ExemptionStatus was not type {exempt: boolean}");
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError(new Error(`Unable to fetch exemption status`));
  };
  getProctoringExemptionStudents = async (assessmentInfo3) => {
    if (assessmentInfo3.courseId) {
      try {
        const response = await super.fetch(
          new URL(
            `/api/verity/proctoring-exemption/lms-course/${assessmentInfo3.courseId}/lms-assessment/${assessmentInfo3.quizId}`,
            this.config.verityOrigin
          ),
          { credentials: "include" }
        );
        if (response.ok) {
          const obj = await response.json();
          if (this.schemaValidators.proctoringExemptions.validate(obj)) {
            return obj;
          }
        } else if (response.status === 404) {
          return [];
        }
      } catch (error) {
        if (error instanceof Error) {
          throw new ApiClientError(error);
        }
      }
    } else {
      console.error("No courseId provided");
    }
    throw new ApiClientError();
  };
  saveProctoringExemptionStudents = async ({
    assessmentInfo: assessmentInfo3,
    students: students2
  }) => {
    try {
      const response = await super.fetch(
        new URL(
          `/api/verity/proctoring-exemption/lms-course/${assessmentInfo3.courseId}/lms-assessment/${assessmentInfo3.quizId}`,
          this.config.verityOrigin
        ),
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            exemptedStudents: students2
          })
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getExemptionInfoSuccessStatus = async (assessmentInfo3) => {
    if (assessmentInfo3.courseId) {
      try {
        const response = await super.fetch(
          new URL(
            `/api/verity/lms-course/${assessmentInfo3.courseId}/lms-assessment/${assessmentInfo3.quizId}`,
            this.config.verityOrigin
          ),
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json"
            }
          }
        );
        return response.ok;
      } catch (error) {
        return false;
      }
    } else {
      return false;
    }
  };
  createStandaloneUser = ({
    instituteUuid,
    courseUuid,
    assessmentUuid,
    email,
    firstName,
    lastName,
    expiryDate,
    emailTemplateData
  }) => {
    return handleApiErrorWithSchema(
      super.fetch(new URL("/api/access-control/single-use-account", this.config.verityOrigin), {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          instituteId: instituteUuid,
          courseId: courseUuid,
          resourceId: assessmentUuid,
          email,
          firstName,
          lastName,
          expiryDate,
          emailTemplateData
        })
      }),
      this.schemaValidators.standaloneAccount
    );
  };
  getUserId = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/user/id`, this.config.verityOrigin), {
        credentials: "include"
      });
      const userId = await response.json();
      if (this.schemaValidators.studentId.validate(userId)) {
        return userId.id;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getUserInfo = async (userId) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/institute-user${userId != null ? `/${userId}` : ""}`, this.config.verityOrigin),
        {
          credentials: "include"
        }
      );
      const userInfo2 = await response.json();
      return userInfo2;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessControlUserInfo = async (userId) => {
    try {
      const response = await super.fetch(new URL(`/api/access-control/user/${userId}`, this.config.verityOrigin));
      const userInfo2 = await response.json();
      if (this.schemaValidators.accessControlUserInfo.validate(userInfo2)) {
        return userInfo2;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getUserLanguage = async (userId) => {
    try {
      const url2 = new URL(`/api/verity/user/${userId}/language`, this.config.verityOrigin);
      const response = await super.fetch(url2, {
        credentials: "include"
      });
      const language2 = await response.json();
      if (this.schemaValidators.language.validate(language2)) {
        return language2;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  saveUserLanguage = async (userId, language2) => {
    try {
      const url2 = new URL(`/api/verity/user/${userId}/language`, this.config.verityOrigin);
      const response = await super.fetch(url2, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          language: language2
        })
      });
      return response.status;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  /**
   * Get the best available launch URL it is one of:
   * - This sessions institute origin
   * - The last course's institute origin
   * - The last course's URL
   * - The external tool URL in the last course
   */
  getLaunchUrl = async () => {
    try {
      const url2 = new URL(`/api/verity/user/launch`, this.config.verityOrigin);
      const response = await super.fetch(url2, {
        credentials: "include"
      });
      if (response.ok) {
        const json = await response.json();
        return json;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getNotifications = async ({ instituteUuid, courseUuid }) => {
    const searchParams = new URLSearchParams();
    if (instituteUuid) {
      searchParams.set("instituteId", instituteUuid);
    }
    if (courseUuid) {
      searchParams.set("courseId", courseUuid);
    }
    return await handleApiErrorWithSchema(
      super.fetch("/api/verity/user/notification" + (searchParams.size > 0 ? `?${searchParams.toString()}` : ""), {
        credentials: "include"
      }),
      this.schemaValidators.notifications
    );
  };
  dismissNotifications = ({ type }) => {
    const searchParams = new URLSearchParams();
    if (type) {
      searchParams.set("type", type);
    }
    return handleApiError(
      super.fetch(`/api/verity/user/notification?${searchParams.toString()}`, {
        method: "DELETE",
        credentials: "include"
      })
    );
  };
  updateLoginCount = () => {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/user/login-count`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      }),
      this.schemaValidators.loginCount
    );
  };
  mapAccessControlInfo = () => handleApiError(super.fetch(`${this.config.verityOrigin}/api/verity/user/map`, { method: "POST" }));
};

// src/start-assessment/schema-validators.ts
import { Type as Type29 } from "@sinclair/typebox";
var checkUserAndAuthenticateResponse = new SchemaValidator(
  Type29.Object({
    eligible: Type29.Boolean(),
    exempted: Type29.Boolean(),
    error: Type29.Optional(
      Type29.Union([
        Type29.String(),
        Type29.Object({
          kind: Type29.Literal("incompatibleBrowser"),
          data: Type29.Object({
            compatibleBrowsers: Type29.Object({
              chrome: Type29.Boolean(),
              firefox: Type29.Boolean()
            })
          })
        })
      ])
    ),
    platforms: Type29.Optional(
      Type29.Object({
        isExtension: Type29.Boolean(),
        isLockdown: Type29.Boolean(),
        isMobile: Type29.Boolean(),
        isChromeOs: Type29.Boolean()
      })
    )
  })
);
var schema_validators_default28 = {
  checkUserAndAuthenticateResponse
};

// src/start-assessment/index.ts
var startAssessmentClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default28);
  }
  check = async (data) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/quiz/take/check`, this.config.verityOrigin), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          data
        })
      });
      if (response.ok) {
        const responseData = await response.json();
        if (this.schemaValidators.checkUserAndAuthenticateResponse.validate(responseData)) {
          return responseData;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  start = async (assessmentInfo3) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/quiz/take/check`, this.config.verityOrigin), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          assessmentInfo: assessmentInfo3
        })
      });
      if (response.ok) {
        const responseData = await response.json();
        if (this.schemaValidators.checkUserAndAuthenticateResponse.validate(responseData)) {
          return responseData;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/system-check/schema-validators.ts
var schema_validators_default29 = {};

// src/system-check/index.ts
var SystemCheckClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default29);
  }
  // Returns upload speed in bits per second
  async #badConnectionCheck(uploadByteSize = 8e5, timeout = 5e3, numFetchRequest = 8) {
    if (globalThis.XMLHttpRequest) {
      const data = new Blob(["0".repeat(uploadByteSize)]);
      return new Promise((resolve, _reject) => {
        const xhr = new XMLHttpRequest();
        const connectionCheckUrl = `${this.config.verityOrigin}/connection/check`;
        let startTime;
        let lastLoaded = 0;
        xhr.open("POST", connectionCheckUrl, true);
        xhr.timeout = timeout;
        xhr.upload.addEventListener("loadstart", function(ev) {
          if (ev.loaded === 0) {
            startTime = performance.now();
          }
        });
        xhr.upload.addEventListener("progress", (ev) => {
          if (ev.lengthComputable) {
            lastLoaded = ev.loaded;
          }
        });
        const finalize = () => {
          const duration = performance.now() - startTime;
          resolve(lastLoaded * 8 / (duration / 1e3));
        };
        xhr.upload.addEventListener("loadend", finalize);
        xhr.ontimeout = finalize;
        xhr.send(data);
      });
    } else {
      const controller = new AbortController();
      const startTime = performance.now();
      let lastFetchEndTime = startTime;
      let lastFetchDuration = 0;
      const smallByteSize = uploadByteSize / numFetchRequest;
      let uploadSuccessCount = 0;
      const data = new Blob(["0".repeat(smallByteSize)]);
      setTimeout(() => {
        controller.abort();
      }, timeout);
      try {
        for (let i = 0; i < numFetchRequest; i++) {
          const fetchStart = performance.now();
          const resp = await this.fetch(`${this.config.verityOrigin}/api/verity/connection/check`, {
            method: "POST",
            body: data,
            signal: controller.signal
          });
          if (resp.ok) {
            lastFetchEndTime = performance.now();
            lastFetchDuration = lastFetchEndTime - fetchStart;
            uploadSuccessCount++;
          }
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          throw err;
        }
      }
      const endTime = performance.now();
      const remainingTime = endTime - lastFetchEndTime;
      const partialRatio = lastFetchDuration === 0 ? 0 : remainingTime / lastFetchDuration;
      const uploadedBytes = uploadSuccessCount * smallByteSize + partialRatio * smallByteSize;
      return uploadedBytes * 8 / ((endTime - startTime) / 1e3);
    }
  }
  /**
   * This connection check sucks
   */
  checkConnection = async (uploadByteSize, timeout, numFetchRequest) => {
    return {
      metrics: {
        //@ts-ignore
        type: navigator.connection?.type,
        //@ts-ignore
        effectiveType: navigator.connection?.effectiveType,
        //@ts-ignore
        downlinkMax: navigator.connection?.downlinkMax,
        //@ts-ignore
        downlink: navigator.connection?.downlink,
        //@ts-ignore
        rtt: navigator.connection?.rtt
      },
      uploadSpeed: await this.#badConnectionCheck(uploadByteSize, timeout, numFetchRequest)
    };
  };
};

// src/course/schema-validators.ts
import { Type as Type30 } from "@sinclair/typebox";
var courseInfoType = Type30.Object({
  id: Type30.Number(),
  instituteId: Type30.Number(),
  courseId: Type30.String(),
  code: Type30.Optional(Type30.Union([Type30.String(), Type30.Null()])),
  name: Type30.Optional(Type30.Union([Type30.String(), Type30.Null()])),
  isBlackboardUltra: Type30.Optional(Type30.Union([Type30.Boolean(), Type30.Null()])),
  verityEnabled: Type30.Boolean(),
  accessControlUuid: Type30.String()
});
var courseInfo = new SchemaValidator(courseInfoType);
var courseInfoList = new SchemaValidator(Type30.Array(courseInfoType));
var accessControlCourseInfoType = Type30.Object({
  id: Type30.String(),
  instituteId: Type30.String(),
  code: OptionalOrNull(Type30.String()),
  name: OptionalOrNull(Type30.String())
});
var accessControlCourseInfo = new SchemaValidator(accessControlCourseInfoType);
var courseId = new SchemaValidator(
  Type30.Object({
    id: Type30.Number()
  })
);
var courseCount = new SchemaValidator(
  Type30.Object({
    totalElements: Type30.Number()
  })
);
var agreementUrl = new SchemaValidator(
  Type30.Object({
    url: Type30.Optional(Type30.String())
  })
);
var geofenceLocationType = Type30.Object({
  longitude: Type30.Number(),
  latitude: Type30.Number(),
  radius: Type30.Number(),
  name: Type30.String(),
  isMetric: Type30.Boolean()
});
var courseSettings = new SchemaValidator(
  Type30.Object({
    geofenceLocations: Type30.Array(geofenceLocationType)
  })
);
var schema_validators_default30 = {
  courseInfo,
  courseInfoList,
  accessControlCourseInfo,
  courseId,
  courseCount,
  agreementUrl,
  courseSettings
};

// src/course/index.ts
var CourseClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default30);
  }
  getCourseInfoFromId = async (id) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/${id}`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.courseInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseInfoFromLmsCourseId = async ({
    lmsCourseId,
    instituteId: instituteId2
  }) => {
    try {
      const url2 = new URL(`${this.config.verityOrigin}/api/verity/course/lms-course/${lmsCourseId}`);
      if (instituteId2) {
        url2.searchParams.set("instituteId", instituteId2.toString());
      }
      const response = await super.fetch(url2, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.courseInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getPolicyPdfFromInstituteId = async (instituteId2) => {
    try {
      const url2 = new URL(`${this.config.verityOrigin}/api/verity/agreement/${instituteId2}/url`);
      const response = await super.fetch(url2, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.agreementUrl.validate(obj)) {
          return obj.url;
        }
      } else {
        return void 0;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseIdFromUuid = async (uuid) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/uuid:${uuid}`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.courseId.validate(obj)) {
          return obj.id;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getInstituteCoursesInfo = async (instituteId2) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/institute/${instituteId2}`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.courseInfoList.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getAccessControlCourse = async (courseUuid) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/course/${courseUuid}`);
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessControlCourseInfo.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseCountForPrefix = async (instituteUuid, namePrefix) => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.set("instituteId", instituteUuid);
      searchParams.set("name", namePrefix);
      searchParams.set("matchNameMode", "STARTS_WITH");
      searchParams.set("ignoreCase", "0");
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/course?${searchParams.toString()}`
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.courseCount.validate(obj)) {
          return obj.totalElements;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCourseSettings = async (verityCourseId) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/verity/course-settings/${verityCourseId}`, this.config.verityOrigin), {
      credentials: "include"
    }),
    this.schemaValidators.courseSettings
  );
  updateCourseSettings = async (verityCourseId, settings) => handleApiErrorWithSchema(
    super.fetch(new URL(`/api/verity/course-settings/${verityCourseId}`, this.config.verityOrigin), {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings)
    }),
    this.schemaValidators.courseSettings
  );
};

// src/proctoring-configuration/schema-validators.ts
import { Type as Type31 } from "@sinclair/typebox";
function OptionalOrNull3(type) {
  return Type31.Optional(Type31.Union([Type31.Null(), type]));
}
var proctoringAssessmentConfigurationType = Type31.Object({
  enabled: Type31.Boolean(),
  isLiveProctored: Type31.Boolean(),
  isLivePopIn: Type31.Boolean(),
  enablePostReview: Type31.Boolean(),
  password: Type31.Union([Type31.Null(), Type31.String()]),
  forceChromeOsManagedGuestSession: OptionalOrNull3(Type31.Boolean()),
  thirdPartyAssessmentOption: OptionalOrNull3(
    Type31.Object({
      thirdPartyUrl: OptionalOrNull3(Type31.String()),
      password: OptionalOrNull3(Type31.String()),
      thirdPartyPlatformId: OptionalOrNull3(Type31.Number())
    })
  ),
  proctoringOptionProfile
});
var proctoringAssessmentConfigurationV2 = new SchemaValidator(proctoringAssessmentConfigurationType);
var proctoringAssessmentConfigurationV2OrNull = new SchemaValidator(
  Type31.Union([Type31.Null(), proctoringAssessmentConfigurationType])
);
var proctoringAssessmentConfigurationResponse = new SchemaValidator(
  Type31.Object({
    standaloneStudentInfo: OptionalOrNull3(
      Type31.Array(
        Type31.Object({
          id: Type31.Number(),
          userId: Type31.Number(),
          email: Type31.String(),
          firstName: Type31.String(),
          lastName: Type31.String()
        })
      )
    )
  })
);
var enabledOptionsResponse = new SchemaValidator(
  Type31.Object({
    proctoringMethod: Type31.Array(
      Type31.Union([
        Type31.Literal("automated"),
        Type31.Literal("live"),
        Type31.Literal("pop-in"),
        Type31.Literal("pop-in-post-review")
      ])
    )
  })
);
var schema_validators_default31 = {
  proctoringAssessmentConfigurationV2,
  proctoringAssessmentConfigurationV2OrNull,
  proctoringAssessmentConfigurationResponse,
  enabledOptionsResponse
};

// src/proctoring-configuration/index.ts
var ProctoringConfigurationClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default31);
  }
  get = async (assessmentInfo3) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/assessment/configuration/${assessmentInfo3?.lms ?? "verity"}/${assessmentInfo3.hostname ?? location.hostname}/${assessmentInfo3.courseId ?? ""}/${assessmentInfo3.quizId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.proctoringAssessmentConfigurationV2OrNull.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  save = async ({
    assessmentInfo: assessmentInfo3,
    courseInfo: courseInfo2,
    profileId,
    proctoringAssessmentConfiguration,
    proctoringOption,
    standaloneOptions
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/assessment/configuration/${assessmentInfo3.lms ?? "verity"}/${assessmentInfo3.hostname ?? location.hostname}/${assessmentInfo3.courseId ?? ""}/${assessmentInfo3.quizId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            assessmentInfo: assessmentInfo3,
            courseInfo: courseInfo2,
            profileId,
            proctoringAssessmentConfiguration,
            proctoringOption,
            standaloneOptions
          }),
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.proctoringAssessmentConfigurationResponse.validate(obj)) {
          return obj;
        }
      }
      if (response.status === 413) {
        const payloadError = new Error("Payload too large");
        throw new ApiClientError(payloadError);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateWhitelist = async ({ courseId: courseId2, quizId, type }) => {
    try {
      let url2 = `${this.config.verityOrigin}/api/verity/assessment/update-whitelist/${courseId2}/${quizId}`;
      if (type) {
        url2 += `?type=${type}`;
      }
      await super.fetch(url2, {
        method: "PUT",
        credentials: "include"
      });
    } catch (error) {
      console.error(error);
    }
    return;
  };
  getEnabledOptions = ({
    assessmentInfo: assessmentInfo3,
    courseName
  }) => {
    let url2 = `${this.config.verityOrigin}/api/verity/assessment/configuration/${assessmentInfo3?.lms ?? "verity"}/${assessmentInfo3.hostname ?? location.hostname}/${assessmentInfo3.courseId ?? ""}/enabled-options`;
    if (courseName) {
      url2 += `?courseName=${encodeURIComponent(courseName)}`;
    }
    return handleApiErrorWithSchema(
      super.fetch(url2, {
        credentials: "include"
      }),
      this.schemaValidators.enabledOptionsResponse
    );
  };
};

// src/proctoring-profile/index.ts
var ProctoringProfileClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default25);
  }
  getAll = async ({ lmsProvidedCourseId }) => {
    try {
      const url2 = new URL(`/api/verity/v2/profile`, this.config.verityOrigin);
      if (lmsProvidedCourseId) {
        url2.searchParams.set("lmsProvidedCourseId", lmsProvidedCourseId);
      }
      const response = await super.fetch(url2, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.profiles.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  create = async ({
    proctoringOption,
    isInstituteProfile,
    lmsProvidedCourseId,
    name
  }) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profile`, this.config.verityOrigin), {
        headers: {
          "Content-Type": "application/json"
        },
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          proctoringOption,
          isInstituteProfile,
          lmsProvidedCourseId,
          name
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.profile.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  update = async ({
    id,
    proctoringOption,
    name
  }) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profile/${id}`, this.config.verityOrigin), {
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        method: "PATCH",
        body: JSON.stringify({
          proctoringOption,
          name
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.profile.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  delete = async (id) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profile/${id}`, this.config.verityOrigin), {
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        method: "DELETE"
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  enforce = async ({
    enforceProfileIds,
    unenforceProfileIds
  }) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profiles/enforce`, this.config.verityOrigin), {
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        method: "PATCH",
        body: JSON.stringify({
          enforceProfileIds,
          unenforceProfileIds
        })
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  profilesEnforced = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profiles/enforce`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.enforced.validate(obj)) {
          return obj.enforced;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getDefault = async () => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profile/default`, this.config.verityOrigin), {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.profileOrNull.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  setDefault = async (proctoringOptionProfileId) => {
    try {
      const response = await super.fetch(new URL(`/api/verity/v2/profile/default`, this.config.verityOrigin), {
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        method: "PATCH",
        body: JSON.stringify({
          proctoringOptionProfileId
        })
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/assessment/schema-validators.ts
import { Type as Type32 } from "@sinclair/typebox";
var assessmentInfoType = Type32.Object({
  id: Type32.Number(),
  courseId: Type32.Number(),
  quizId: Type32.String(),
  quizInfo: OptionalOrNull(
    Type32.Union([
      Type32.String(),
      Type32.Intersect([
        Type32.Object({
          name: OptionalOrNull(Type32.String())
        }),
        Type32.Record(Type32.String(), Type32.Any())
      ])
    ])
  ),
  isDeleted: Type32.Boolean(),
  createdAt: OptionalOrNull(Type32.Union([Type32.Number(), Type32.String()])),
  updatedAt: OptionalOrNull(Type32.Union([Type32.Number(), Type32.String()])),
  sourcesLastScanned: Type32.Union([Type32.String(), Type32.Null()])
});
var assessmentInfo2 = new SchemaValidator(assessmentInfoType);
var assessmentInfoArray = new SchemaValidator(Type32.Array(assessmentInfoType));
var assessmentInfoList = new SchemaValidator(
  Type32.Array(
    Type32.Intersect([
      assessmentInfoType,
      Type32.Object({
        hasDecoySite: Type32.Boolean(),
        proctoringOptions: OptionalOrNull(
          Type32.Object({
            enabled: OptionalOrNull(Type32.Boolean()),
            isLiveProctored: OptionalOrNull(Type32.Boolean()),
            isLivePopIn: OptionalOrNull(Type32.Boolean()),
            enablePostReview: OptionalOrNull(Type32.Boolean()),
            isExtension: OptionalOrNull(Type32.Boolean()),
            isLockdown: OptionalOrNull(Type32.Boolean()),
            isMobile: OptionalOrNull(Type32.Boolean()),
            chromeOsAccess: chromeOsAccessType,
            requiresIncognito: OptionalOrNull(Type32.Boolean())
          })
        )
      })
    ])
  )
);
var standaloneStudent = Type32.Object({
  userId: Type32.Number(),
  firstName: Type32.String(),
  lastName: Type32.String(),
  email: Type32.String()
});
var standaloneStudentList = new SchemaValidator(Type32.Array(standaloneStudent));
var d2lContentQuizzes = new SchemaValidator(
  Type32.Array(
    Type32.Object({
      id: Type32.Number(),
      quizId: Type32.String()
    })
  )
);
var d2lContentPlatform = new SchemaValidator(Type32.Number());
var verityEnabledAssessment = new SchemaValidator(
  Type32.Object({
    quizId: Type32.String()
  })
);
var verityEnabledAssessments = new SchemaValidator(Type32.Array(verityEnabledAssessment.schema));
var lmsThirdPartyQuiz = new SchemaValidator(
  Type32.Object({
    quizId: Type32.String(),
    platformId: Type32.Union([Type32.Number(), Type32.Null()])
  })
);
var lmsThirdPartyQuizzes = new SchemaValidator(Type32.Array(lmsThirdPartyQuiz.schema));
var lmsThirdPartyQuizWithActivityUrl = new SchemaValidator(
  Type32.Intersect([
    lmsThirdPartyQuiz.schema,
    Type32.Object({
      activityUrl: Type32.Union([Type32.String(), Type32.Null()])
    })
  ])
);
var lmsThirdPartyQuizzesWithActivityUrl = new SchemaValidator(Type32.Array(lmsThirdPartyQuizWithActivityUrl.schema));
var suffixUpdateResult = new SchemaValidator(
  Type32.Object({
    ok: Type32.Boolean(),
    exists: Type32.Boolean(),
    newName: Type32.Union([Type32.String(), Type32.Null()])
  })
);
var assessmentMonthlyCountType = Type32.Object({
  count: Type32.Number(),
  monthlyCounts: Type32.Array(Type32.Number())
});
var assessmentMonthlyCountList = new SchemaValidator(
  Type32.Object({
    liveAssessments: assessmentMonthlyCountType,
    nonLiveAssessments: assessmentMonthlyCountType
  })
);
var validate = new SchemaValidator(
  Type32.Object({
    didValidate: Type32.Boolean(),
    doPasswordsMatch: Type32.Boolean(),
    isPasswordRequired: Type32.Boolean()
  })
);
var synchronize = new SchemaValidator(
  Type32.Object({
    didSynchronize: Type32.Boolean(),
    doPasswordsMatch: Type32.Boolean(),
    isPasswordRequired: Type32.Boolean()
  })
);
var assessmentQuestionType = Type32.Object({
  id: Type32.Number(),
  createdAt: Type32.String(),
  updatedAt: Type32.String(),
  assessmentId: Type32.Number(),
  lmsProvidedQuestionId: Type32.Union([Type32.String(), Type32.Null()]),
  title: Type32.Union([Type32.String(), Type32.Null()]),
  bodyHtml: Type32.Union([Type32.String(), Type32.Null()]),
  bodyText: Type32.Union([Type32.String(), Type32.Null()]),
  sources: Type32.Union([Type32.Array(Type32.String()), Type32.Null()]),
  numSources: Type32.Number(),
  hasDecoySitePost: Type32.Boolean(),
  isDeleted: Type32.Boolean()
});
var assessmentQuestion = new SchemaValidator(assessmentQuestionType);
var assessmentQuestions = new SchemaValidator(Type32.Array(assessmentQuestionType));
var questionSources = new SchemaValidator(
  Type32.Object({
    sources: Type32.Array(
      Type32.Object({
        url: Type32.String(),
        email: Type32.Union([Type32.String(), Type32.Null()])
      })
    ),
    numSources: Type32.Number()
  })
);
var schema_validators_default32 = {
  assessmentInfo: assessmentInfo2,
  assessmentInfoArray,
  assessmentInfoList,
  standaloneStudentList,
  d2lContentQuizzes,
  d2lContentPlatform,
  lmsThirdPartyQuiz,
  lmsThirdPartyQuizzes,
  lmsThirdPartyQuizWithActivityUrl,
  lmsThirdPartyQuizzesWithActivityUrl,
  verityEnabledAssessment,
  verityEnabledAssessments,
  suffixUpdateResult,
  assessmentMonthlyCountList,
  validate,
  synchronize,
  assessmentQuestion,
  assessmentQuestions,
  questionSources
};

// src/assessment/index.ts
var AssessmentClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default32);
  }
  getAssessments = async ({ course }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/course/${course.id}/assessments`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.assessmentInfoArray.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    return [];
  };
  useAssessmentsWithTypeCount = async ({
    instituteId: instituteId2,
    courseId: courseId2,
    time
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/institute/${instituteId2}/courseId/${courseId2}/time/${time}`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.assessmentMonthlyCountList.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
  };
  getCourseAssessments = async (lmsCourseId) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/lms-course/${lmsCourseId}/assessments`,
        {
          credentials: "include"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.assessmentInfoList.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    return [];
  };
  getVerityEnabledAssessments = async (instituteId2, lmsCourseId) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/lms-course/${lmsCourseId}/verity-enabled-assessments?instituteId=${instituteId2}`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.verityEnabledAssessments
  );
  getAssessmentByCourseQuizId = async ({ lmsQuizId, courseId: courseId2 }) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/course/${courseId2}/lms-assessment/${lmsQuizId}`, `${this.config.verityOrigin}`),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.assessmentInfo.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
  };
  getAssessmentByAssessmentInfo = async (assessmentInfo3) => {
    try {
      const resp = await super.fetch(
        new URL(
          `/api/verity/lms-course/${assessmentInfo3.courseId}/lms-assessment/${assessmentInfo3.quizId}`,
          `${this.config.verityOrigin}`
        ),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.assessmentInfo.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
  };
  getAssessmentByAssessmentId = async (assessmentId) => {
    try {
      const resp = await super.fetch(new URL(`/api/verity/assessment/${assessmentId}`, `${this.config.verityOrigin}`), {
        credentials: "include"
      });
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.assessmentInfo.validate(data)) {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
  };
  isAssessmentVerityEnabled = async (assessmentInfo3) => {
    try {
      const searchParams = new URLSearchParams();
      if (assessmentInfo3.instituteId) {
        searchParams.append("instituteId", assessmentInfo3.instituteId.toString());
      }
      if (assessmentInfo3.hostname) {
        searchParams.append("hostname", assessmentInfo3.hostname);
      }
      const resp = await super.fetch(
        new URL(
          `/api/verity/lms-course/${assessmentInfo3.courseId}/lms-assessment/${assessmentInfo3.quizId}/proctored?${searchParams.toString()}`,
          `${this.config.verityOrigin}`
        ),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (typeof data === "boolean") {
          return data;
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  assessmentCopyCheck = async (assessmentInfo3) => {
    try {
      const type = assessmentInfo3.isCanvasNewQuiz ? "canvas-new-quiz" : void 0;
      const searchParams = new URLSearchParams();
      if (assessmentInfo3.instituteId) {
        searchParams.append("instituteId", assessmentInfo3.instituteId.toString());
      }
      if (assessmentInfo3?.hostname) {
        searchParams.append("hostname", assessmentInfo3.hostname);
      }
      if (type) {
        searchParams.append("type", type);
      }
      const resp = await super.fetch(
        new URL(
          `/api/verity/lms-course/${assessmentInfo3.courseId}/lms-assessment/${assessmentInfo3.quizId}/copy-check?${searchParams.toString()}`,
          `${this.config.verityOrigin}`
        ),
        { method: "PUT", credentials: "include" }
      );
      if (resp.ok) {
        return;
      }
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getStandaloneStudents = async (assessmentId) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/standalone-api/assessment/${assessmentId}/students`, `${this.config.verityOrigin}`),
        { credentials: "include" }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.standaloneStudentList.validate(data)) {
          return data;
        }
        throw new Error(`Invalid shape of response`);
      }
      throw new Error(`Invalid response: ${resp.status} - ${resp.statusText}`);
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  linkStandaloneStudentUser = ({
    assessmentId,
    standaloneStudentId,
    accountUuid,
    userUuid
  }) => {
    return handleApiError(
      super.fetch(
        new URL(
          `/api/verity/standalone-api/assessment/${assessmentId}/student/${standaloneStudentId}/map`,
          `${this.config.verityOrigin}`
        ),
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ accountUuid, userUuid })
        }
      )
    );
  };
  deleteStandaloneAssessmentByUUID = async (assessmentUUID, courseId2) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/standalone-api/assessment/${assessmentUUID}/${courseId2}`, `${this.config.verityOrigin}`),
        { method: "DELETE", credentials: "include" }
      );
      if (resp.ok) {
        return { success: true };
      }
      throw new Error(`Invalid response: ${resp.status} - ${resp.statusText}`);
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  updateLmsAssessmentNameSuffix = async ({
    assessmentId,
    oldSuffix,
    newSuffix
  }) => {
    try {
      const resp = await super.fetch(
        new URL(`/api/verity/assessment/${assessmentId}/suffix`, `${this.config.verityOrigin}`),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            oldSuffix,
            newSuffix
          })
        }
      );
      if (resp.ok) {
        const data = await resp.json();
        if (this.schemaValidators.suffixUpdateResult.validate(data)) {
          return data;
        }
        throw new Error(`Invalid shape of response`);
      }
      throw new Error(`Invalid response: ${resp.status} - ${resp.statusText}`);
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  /**
   * Validate the assessments configuration against the LMS and
   * return the results
   */
  validate = async ({ courseId: courseId2, quizId }) => {
    try {
      const response = await super.fetch(
        new URL(`/api/verity/lms-course/${courseId2}/lms-assessment/${quizId}/validate`, `${this.config.verityOrigin}`),
        {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (this.schemaValidators.validate.validate(data)) {
          return data;
        }
        throw new Error(`Invalid shape of response`);
      }
      throw new Error(`Invalid response: ${response.status} - ${response.statusText}`);
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  /**
   * Synchronize the given assessment's password between the
   * LMS and our database if needed
   */
  synchronize = async ({ courseId: courseId2, quizId }) => {
    try {
      const response = await super.fetch(
        new URL(
          `/api/verity/lms-course/${courseId2}/lms-assessment/${quizId}/synchronize`,
          `${this.config.verityOrigin}`
        ),
        {
          method: "GET",
          headers: { "Content-Type": "application/json" }
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (this.schemaValidators.synchronize.validate(data)) {
          return data;
        }
        throw new Error(`Invalid shape of response`);
      }
      throw new Error(`Invalid response: ${response.status} - ${response.statusText}`);
    } catch (e) {
      if (e instanceof Error) {
        throw new ApiClientError(e);
      }
    }
    throw new ApiClientError();
  };
  getContentQuizThirdPartyPlatform = async (instituteId2, courseId2, quizId) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/institute/${instituteId2}/courseId/${courseId2}/quizId/${quizId}/third-party-platform`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.d2lContentPlatform
  );
  getVerityThirdPartyQuizzes = async (instituteId2, courseId2) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/lms-course/${courseId2}/verity-third-party-quizzes?instituteId=${instituteId2}`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.lmsThirdPartyQuizzes
  );
  getQuizzesFromCache = async (instituteId2, courseId2, lmsAssessmentIds) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/lms-course/${courseId2}/check-quiz-cache?instituteId=${instituteId2}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          quizIds: lmsAssessmentIds
        })
      }
    ),
    this.schemaValidators.lmsThirdPartyQuizzesWithActivityUrl
  );
  saveQuizzesToCache = async (instituteId2, courseId2, quizesToSave) => handleApiError(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/lms-course/${courseId2}/save-quiz-cache?instituteId=${instituteId2}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          results: quizesToSave
        })
      }
    )
  );
  getAssessmentQuestionsFromLMS = async (assessmentId) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/lms-questions`, {
      credentials: "include"
    }),
    this.schemaValidators.assessmentQuestions
  );
  getAssessmentQuestions = async (assessmentId, includeDeleted) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/questions` + (includeDeleted ? "?includeDeleted=true" : ""),
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.assessmentQuestions
  );
  getQuestionSourcesWithEmail = async (assessmentId, questionId, fetchNewSources) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/verity/dmca/${assessmentId}/questions/${questionId}/sources?fetch=${fetchNewSources}`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.questionSources
  );
  createUserQuestion = async (assessmentId, userQuestion) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/user-question`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question: userQuestion })
    }),
    this.schemaValidators.assessmentQuestion
  );
  createDecoySite = async (assessmentId) => handleApiError(
    super.fetch(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/decoy-site`, {
      method: "PUT",
      credentials: "include"
    })
  );
  deleteDecoySite = async (assessmentId) => handleApiError(
    super.fetch(`${this.config.verityOrigin}/api/verity/assessment/${assessmentId}/decoy-site`, {
      method: "DELETE",
      credentials: "include"
    })
  );
};

// src/support/schema-validators.ts
import { Type as Type33 } from "@sinclair/typebox";
var callAccessCode = new SchemaValidator(
  Type33.Object({
    accessCode: OptionalOrNull(Type33.Number())
  })
);
var schema_validators_default33 = {
  callAccessCode
};

// src/support/index.ts
var SupportClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default33);
  }
  sendSupportEmail = async ({ name, email, subject, message }) => {
    try {
      const res = await super.fetch(new URL(`/api/verity/support/email`, this.config.verityOrigin), {
        method: "POST",
        body: JSON.stringify({ name, email, subject, message }),
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (res.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  sendPlatformRequestEmailCSM = async ({ name, link }) => {
    try {
      const res = await super.fetch(
        new URL(`/api/verity/support/send-platform-request-email-csm`, this.config.verityOrigin),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            name,
            link
          })
        }
      );
      return res.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getCallAccessCode = async () => {
    return handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/support/getCallAccessCode`, {
        method: "GET",
        credentials: "include"
      }),
      this.schemaValidators.callAccessCode
    );
  };
};

// src/installation/schema-validators.ts
import { Type as Type34 } from "@sinclair/typebox";
var installation = new SchemaValidator(
  Type34.Object({
    id: Type34.String(),
    platform: Type34.Union([Type34.Literal("extension"), Type34.Literal("lockdown"), Type34.Literal("mobile")]),
    version: Type34.String(),
    fingerprint: Type34.String(),
    details: Type34.Object({})
  })
);
var schema_validators_default34 = {
  installation
};

// src/installation/index.ts
var InstallationClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default34);
  }
  async createInstallation(systemInfo) {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/installation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(systemInfo)
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.installation.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
  async updateInstallation(id, systemInfo) {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/installation/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(systemInfo)
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.installation.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  }
};

// src/oauth/schema-validators.ts
import { Type as Type35 } from "@sinclair/typebox";
var authorization = new SchemaValidator(
  Type35.Object({
    authorizationUrl: Type35.String()
  })
);
var accessToken = new SchemaValidator(
  Type35.Object({
    accessToken: Type35.String(),
    expiresIn: Type35.Number()
  })
);
var schema_validators_default35 = {
  authorization,
  accessToken
};

// src/oauth/index.ts
var OAuthClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default35);
  }
  getAuthorizationUrl = async ({ courseId: courseId2, callbackUrl }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/oauth/grant`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ courseId: courseId2, callbackUrl })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.authorization.validate(obj)) {
          return obj.authorizationUrl;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getOAuthAccessToken = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/oauth/token`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.accessToken.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  refreshStoredToken = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/oauth/refresh`, {
        credentials: "include"
      });
      return response.ok;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/lti/schema-validators.ts
import { Type as Type36 } from "@sinclair/typebox";
var clientRegistrationType = Type36.Object({
  id: Type36.Integer(),
  linkageAttribute: OptionalOrNull(Type36.String()),
  linkUsersByEmail: Type36.Boolean()
});
var clientRegistration = new SchemaValidator(clientRegistrationType);
var clientRegistrations = new SchemaValidator(PaginatedResults(clientRegistrationType, "clientRegistrations"));
var schema_validators_default36 = {
  clientRegistration,
  clientRegistrations
};

// src/lti/index.ts
var LtiClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default36);
  }
  getInstituteClientRegistrations = async (instituteId2) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/access-control/client-registration?instituteId=${instituteId2}`),
    this.schemaValidators.clientRegistrations
  );
  updateClientRegistration = async ({
    id,
    linkageAttribute,
    linkUsersByEmail
  }) => handleApiErrorWithSchema(
    super.fetch(`${this.config.verityOrigin}/api/access-control/client-registration/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        linkageAttribute: linkageAttribute === void 0 ? null : linkageAttribute ?? "",
        linkUsersByEmail
      })
    }),
    this.schemaValidators.clientRegistration
  );
};

// src/basic-auth/schema-validators.ts
import { Type as Type37 } from "@sinclair/typebox";
var createAccount = new SchemaValidator(
  Type37.Object({
    id: Type37.String(),
    email: Type37.String(),
    enabled: Type37.Boolean(),
    accountNonLocked: Type37.Boolean(),
    currentUserId: OptionalOrNull(Type37.String()),
    userIds: Type37.Array(Type37.String())
  })
);
var login = new SchemaValidator(
  Type37.Object({
    refreshToken: Type37.String(),
    accountId: Type37.String()
  })
);
var verifyEmail = new SchemaValidator(
  Type37.Object({
    accountId: Type37.String(),
    emailVerificationToken: Type37.String()
  })
);
var resetPassword = new SchemaValidator(
  Type37.Object({
    accountId: Type37.String(),
    passwordRestToken: Type37.String(),
    password: Type37.String()
  })
);
var schema_validators_default37 = {
  createAccount,
  login,
  verifyEmail,
  resetPassword
};

// src/basic-auth/index.ts
var BasicAuthClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default37);
  }
  login = async ({ email, password }) => {
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      formData.append("password", password);
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/login`, {
        credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: formData.toString()
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.login.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  createAccount = async ({ email, password }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.createAccount.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  sendVerificationEmail = async ({ email }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/basic-auth/email-verification`,
        {
          credentials: "include",
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email
          })
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  verifyEmail = async ({
    accountId,
    emailVerificationToken
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth/verify-email`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountId,
          emailVerificationToken
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.verifyEmail.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  sendResetPasswordEmail = async ({ email }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth/reset-password`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email
        })
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  resetPassword = async ({
    accountId,
    passwordRestToken,
    password
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/basic-auth/password`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountId,
          passwordRestToken,
          password
        })
      });
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/saml/schema-validators.ts
import { Type as Type38 } from "@sinclair/typebox";
var assertingPartyDetails = new SchemaValidator(
  Type38.Object({
    entityId: Type38.Union([Type38.String(), Type38.Null()]),
    wantAuthnRequestsSigned: Type38.Union([Type38.Boolean(), Type38.Null()]),
    singleSignOnServiceLocation: Type38.Union([Type38.String(), Type38.Null()]),
    singleSignOnServiceBindingUrn: Type38.Union([Type38.String(), Type38.Null()]),
    singleLogoutServiceLocation: Type38.Union([Type38.String(), Type38.Null()]),
    singleLogoutServiceResponseLocation: Type38.Union([Type38.String(), Type38.Null()]),
    singleLogoutServiceBindingUrn: Type38.Union([Type38.String(), Type38.Null()]),
    signingAlgorithms: Type38.Array(Type38.String()),
    verificationX509Credentials: Type38.Array(
      Type38.Object({
        certificate: Type38.String(),
        type: Type38.String()
      })
    ),
    encryptionX509Credentials: Type38.Array(
      Type38.Object({
        certificate: Type38.String(),
        type: Type38.String()
      })
    )
  })
);
var attributeMapping = new SchemaValidator(
  Type38.Object({
    email: Type38.Union([Type38.String(), Type38.Null()]),
    name: Type38.Union([Type38.String(), Type38.Null()]),
    role: Type38.Union([Type38.String(), Type38.Null()]),
    linkage: Type38.Union([Type38.String(), Type38.Null()])
  })
);
var roleMapping = new SchemaValidator(
  Type38.Object({
    id: Type38.Number(),
    attribute: Type38.String(),
    privilegeGroupId: Type38.Number(),
    requiredParentPrivilegeGroupId: OptionalOrNull(Type38.Integer())
  })
);
var relyingPartyRegistrationIds = {
  instituteId: Type38.String(),
  registrationId: Type38.String()
};
var relyingPartyRegistration = new SchemaValidator(
  Type38.Object({
    ...relyingPartyRegistrationIds,
    metadataLocation: Type38.Union([Type38.String(), Type38.Null()]),
    nameIdFormat: Type38.Union([Type38.String(), Type38.Null()]),
    assertingPartyDetails: Type38.Union([assertingPartyDetails.schema, Type38.Null()]),
    attributeMapping: Type38.Union([attributeMapping.schema, Type38.Null()]),
    roleMappings: Type38.Union([Type38.Array(roleMapping.schema), Type38.Null()]),
    linkUsersByEmail: Type38.Boolean(),
    enabled: Type38.Boolean()
  })
);
var getRelyingPartyRegistrations = new SchemaValidator(
  Type38.Object({
    content: Type38.Array(relyingPartyRegistration.schema)
  })
);
var getRelyingPartyRegistrationIds = new SchemaValidator(
  Type38.Object({
    content: Type38.Array(Type38.Object(relyingPartyRegistrationIds))
  })
);
var addRoleMapping = new SchemaValidator(
  Type38.Object({
    attribute: Type38.String(),
    privilegeGroupId: Type38.Number(),
    requiredParentPrivilegeGroupId: OptionalOrNull(Type38.Integer())
  })
);
var updateRelyingPartyRegistration = new SchemaValidator(
  Type38.Object({
    metadataLocation: Type38.Union([Type38.String(), Type38.Null()]),
    nameIdFormat: Type38.Union([Type38.String(), Type38.Null()]),
    assertingPartyDetails: Type38.Union([assertingPartyDetails.schema, Type38.Null()]),
    attributeMapping: Type38.Union([attributeMapping.schema, Type38.Null()]),
    linkUsersByEmail: Type38.Boolean(),
    enabled: Type38.Union([Type38.Boolean(), Type38.Null()])
  })
);
var createRelyingPartyRegistration = new SchemaValidator(
  Type38.Object({
    instituteId: Type38.String(),
    registrationId: Type38.Union([Type38.String(), Type38.Null()]),
    metadataLocation: Type38.Union([Type38.String(), Type38.Null()]),
    nameIdFormat: Type38.Union([Type38.String(), Type38.Null()]),
    assertingPartyDetails: Type38.Union([assertingPartyDetails.schema, Type38.Null()]),
    attributeMapping: Type38.Union([attributeMapping.schema, Type38.Null()]),
    roleMappings: Type38.Union([Type38.Array(addRoleMapping.schema), Type38.Null()]),
    linkUsersByEmail: Type38.Boolean(),
    enabled: Type38.Union([Type38.Boolean(), Type38.Null()])
  })
);
var getRoleMappingPresets = new SchemaValidator(
  Type38.Array(
    Type38.Object({
      preset: Type38.Union([Type38.Literal("STUDENT"), Type38.Literal("INSTRUCTOR"), Type38.Literal("ADMIN")]),
      privilegeGroupId: Type38.Number(),
      requiredParentPrivilegeGroupId: OptionalOrNull(Type38.Integer())
    })
  )
);
var schema_validators_default38 = {
  relyingPartyRegistration,
  getRelyingPartyRegistrations,
  getRelyingPartyRegistrationIds,
  addRoleMapping,
  updateRelyingPartyRegistration,
  createRelyingPartyRegistration,
  getRoleMappingPresets
};

// src/saml/index.ts
var SAMLClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default38);
  }
  createRelyingPartyRegistration = async ({
    createRequestBody
  }) => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/relyingPartyRegistration`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...createRequestBody
        })
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getRelyingPartyRegistration = async ({ registrationId }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}`,
        {
          credentials: "include",
          method: "GET"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getRelyingPartyRegistrations = async ({ instituteId: instituteId2, enabled }) => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.set("instituteId", instituteId2);
      if (enabled !== void 0) {
        searchParams.set("enabled", enabled.toString());
      }
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration?${searchParams.toString()}`,
        {
          credentials: "include",
          method: "GET"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getRelyingPartyRegistrations.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getRelyingPartyRegistrationIds = async ({ instituteId: instituteId2, enabled }) => {
    const searchParams = new URLSearchParams();
    searchParams.set("instituteId", instituteId2);
    if (enabled !== void 0) {
      searchParams.set("enabled", enabled.toString());
    }
    searchParams.set("getRegistrationIds", "true");
    return handleApiErrorWithSchema(
      super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration?${searchParams.toString()}`,
        {
          credentials: "include",
          method: "GET"
        }
      ),
      this.schemaValidators.getRelyingPartyRegistrationIds
    );
  };
  updateRelyingPartyRegistration = async ({
    registrationId,
    updateRequestBody
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}`,
        {
          credentials: "include",
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...updateRequestBody
          })
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  addRoleMapping = async ({
    registrationId,
    attribute,
    privilegeGroupId,
    requiredParentPrivilegeGroupId
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}/roleMapping`,
        {
          credentials: "include",
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attribute,
            privilegeGroupId,
            requiredParentPrivilegeGroupId
          })
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  deleteRoleMapping = async ({ registrationId, roleMappingId }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}/roleMapping/${roleMappingId}`,
        {
          credentials: "include",
          method: "DELETE",
          headers: { "Content-Type": "application/json" }
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.relyingPartyRegistration.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getRoleMappingPresets = async ({ registrationId }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/access-control/relyingPartyRegistration/${registrationId}/presets`,
        {
          credentials: "include",
          method: "GET",
          headers: { "Content-Type": "application/json" }
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getRoleMappingPresets.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/media/schema-validators.ts
import { Type as Type39 } from "@sinclair/typebox";
var getNextRecordingNumbers = new SchemaValidator(
  Type39.Object({
    screencapture: Type39.Number(),
    webcam: Type39.Number()
  })
);
var uploadRecording = new SchemaValidator(
  Type39.Object({
    url: Type39.String()
  })
);
var schema_validators_default39 = {
  getNextRecordingNumbers,
  uploadRecording
};

// src/media/index.ts
var MediaClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default39);
  }
  getNextRecordingNumbers = async ({ sessionId }) => {
    const searchParams = new URLSearchParams([
      ["number", "webcam"],
      ["number", "screencapture"],
      ["next", "true"]
    ]);
    const data = await handleApiErrorWithSchema(
      super.fetch(`${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}?${searchParams.toString()}`),
      schema_validators_default39.getNextRecordingNumbers
    );
    return {
      internal: data.screencapture,
      external: data.webcam
    };
  };
  uploadRecording = async ({
    sessionId,
    recordingDetails
  }) => {
    const videoBlob = new Blob([await fetch(recordingDetails.videoObjectUrl).then((r) => r.arrayBuffer())], {
      type: recordingDetails.videoMimeType
    });
    const searchParams = new URLSearchParams({
      segment: `${recordingDetails.blobNumber}`,
      url: "upload"
    });
    const response = await handleApiError(
      super.fetch(
        `${this.config.verityOrigin}/api/verity/quiz/recording/${sessionId}/${recordingDetails.recordingType === "display" ? "screencapture" : recordingDetails.recordingType}/${recordingDetails.videoNumber}?${searchParams.toString()}`
      )
    );
    if (response.status === 409) {
      throw new Error("409");
    } else if (!response.ok) {
      throw new ApiClientError(new Error(`Unable to get upload URL: ${response.status} - ${response.statusText}`));
    }
    const data = await response.json();
    if (!schema_validators_default39.uploadRecording.validate(data)) {
      throw new ApiClientError(new Error(`Invalid upload URL: ${JSON.stringify(data)}`));
    }
    const s3Response = await fetch(data.url, {
      method: "PUT",
      body: videoBlob
    });
    if (!s3Response.ok) {
      throw new ApiClientError(new Error(`Upload to S3 failed: ${s3Response.status} - ${s3Response.statusText}`));
    }
  };
  uploadPhoto = async ({
    sessionId,
    type,
    imageObjectUrl,
    mimeType
  }) => {
    const imageBlob = new Blob([await fetch(imageObjectUrl).then((r) => r.arrayBuffer())], { type: mimeType });
    await handleApiError(
      super.fetch(`${this.config.verityOrigin}/api/verity/photo/${sessionId}-${type}.png`, {
        method: "PUT",
        body: imageBlob
      })
    );
  };
};

// src/data-dump/index.ts
import { coerceToContractAbsoluteBase as coerceToContractAbsoluteBase3, DataDumpCallbackContract, DataDumpGrantContract, DataDumpLinksContract, withContractBase as withContractBase3 } from "@yujacorp/verity-api-contract";
var DataDumpClient = class extends ApiClient {
  constructor(config) {
    super(config, {});
  }
  createUploadGrant = withDefaultErrorHandler(async (dynamicInput) => {
    const handlers = withContractBase3({
      contract: DataDumpGrantContract,
      base: coerceToContractAbsoluteBase3(`${this.config.verityOrigin}/api/verity/`)
    }).withFetchParams({
      pathParams: dynamicInput.pathParams,
      body: dynamicInput.body
    }).withFetcher(async ({ method, url: url2, body }) => {
      const res = await this.fetch(url2, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : void 0
      });
      return res.json();
    });
    const data = await handlers.toParsed();
    return data;
  });
  executeUploadCallback = withDefaultErrorHandler(async (dynamicInput) => {
    const handlers = withContractBase3({
      contract: DataDumpCallbackContract,
      base: coerceToContractAbsoluteBase3(`${this.config.verityOrigin}/api/verity/`)
    }).withFetchParams({
      pathParams: dynamicInput.pathParams,
      body: dynamicInput.body
    }).withFetcher(async ({ method, url: url2, body }) => {
      const res = await this.fetch(url2, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : void 0
      });
      return { ok: res.ok };
    });
    const { ok } = await handlers.toRaw();
    return ok;
  });
  getLinks = withDefaultErrorHandler(async (dynamicInput) => {
    const handlers = withContractBase3({
      contract: DataDumpLinksContract,
      base: coerceToContractAbsoluteBase3(`${this.config.verityOrigin}/api/verity/`)
    }).withFetchParams({
      pathParams: dynamicInput.pathParams
    }).withFetcher(async ({ method, url: url2 }) => {
      const res = await this.fetch(url2, {
        method,
        headers: { "Content-Type": "application/json" }
      });
      return res.json();
    });
    return handlers.toParsed();
  });
};

// src/payment/index.ts
var PaymentClient = class extends ApiClient {
  constructor(config) {
    super(config, {});
  }
  initiatePayment = async ({ assessmentId }) => {
    try {
      const res = await super.fetch(new URL(`/api/verity/payment`, this.config.verityOrigin), {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          assessmentId
        })
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  checkPaymentStatus = async ({ sessionId, assessmentId }) => {
    try {
      const res = await super.fetch(
        new URL(
          `/api/verity/payment/complete?stripeSessionId=${sessionId}&assessmentId=${assessmentId}`,
          this.config.verityOrigin
        ),
        {
          method: "GET",
          credentials: "include"
        }
      );
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getEnabled = async () => {
    try {
      const res = await super.fetch(new URL(`/api/verity/payment/enabled`, this.config.verityOrigin), {
        method: "GET",
        credentials: "include"
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/feedback/schema-validators.ts
import { Type as Type40 } from "@sinclair/typebox";
var satisfactionType = new SchemaValidator(
  Type40.Union([Type40.Literal("poor"), Type40.Literal("satisfied"), Type40.Literal("good"), Type40.Literal("excellent")])
);
var categoryType = new SchemaValidator(
  Type40.Union([Type40.Literal("suggestion"), Type40.Literal("bug"), Type40.Literal("comment"), Type40.Literal("other")])
);
var schema_validators_default40 = {
  satisfactionType,
  categoryType
};

// src/feedback/index.ts
var FeedbackClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default40);
  }
  submitFeedback = async ({
    satisfaction,
    category,
    summary,
    details
  }) => {
    try {
      const url2 = new URL("/api/verity/feedback", this.config.verityOrigin);
      const response = await super.fetch(url2, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          satisfaction,
          category,
          summary,
          details
        })
      });
      if (response.ok) return;
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/scratch-note/schema-validators.ts
import { Type as Type41 } from "@sinclair/typebox";
var createUploadGrant = new SchemaValidator(
  Type41.Object({
    id: Type41.String(),
    url: Type41.String()
  })
);
var getLinks = new SchemaValidator(
  Type41.Array(
    Type41.Object({
      id: Type41.String(),
      url: Type41.String(),
      uploadedAt: Type41.Optional(Type41.Number())
    })
  )
);
var schema_validators_default41 = {
  createUploadGrant,
  getLinks
};

// src/scratch-note/index.ts
var ScratchNoteClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default41);
  }
  createUploadGrant = async ({
    proctoringSessionUuid,
    mimeType
  }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/scratch-note/upload/grant/${proctoringSessionUuid}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ mimeType })
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.createUploadGrant.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  uploadCallback = async ({ scratchNoteId }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/scratch-note/upload/callback/${scratchNoteId}`,
        {
          method: "GET"
        }
      );
      if (response.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getLinks = async ({ proctoringSessionUuid }) => {
    try {
      const response = await super.fetch(
        `${this.config.verityOrigin}/api/verity/scratch-note/links/${proctoringSessionUuid}`,
        {
          method: "GET"
        }
      );
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getLinks.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/report/schema-validators.ts
var schema_validators_default42 = {};

// src/report/index.ts
var InstituteClient2 = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default42);
  }
  getInstituteReport = async ({
    instituteId: instituteId2,
    subAccountId,
    startDate,
    endDate,
    monthYearRange
  }) => {
    try {
      const params = new URLSearchParams({
        instituteId: instituteId2.toString(),
        ...subAccountId ? { subAccountId: subAccountId.toString() } : {},
        startDate: startDate.getTime().toString(),
        endDate: endDate.getTime().toString(),
        monthYearRange: JSON.stringify(monthYearRange),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      const url2 = new URL(`/api/verity/report/admin/institute-report`, this.config.verityOrigin);
      url2.search = params.toString();
      const res = await super.fetch(url2);
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getSubAccounts = async () => {
    try {
      const url2 = new URL(`/api/verity/report/admin/subaccounts`, this.config.verityOrigin);
      const res = await super.fetch(url2);
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getReceiveInstitutionReportEmail = async () => {
    try {
      const url2 = new URL(`/api/verity/report/admin/receive-institution-report-email`, this.config.verityOrigin);
      const res = await super.fetch(url2);
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getUsageEmailsData = async ({
    instituteId: instituteId2,
    startDate,
    endDate,
    isCSM
  }) => {
    try {
      const params = new URLSearchParams({
        instituteId: instituteId2.toString(),
        startMonth: startDate.month.toString(),
        startYear: startDate.year.toString(),
        endMonth: endDate.month.toString(),
        endYear: endDate.year.toString()
      });
      const url2 = new URL(`/api/verity/report/admin/get-monthly-usage-emails-data`, this.config.verityOrigin);
      url2.search = params.toString();
      const res = await super.fetch(url2);
      if (res.ok) {
        const raw = await res.json();
        const data = raw;
        if (!isCSM) {
          return data.filter((item) => !item.topMetrics.some((m) => m.type.toLowerCase().includes("csm")));
        }
        return data;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  generateHistoricEmailReport = async (email, canEditVeritySettings) => {
    try {
      const res = await super.fetch(`/api/verity/report/admin/generate-historic-email-report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          isForCSM: canEditVeritySettings
        })
      });
      if (res.ok) {
        return await res.json();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  generateEmailReportPDF = async (html) => {
    try {
      const res = await super.fetch(`/api/verity/report/admin/generate-email-report-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ html })
      });
      if (res.ok) {
        return await res.blob();
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  updateReceiveInstitutionReportEmail = async (receiveEmail) => {
    try {
      const url2 = new URL(`/api/verity/report/admin/receive-institution-report-email`, this.config.verityOrigin);
      const res = await super.fetch(url2, {
        method: "POST",
        body: JSON.stringify({ receiveEmail }),
        headers: { "Content-Type": "application/json" }
      });
      if (res.ok) {
        return;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  sendReportEmail = async (exportReportEmail) => {
    try {
      const { toEmail, reportType, filters, fileName, fileContent, contentType } = exportReportEmail;
      const res = await super.fetch(
        new URL(`/api/verity/report/admin/send-export-report-email`, this.config.verityOrigin),
        {
          method: "POST",
          body: JSON.stringify({
            toEmail,
            reportType,
            filters,
            fileName,
            fileContent,
            contentType
          }),
          headers: {
            "Content-Type": "application/json"
          }
        }
      );
      if (res.ok) {
        return true;
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/roster/schema-validators.ts
import { Type as Type42 } from "@sinclair/typebox";
var getRosterStatistics = new SchemaValidator(
  Type42.Object({
    TOTAL: Type42.Number(),
    ADMIN: Type42.Number(),
    INSTRUCTOR: Type42.Number(),
    STUDENT: Type42.Number()
  })
);
var privilegeGroupIds = new SchemaValidator(Type42.Array(Type42.Number()));
var schema_validators_default43 = {
  getRosterStatistics,
  privilegeGroupIds
};

// src/roster/index.ts
var RosterClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default43);
  }
  getRosterStatistics = async () => {
    try {
      const response = await super.fetch(`${this.config.verityOrigin}/api/verity/roster/stats`, {
        credentials: "include"
      });
      if (response.ok) {
        const obj = await response.json();
        if (this.schemaValidators.getRosterStatistics.validate(obj)) {
          return obj;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
  getPrivilegeGroupIdsForRole = async (instituteUuid, role) => handleApiErrorWithSchema(
    super.fetch(
      `${this.config.verityOrigin}/api/access-control/roster/${instituteUuid}/role/${role}/privilege-group`,
      {
        credentials: "include"
      }
    ),
    this.schemaValidators.privilegeGroupIds
  );
};

// src/theme/index.ts
var ThemeClient = class extends ApiClient {
  constructor(config) {
    super(config, {});
  }
  getVisualizerInfo = async (lms2, instituteId2) => {
    try {
      let downloadUrl;
      let viewUrl;
      if (lms2 === "canvas") {
        downloadUrl = `${this.config.verityOrigin}/api/verity/theme/canvas.js?instituteId=${instituteId2}&download=true`;
        viewUrl = `${this.config.verityOrigin}/api/verity/theme/canvas.js?instituteId=${instituteId2}`;
      } else if (lms2 === "desire2learn") {
        downloadUrl = `${this.config.verityOrigin}/api/verity/theme/d2l.js?instituteId=${instituteId2}&download=true`;
        viewUrl = `${this.config.verityOrigin}/api/verity/theme/d2l.js?instituteId=${instituteId2}`;
      } else if (lms2 === "moodle" && this.config?.visualizerOrigin) {
        downloadUrl = `${this.config.visualizerOrigin}/verity-local.zip`;
      } else {
        return void 0;
      }
      let response;
      if (lms2 === "moodle") {
        response = await fetch(new URL(downloadUrl), {
          // Do not use this.fetch
          method: "HEAD",
          headers: { Accept: "application/zip" }
        });
      } else {
        response = await this.fetch(new URL(downloadUrl), {
          method: "HEAD"
        });
      }
      if (!response.ok) throw new Error(`Failed to fetch visualizer metadata for ${lms2}`);
      const contentLength = response.headers.get("Content-Length");
      const size = contentLength ? Number(contentLength) : lms2 === "moodle" ? void 0 : 1e3;
      return {
        downloadUrl,
        viewUrl,
        size,
        type: response.headers.get("Content-Type") || void 0
      };
    } catch (error) {
      if (error instanceof Error) {
        throw new ApiClientError(error);
      }
    }
    throw new ApiClientError();
  };
};

// src/payment/schema-validators.ts
var schema_validators_default44 = {};

// src/admin/schema-validators.ts
var schema_validators_default45 = {};

// src/trpc/index.ts
import { createTRPCClient, httpLink } from "@trpc/client";
import superjson from "superjson";
var TrpcClient = class extends ApiClient {
  trpc;
  constructor(config) {
    super(config, {});
    this.trpc = createTRPCClient({
      links: [
        httpLink({
          url: `${this.config.verityOrigin}/api/verity/trpc`,
          fetch: this.fetch.bind(this),
          transformer: superjson
        })
      ]
    });
  }
};

// src/storage-adapters/local-storage.ts
var STORAGE_KEYS = {
  ACCESS_TOKEN: "verityToken",
  ACCESS_EXPIRES_AT: "verityAccessExpiresAt",
  REFRESH_TOKEN: "verityInfo",
  REFRESH_EXPIRES_AT: "verityRefreshExpiresAt",
  ACCOUNT_ID: "verityAccountId",
  LOCAL_REFRESHED_TIME: "verityLocalRefreshTimestamp"
};
function createLocalStorageAdapter(config) {
  return {
    ...config,
    /**
     * Retrieves the refresh token from localStorage.
     * @returns The stored refresh token or null if not found
     */
    getRefreshToken: async () => {
      return Promise.resolve(localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ?? void 0);
    },
    /**
     * Stores or removes the refresh token in localStorage.
     * If the token is undefined, removes the key entirely.
     * @param refreshToken - The token to store or null to remove
     */
    setRefreshToken: async (refreshToken) => {
      if (refreshToken === void 0) {
        return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN));
      } else {
        return Promise.resolve(localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken));
      }
    },
    /**
     * Retrieves the account ID from localStorage.
     * @returns The stored account ID or null if not found
     */
    getAccountId: async () => {
      return Promise.resolve(localStorage.getItem(STORAGE_KEYS.ACCOUNT_ID) ?? void 0);
    },
    /**
     * Stores or removes the account ID in localStorage.
     * If the ID is falsy, removes the key entirely.
     * @param accountId - The ID to store or null to remove
     */
    setAccountId: async (accountId) => {
      if (accountId) {
        return Promise.resolve(localStorage.setItem(STORAGE_KEYS.ACCOUNT_ID, accountId));
      } else {
        return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.ACCOUNT_ID));
      }
    },
    /**
     * Retrieves the access token from localStorage.
     * @returns The stored access token or null if not found
     */
    getAccessToken: async () => {
      return Promise.resolve(localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ?? void 0);
    },
    /**
     * Stores or removes the access token in localStorage.
     * If the token is undefined, removes the key entirely.
     * @param accessToken - The token to store or null to remove
     */
    setAccessToken: async (accessToken2) => {
      if (accessToken2) {
        return Promise.resolve(localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken2));
      } else {
        return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN));
      }
    },
    /**
     * Retrieves the local refresh timestamp from localStorage.
     * Converts the stored string value to a number.
     * @returns The timestamp as a number or null if not found
     */
    getLocalRefreshedTime: async () => {
      const timestamp = localStorage.getItem(STORAGE_KEYS.LOCAL_REFRESHED_TIME);
      return Promise.resolve(timestamp ? Number.parseInt(timestamp) : void 0);
    },
    /**
     * Stores or removes the local refresh timestamp in localStorage.
     * Converts the number to a string for storage.
     * @param timestamp - The timestamp to store or null to remove
     */
    setLocalRefreshedTime: async (timestamp) => {
      if (timestamp) {
        return Promise.resolve(localStorage.setItem(STORAGE_KEYS.LOCAL_REFRESHED_TIME, timestamp.toString()));
      } else {
        return Promise.resolve(localStorage.removeItem(STORAGE_KEYS.LOCAL_REFRESHED_TIME));
      }
    },
    /**
     * Verifies if localStorage is available and functioning in the current environment.
     *
     * Performs the following checks:
     * 1. Verifies that window.localStorage exists
     * 2. Tests writing and reading from localStorage
     * 3. Handles any permission or access errors
     *
     * @returns A boolean indicating if localStorage is supported
     */
    isEnvironmentSupported: () => {
      try {
        if (typeof window === "undefined" || !window.localStorage) {
          return false;
        }
        const testKey = "__test_storage__";
        localStorage.setItem(testKey, "test");
        const testValue = localStorage.getItem(testKey);
        localStorage.removeItem(testKey);
        if (testValue !== "test") {
          return false;
        }
        return true;
      } catch {
        return false;
      }
    }
  };
}

// src/storage-adapters/lti-postmessage.ts
var DEFAULT_TIMEOUT = 5e3;
var STORAGE_KEYS2 = {
  /** Access token for API authentication */
  ACCESS_TOKEN: "lti.verity.accessToken",
  /** Account identifier for the current user */
  ACCOUNT_ID: "lti.verity.accountId",
  /** Refresh token for obtaining new access tokens */
  REFRESH_TOKEN: "lti.verity.refreshToken",
  /** Timestamp of the last token refresh operation */
  LOCAL_REFRESHED_TIME: "lti.verity.localRefreshTime"
};
function generateMessageId() {
  return `lti_msg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}
async function sendMessage(targetWindow, targetOrigin, message, timeout) {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      window.removeEventListener("message", messageHandler);
      resolve(void 0);
    }, timeout);
    function messageHandler(event) {
      const isValidOrigin = event.origin === targetOrigin;
      if (isValidOrigin) {
        const data = event.data;
        if (data !== null) {
          const isValidResponseStructure = typeof data === "object" && "subject" in data && typeof data.subject === "string" && "message_id" in data && typeof data.message_id === "string";
          if (!isValidOrigin || !isValidResponseStructure) {
            return;
          }
          window.removeEventListener("message", messageHandler);
          clearTimeout(timeoutId);
          const verifiedData = data;
          if (verifiedData.error) {
            resolve(void 0);
          } else {
            resolve(verifiedData.value);
          }
        }
      }
    }
    window.addEventListener("message", messageHandler);
    targetWindow.postMessage(message, targetOrigin);
  });
}
function createLtiPostMessageAdapter(targetWindow, targetOrigin, config, timeout = DEFAULT_TIMEOUT) {
  return {
    ...config,
    getRefreshToken: async () => {
      return sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.get_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.REFRESH_TOKEN
        },
        timeout
      );
    },
    setRefreshToken: async (refreshToken) => {
      await sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.put_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.REFRESH_TOKEN,
          value: refreshToken
        },
        timeout
      );
    },
    getAccountId: async () => {
      return sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.get_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.ACCOUNT_ID
        },
        timeout
      );
    },
    setAccountId: async (accountId) => {
      await sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.put_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.ACCOUNT_ID,
          value: accountId
        },
        timeout
      );
    },
    getAccessToken: async () => {
      return sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.get_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.ACCESS_TOKEN
        },
        timeout
      );
    },
    setAccessToken: async (accessToken2) => {
      await sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.put_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.ACCESS_TOKEN,
          value: accessToken2
        },
        timeout
      );
    },
    getLocalRefreshedTime: async () => {
      return sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.get_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.LOCAL_REFRESHED_TIME
        },
        timeout
      );
    },
    setLocalRefreshedTime: async (timestamp) => {
      await sendMessage(
        targetWindow,
        targetOrigin,
        {
          subject: "lti.put_data",
          message_id: generateMessageId(),
          key: STORAGE_KEYS2.LOCAL_REFRESHED_TIME,
          value: timestamp
        },
        timeout
      );
    },
    isEnvironmentSupported: () => {
      try {
        if (typeof window === "undefined") {
          return false;
        }
        if (!targetWindow || targetWindow.closed) {
          return false;
        }
        if (!targetWindow.postMessage) {
          return false;
        }
        return true;
      } catch (error) {
        return false;
      }
    }
  };
}

// src/storage-adapters/state.ts
function createStateStorageAdapter(state, config) {
  return {
    ...config,
    /**
     * Retrieves the refresh token from the state
     * @returns The stored refresh token or null if not found
     */
    getRefreshToken: async () => {
      return await state.use((_, { refreshToken }) => refreshToken);
    },
    /**
     * Stores the refresh token in the state
     * @param refreshToken - The token to store or null to clear
     */
    setRefreshToken: async (refreshToken) => {
      return await state.use((_, persistentState) => {
        persistentState.refreshToken = refreshToken;
        return;
      });
    },
    /**
     * Retrieves the account ID from the state
     * @returns The stored account ID or null if not found
     */
    getAccountId: async () => {
      return await state.use((_, { accountId }) => accountId);
    },
    /**
     * Stores the account ID in the state
     * @param accountId - The ID to store or null to clear
     */
    setAccountId: async (accountId) => {
      return await state.use((_, persistentState) => {
        persistentState.accountId = accountId;
        return;
      });
    },
    /**
     * Retrieves the access token from the state
     * @returns The stored access token or null if not found
     */
    getAccessToken: async () => {
      return await state.use((_, { accessToken: accessToken2 }) => accessToken2);
    },
    /**
     * Stores the access token in the state
     * @param accessToken - The token to store or null to clear
     */
    setAccessToken: async (accessToken2) => {
      return await state.use((_, persistentState) => {
        persistentState.accessToken = accessToken2;
        return;
      });
    },
    /**
     * Retrieves the local refresh timestamp from the state
     * @returns The timestamp or null if not found
     */
    getLocalRefreshedTime: async () => {
      return await state.use((_, { localRefreshedTime }) => localRefreshedTime);
    },
    /**
     * Stores the local refresh timestamp in the state
     * @param timestamp - The timestamp to store or null to clear
     */
    setLocalRefreshedTime: async (timestamp) => {
      return await state.use((_, persistentState) => {
        persistentState.localRefreshedTime = timestamp;
        return;
      });
    },
    /**
     * Verifies if the state manager can support token storage operations.
     *
     * Checks:
     * 1. If state manager is accessible and supports use callback
     *
     * @returns A boolean indicating if the state manager is supported
     */
    isEnvironmentSupported: () => {
      try {
        if (typeof state === "undefined") {
          return false;
        }
        if (typeof state.use !== "function") {
          return false;
        }
        return true;
      } catch {
        return false;
      }
    }
  };
}

// src/admin/index.ts
var AdminClient = class extends ApiClient {
  constructor(config) {
    super(config, schema_validators_default45);
  }
  updateVerityInstituteSettings = async (instituteId2, settings) => {
    return await handleApiError(
      super.fetch(new URL(`/api/verity/lti-institute/${instituteId2}/settings`, this.config.verityOrigin), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
        credentials: "include"
      })
    );
  };
};

// src/restricted-device/index.ts
import { RestrictedDeviceGrantContract, RestrictedDeviceRegistrationContract, withContractBase as withContractBase4, coerceToContractAbsoluteBase as coerceToContractAbsoluteBase4 } from "@yujacorp/verity-api-contract";
var RestrictedDeviceClient = class extends ApiClient {
  constructor(config) {
    super(config, {});
  }
  registerPeer = withDefaultErrorHandler(async (dynamicInput) => {
    const handlers = withContractBase4({
      contract: RestrictedDeviceRegistrationContract,
      base: coerceToContractAbsoluteBase4(`${this.config.verityOrigin}/api/verity/`)
    }).withFetchParams({
      body: dynamicInput.body
    }).withFetcher(async ({ method, url: url2, body }) => {
      const res = await this.fetch(url2, {
        method,
        headers: { "Content-Type": "application/json" },
        body: body ? JSON.stringify(body) : void 0
      });
      return res.json();
    });
    return handlers.toParsed();
  });
  createSingleUseGrant = withDefaultErrorHandler(async (dynamicInput) => {
    const handlers = withContractBase4({
      contract: RestrictedDeviceGrantContract,
      base: coerceToContractAbsoluteBase4(`${this.config.verityOrigin}/api/verity/`)
    }).withFetchParams({
      pathParams: dynamicInput.pathParams
    }).withFetcher(async ({ method, url: url2 }) => {
      const res = await this.fetch(url2, {
        method,
        headers: { "Content-Type": "application/json" }
      });
      return res.json();
    });
    return handlers.toParsed();
  });
};

// src/index.ts
var VerityApiClient = class extends ApiClient {
  authentication;
  lmsInfo;
  proctoringSession;
  liveClient;
  platformInfo;
  permission;
  download;
  institute;
  course;
  assessment;
  verityOptions;
  user;
  startAssessmentClient;
  systemCheck;
  proctoringConfiguration;
  proctoringProfile;
  support;
  installation;
  oauth;
  scratchNote;
  report;
  roster;
  lti;
  saml;
  basicAuth;
  media;
  dataDump;
  payment;
  admin;
  restrictedDevice;
  theme;
  feedback;
  trpc;
  constructor(config) {
    super(config, {});
    this.authentication = new AuthenticationClient(config);
    this.lmsInfo = new LmsInfoClient(config);
    this.proctoringSession = new ProctoringSessionClient(config);
    this.liveClient = new LiveClient(config);
    this.platformInfo = new PlatformInfoClient(config);
    this.permission = new PermissionClient(config);
    this.download = new DownloadClient(config);
    this.institute = new InstituteClient(config);
    this.course = new CourseClient(config);
    this.assessment = new AssessmentClient(config);
    this.verityOptions = new VerityOptionsClient(config);
    this.user = new UserClient(config);
    this.startAssessmentClient = new startAssessmentClient(config);
    this.systemCheck = new SystemCheckClient(config);
    this.proctoringConfiguration = new ProctoringConfigurationClient(config);
    this.proctoringProfile = new ProctoringProfileClient(config);
    this.support = new SupportClient(config);
    this.installation = new InstallationClient(config);
    this.oauth = new OAuthClient(config);
    this.scratchNote = new ScratchNoteClient(config);
    this.report = new InstituteClient2(config);
    this.roster = new RosterClient(config);
    this.lti = new LtiClient(config);
    this.saml = new SAMLClient(config);
    this.basicAuth = new BasicAuthClient(config);
    this.media = new MediaClient(config);
    this.dataDump = new DataDumpClient(config);
    this.payment = new PaymentClient(config);
    this.admin = new AdminClient(config);
    this.restrictedDevice = new RestrictedDeviceClient(config);
    this.theme = new ThemeClient(config);
    this.feedback = new FeedbackClient(config);
    this.trpc = new TrpcClient(config);
  }
};
var VerityApiValidator = class {
  authentication;
  lmsInfo;
  proctoringSession;
  liveClient;
  platformInfo;
  permission;
  download;
  institute;
  course;
  assessment;
  verityOptions;
  user;
  startAssessmentClient;
  systemCheck;
  proctoringConfiguration;
  proctoringProfile;
  support;
  installation;
  oauth;
  scratchNote;
  report;
  roster;
  lti;
  saml;
  basicAuth;
  media;
  stripe;
  admin;
  feedback;
  constructor() {
    this.authentication = { schemaValidators: schema_validators_default };
    this.lmsInfo = { schemaValidators: schema_validators_default23 };
    this.proctoringSession = { schemaValidators: schema_validators_default18 };
    this.liveClient = { schemaValidators: schema_validators_default20 };
    this.platformInfo = { schemaValidators: schema_validators_default21 };
    this.permission = { schemaValidators: schema_validators_default22 };
    this.download = { schemaValidators: schema_validators_default24 };
    this.institute = { schemaValidators: schema_validators_default26 };
    this.course = { schemaValidators: schema_validators_default30 };
    this.assessment = { schemaValidators: schema_validators_default32 };
    this.verityOptions = { schemaValidators: schema_validators_default17 };
    this.user = { schemaValidators: schema_validators_default27 };
    this.startAssessmentClient = { schemaValidators: schema_validators_default28 };
    this.systemCheck = { schemaValidators: schema_validators_default29 };
    this.proctoringConfiguration = { schemaValidators: schema_validators_default31 };
    this.proctoringProfile = { schemaValidators: schema_validators_default25 };
    this.support = { schemaValidators: schema_validators_default33 };
    this.installation = { schemaValidators: schema_validators_default34 };
    this.oauth = { schemaValidators: schema_validators_default35 };
    this.scratchNote = { schemaValidators: schema_validators_default41 };
    this.report = { schemaValidators: schema_validators_default42 };
    this.roster = { schemaValidators: schema_validators_default43 };
    this.lti = { schemaValidators: schema_validators_default36 };
    this.saml = { schemaValidators: schema_validators_default38 };
    this.basicAuth = { schemaValidators: schema_validators_default37 };
    this.media = { schemaValidators: schema_validators_default39 };
    this.stripe = { schemaValidators: schema_validators_default44 };
    this.admin = { schemaValidators: schema_validators_default45 };
    this.feedback = { schemaValidators: schema_validators_default40 };
  }
};
var WebApiClient = class _WebApiClient extends VerityApiClient {
  static instances = /* @__PURE__ */ new Map();
  constructor(config) {
    super(config);
  }
  /**
   * Generate a unique key for the config to use as map key
   * @param config Platform configuration
   * @returns A string key representing the config
   */
  static getConfigKey(config) {
    return `${config.verityOrigin}:${config.lockdown.origin}:${config.lockdown.channel}`;
  }
  /**
   * Get or create a singleton instance of WebApiClient for the given config
   * @param config Platform configuration that determines which instance to use
   * @returns The singleton instance of WebApiClient for the given config
   */
  static getInstance(config) {
    const configKey = _WebApiClient.getConfigKey(config);
    const existingInstance = _WebApiClient.instances.get(configKey);
    if (existingInstance) {
      return existingInstance;
    }
    const localStorageAdapter = createLocalStorageAdapter(config);
    if (localStorageAdapter.isEnvironmentSupported()) {
      const instance = new _WebApiClient(localStorageAdapter);
      _WebApiClient.instances.set(configKey, instance);
      return instance;
    }
    const ltiPostMessageAdapter = createLtiPostMessageAdapter(
      window.top ?? window.parent,
      new URL(window.location.ancestorOrigins[0]).origin,
      config
    );
    if (ltiPostMessageAdapter.isEnvironmentSupported()) {
      const instance = new _WebApiClient(ltiPostMessageAdapter);
      _WebApiClient.instances.set(configKey, instance);
      return instance;
    }
    throw new Error("No valid adapter found");
  }
};
var WorkerApiClient = class _WorkerApiClient extends VerityApiClient {
  static instances = /* @__PURE__ */ new Map();
  constructor(config) {
    super(config);
  }
  /**
   * Generate a unique key for the config to use as map key
   * @param config Platform configuration
   * @returns A string key representing the config
   */
  static getConfigKey(config) {
    return `${config.verityOrigin}:${config.lockdown.origin}:${config.lockdown.channel}`;
  }
  /**
   * Get or create a singleton instance of WorkerApiClient for the given config and state manager
   * @param state State manager to use for storage
   * @param config Platform configuration that determines which instance to use
   * @returns The singleton instance of WorkerApiClient for the given config and state
   */
  static getInstance(state, config) {
    const instanceKey = _WorkerApiClient.getConfigKey(config);
    const existingInstance = _WorkerApiClient.instances.get(instanceKey);
    if (existingInstance) {
      return existingInstance;
    }
    const stateStorageAdapter = createStateStorageAdapter(state, config);
    if (stateStorageAdapter.isEnvironmentSupported()) {
      const instance = new _WorkerApiClient(stateStorageAdapter);
      _WorkerApiClient.instances.set(instanceKey, instance);
      return instance;
    }
    throw new Error("No valid adapter found");
  }
};
export {
  ApiClientError,
  ApiClientResponseError,
  ApiClientSchemaError,
  BaseApiClientError,
  UnauthenticatedError,
  VerityApiClient,
  VerityApiValidator,
  WebApiClient,
  WorkerApiClient,
  handleApiError,
  handleApiErrorWithSchema,
  withDefaultErrorHandler
};
//# sourceMappingURL=index.js.map